import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { useNavigate,
  useLocation } from 'react-router-dom';
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Button,
  IconButton,
  Badge,
  useDisclosure,
  Spinner,
  Input,
  Textarea,
  NativeSelect,
  Checkbox,
  SimpleGrid,
  Card,
  Tabs,
  Menu,
  Icon,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
  NumberInput,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Link,
  Alert,
  Dialog,
  Portal,
  Separator,
  Field,
  List,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { TabList, Tab, TabPanels, TabPanel, Switch, tabsControlledValue, tabsOnValueChange } from '@/components/ui/chakra-v2-compat';
import { MultiCheckboxField, IsolateCheckboxesFromField } from '@/components/ui/multi-checkbox-field';
import { toaster } from '@/components/ui/toaster';
import { InputGroup, InputLeftElement, InputRightElement } from '@/components/ui/input-group';
import { Tooltip } from '@/components/ui/tooltip';
import {
  FaPlus, FaEdit, FaTrash, FaChevronDown, FaChevronLeft, FaChevronRight,
  FaClock, FaMapMarkerAlt, FaUsers, FaCheck, FaTimes, FaQuestion, FaMinus,
  FaCalendarAlt, FaThList, FaFilter, FaStar, FaExclamationCircle, FaSearch,
  FaLayerGroup, FaEllipsisV, FaSync, FaUserCheck, FaDrum, FaBell,
  FaFolder, FaGripVertical, FaLink, FaListUl, FaExternalLinkAlt,
  FaChartBar, FaSortAmountDown, FaSortAmountUp, FaTrophy, FaExclamationTriangle,
  FaWhatsapp, FaFilePdf, FaShareAlt, FaCopy, FaCalendar, FaSun, FaBuilding, FaHistory,
} from 'react-icons/fa';
import { ensayosAPI, musicosAPI, actuacionesAPI, bandasAPI } from '../services/api';
import { openWhatsappChat } from '../utils/whatsappLink';
import AppLayout from './AppLayout';
import { useAuth } from '../contexts/AuthContext';
import PasarListaModal from './PasarListaModal';
import { useProductTour } from '../hooks/useProductTour';
import { bandaEnsayosSteps } from '../tours/bandaEnsayosTour';

// ─── Constantes ─────────────────────────────────────────────────────────────

const DIAS_SEMANA = [
  { value: 0, label: 'Dom' },
  { value: 1, label: 'Lun' },
  { value: 2, label: 'Mar' },
  { value: 3, label: 'Mié' },
  { value: 4, label: 'Jue' },
  { value: 5, label: 'Vie' },
  { value: 6, label: 'Sáb' },
];

const DIAS_SEMANA_FULL = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const ESTADO_CONFIG = {
  PROGRAMADO: { label: 'Programado', color: 'blue', icon: FaCalendarAlt },
  REALIZADO:  { label: 'Realizado',  color: 'green', icon: FaCheck },
  CANCELADO:  { label: 'Cancelado',  color: 'red', icon: FaTimes },
};

const ASISTENCIA_CONFIG = {
  PENDIENTE:   { label: 'Pendiente',   color: 'gray',   icon: FaQuestion },
  PRESENTE:    { label: 'Presente',    color: 'green',  icon: FaCheck },
  AUSENTE:     { label: 'Ausente',     color: 'red',    icon: FaTimes },
  JUSTIFICADO: { label: 'Justificado', color: 'orange', icon: FaExclamationCircle },
};

const PALETTE = [
  '#6B46C1', '#E53E3E', '#38A169', '#3182CE', '#DD6B20',
  '#D53F8C', '#319795', '#805AD5', '#2B6CB0', '#C05621',
];

function requiereMusicosEnsayosFn(musicos) {
  if ((musicos || []).length > 0) return true;
  toaster.create({
    title: 'Añade músicos en Mis músicos',
    description: 'Necesitas al menos un músico en el censo de la banda para usar esta sección.',
    type: 'warning',
    duration: 5000,
    closable: true,
  });
  return false;
}

// ─── Repertorio helpers (mismo sistema que Actuaciones) ──────────────────────

let _repLineCounter = 0;
function repLineId() { return `rl-${Date.now()}-${++_repLineCounter}`; }

function isCarpetaPartiturasGroup(r) {
  return r && r.tipo === 'carpeta_partituras' && (r.nombreCarpeta || r.nombre) && Array.isArray(r.obras);
}

function isObraCarpetaLine(r) {
  return r && r.tipo === 'obra_carpeta' && (r.titulo || r.nombre);
}

/** Misma lógica que en ActuacionDetalle: no deja filas “vacías” sin título. */
function normalizeRepertorio(rep) {
  const src = Array.isArray(rep) ? rep : [];
  const out = [];
  for (const r of src) {
    if (!r) continue;
    if (isCarpetaPartiturasGroup(r)) {
      const folder = r.nombreCarpeta || r.nombre;
      const sorted = [...r.obras].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
      for (const row of sorted) {
        const tit = row?.obra?.titulo ?? row?.titulo ?? row?.nombre ?? 'Sin título';
        const aut = row?.obra?.autor ?? row?.autor ?? null;
        const enl = row?.obra?.enlace ?? row?.enlace ?? null;
        const oid = row?.obra?.id ?? row?.obraId ?? null;
        out.push({
          id: repLineId(),
          tipo: 'obra_carpeta',
          titulo: tit,
          autor: aut,
          nombreCarpeta: folder,
          carpetaId: r.carpetaId,
          ...(oid ? { obraId: oid } : {}),
          ...(enl ? { enlace: enl } : {}),
        });
      }
    } else if (isObraCarpetaLine(r)) {
      out.push({
        ...r,
        id: r.id || repLineId(),
        titulo: r.titulo || r.nombre,
        autor: r.autor ?? null,
        nombreCarpeta: r.nombreCarpeta,
        carpetaId: r.carpetaId,
      });
    } else if (r.titulo || r.nombre) {
      const manual = {
        id: r.id || repLineId(),
        titulo: r.titulo || r.nombre,
        ...(r.obraId ? { obraId: r.obraId } : {}),
        ...(r.enlace ? { enlace: r.enlace } : {}),
      };
      const aut = r.autor != null && String(r.autor).trim() ? String(r.autor).trim() : null;
      if (aut) manual.autor = aut;
      out.push(manual);
    }
  }
  return out;
}

function repItemTitulo(item) {
  if (!item) return 'Sin título';
  const t = item.titulo || item.nombre;
  if (t != null && String(t).trim()) return String(t).trim();
  return 'Sin título';
}

function escapeHtmlShare(s) {
  if (s == null || s === '') return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function lineaSedeEnsayoShare(ensayo) {
  const s = ensayo?.sede;
  if (!s) return null;
  const p = [s.nombre, s.direccion].filter(Boolean);
  return p.length ? p.join(' · ') : null;
}

function lineasRepertorioEnsayoShare(ensayo) {
  const flat = normalizeRepertorio(ensayo?.repertorioPropio);
  return flat.map((row, i) => {
    const t = repItemTitulo(row);
    const c = row.nombreCarpeta ? ` [${row.nombreCarpeta}]` : '';
    return `${i + 1}. ${t}${c}`;
  });
}

function musicoNombreCompleto(m) {
  if (!m) return '';
  return [m.nombre, m.apellidos].filter(Boolean).join(' ').trim();
}

function musicoInstrumentoResumen(m) {
  if (!m?.instrumentosAsignados?.length) return null;
  const ia = m.instrumentosAsignados[0];
  const ins = ia.instrumento?.nombre ?? '';
  const voz = ia.vozPartituraPersonalizada?.nombre;
  const s = [ins, voz].filter(Boolean).join(' ');
  return s || null;
}

function asistenciaEstadoPara(musicoId, asistenciaRows) {
  const a = (asistenciaRows ?? []).find((x) => x.musicoId === musicoId);
  return a?.estado ?? 'PENDIENTE';
}

/** Texto plano (WhatsApp) con toda la información del ensayo; músicos según opt.incluirMusicos */
function buildEnsayoShareWhatsAppText(ensayo, asistenciaRows, opt) {
  const incluirMusicos = opt?.incluirMusicos === true;
  const lines = [];
  lines.push('🥁 *ENSAYO*');
  if (ensayo.grupo?.nombre) lines.push(`_${ensayo.grupo.nombre}_`);
  lines.push('');

  lines.push(`📅 *Fecha:* ${formatFecha(ensayo.fecha)}`);
  const hora = ensayo.horaInicio
    ? (ensayo.horaFin ? `${ensayo.horaInicio} – ${ensayo.horaFin}` : String(ensayo.horaInicio))
    : '—';
  lines.push(`🕐 *Horario:* ${hora}`);
  if (ensayo.lugar) lines.push(`📌 *Lugar:* ${ensayo.lugar}`);
  if (ensayo.plantillaFormacion) lines.push(`📋 *Formación:* ${ensayo.plantillaFormacion.nombre}`);
  const estL = ESTADO_CONFIG[ensayo.estado]?.label ?? ensayo.estado;
  lines.push(`🏷 *Estado:* ${estL}`);
  lines.push(
    `📣 *Tipo:* ${ensayo.tipo === 'EXTRAORDINARIO' ? 'Extraordinario' : 'Regular'} · *Asistencia:* ${
      ensayo.obligatorio ? 'Obligatoria' : 'Opcional'
    }`,
  );
  if (ensayo.destacado) lines.push('⭐ *Destacado*');
  lines.push('');

  const sl = lineaSedeEnsayoShare(ensayo);
  if (sl) {
    lines.push(`📍 *Sede:* ${sl}`);
    if (Array.isArray(ensayo.sede?.servicios) && ensayo.sede.servicios.length) {
      lines.push(`🛠 *Servicios:* ${ensayo.sede.servicios.join(', ')}`);
    }
    lines.push('');
  }

  if (ensayo.descripcion) {
    lines.push('📝 *NOTAS*');
    lines.push(ensayo.descripcion);
    lines.push('');
  }

  const rep = lineasRepertorioEnsayoShare(ensayo);
  if (rep.length) {
    lines.push('🎼 *REPERTORIO*');
    rep.forEach((l) => lines.push(l));
    lines.push('');
  }

  if (incluirMusicos) {
    lines.push('👥 *MÚSICOS CONVOCADOS*');
    const conv = ensayo.convocados ?? [];
    if (conv.length === 0) {
      lines.push('_Sin músicos convocados_');
    } else {
      conv.forEach((c) => {
        const m = c.musico;
        if (!m) return;
        const st = asistenciaEstadoPara(m.id, asistenciaRows);
        const stLabel = ASISTENCIA_CONFIG[st]?.label ?? st;
        const ins = musicoInstrumentoResumen(m);
        lines.push(`• ${musicoNombreCompleto(m)}${ins ? ` (${ins})` : ''} — ${stLabel}`);
      });
    }
    lines.push('');
  }

  lines.push('_Enviado desde Cadencya 🎵_');
  const repertorioUrl = String(opt?.repertorioUrl || '').trim();
  if (repertorioUrl) {
    lines.push('');
    lines.push(repertorioUrl);
  }
  return lines.join('\n');
}

/** Cuerpo HTML (solo el bloque central) de la ficha de ensayo para imprimir / PDF */
function buildEnsayoFichaBodyHtml(ensayo, asistenciaRows, opt) {
  const incluirMusicos = opt?.incluirMusicos === true;
  const gc = ensayo.grupo?.color ?? '#6B46C1';
  const seccion = (tit, inner) => `
    <div style="margin-top:16px">
      <div style="font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:0.05em;color:#6b46c1;margin-bottom:6px">${tit}</div>
      <div style="font-size:13px;color:#2d3748;line-height:1.5">${inner}</div>
    </div>`;
  const fila = (k, v) => `<tr><td style="padding:6px 0;color:#718096;vertical-align:top;width:32%">${escapeHtmlShare(
    k,
  )}</td><td style="padding:6px 0;font-weight:600;color:#1a202c">${v}</td></tr>`;
  const hora = ensayo.horaInicio
    ? escapeHtmlShare(
        ensayo.horaFin ? `${ensayo.horaInicio} – ${ensayo.horaFin}` : String(ensayo.horaInicio),
      )
    : '—';
  let filas = [
    fila('Fecha', escapeHtmlShare(formatFecha(ensayo.fecha))),
    fila('Horario', hora),
    ensayo.lugar ? fila('Lugar (texto)', escapeHtmlShare(ensayo.lugar)) : null,
    ensayo.grupo ? fila('Grupo de ensayos', escapeHtmlShare(ensayo.grupo.nombre)) : null,
    ensayo.plantillaFormacion
      ? fila('Formación', escapeHtmlShare(ensayo.plantillaFormacion.nombre))
      : null,
    fila('Estado', escapeHtmlShare(ESTADO_CONFIG[ensayo.estado]?.label ?? ensayo.estado)),
    fila(
      'Tipo / asistencia',
      escapeHtmlShare(
        `${ensayo.tipo === 'EXTRAORDINARIO' ? 'Extraordinario' : 'Regular'} · ${
          ensayo.obligatorio ? 'Obligatorio' : 'Opcional'
        }`,
      ),
    ),
  ].filter(Boolean);
  if (ensayo.destacado) {
    filas = [...filas, fila('Destacado', 'Sí')];
  }
  const sl = lineaSedeEnsayoShare(ensayo);
  if (sl) filas = [...filas, fila('Sede', escapeHtmlShare(sl))];
  if (Array.isArray(ensayo.sede?.servicios) && ensayo.sede.servicios.length) {
    filas = [...filas, fila('Servicios en sede', escapeHtmlShare(ensayo.sede.servicios.join(', ')))];
  }

  const rep = lineasRepertorioEnsayoShare(ensayo);
  const repBlock =
    rep.length > 0
      ? seccion(
        'Repertorio',
        `<ol style="margin:0;padding-left:20px">${rep
          .map((l) => `<li style="margin:4px 0">${escapeHtmlShare(l)}</li>`)
          .join('')}</ol>`,
      )
      : '';
  const notasBlock = ensayo.descripcion
    ? seccion('Notas', `<div style="font-style:italic;white-space:pre-wrap">${escapeHtmlShare(ensayo.descripcion).replace(
        /\n/g,
        '<br>',
      )}</div>`)
    : '';

  let musBlock = '';
  if (incluirMusicos) {
    const conv = ensayo.convocados ?? [];
    if (conv.length === 0) {
      musBlock = seccion('Músicos convocados', '<span style="color:#a0aec0">Sin convocados en este ensayo.</span>');
    } else {
      const filasM = conv
        .map((c) => {
          const m = c.musico;
          if (!m) return '';
          const st = asistenciaEstadoPara(m.id, asistenciaRows);
          const stLabel = ASISTENCIA_CONFIG[st]?.label ?? st;
          const ins = musicoInstrumentoResumen(m);
          return `<tr style="border-bottom:1px solid #edf2f7">
            <td style="padding:8px 4px;vertical-align:top">${escapeHtmlShare(musicoNombreCompleto(m))}</td>
            <td style="padding:8px 4px;vertical-align:top;color:#718096;font-size:12px">${ins ? escapeHtmlShare(ins) : '—'}</td>
            <td style="padding:8px 4px;vertical-align:top"><span style="background:#e9d8fd;color:#553c9a;padding:2px 8px;border-radius:12px;font-size:11px;font-weight:600">${escapeHtmlShare(
    stLabel,
  )}</span></td>
          </tr>`;
        })
        .filter(Boolean)
        .join('');
      musBlock = seccion(
        'Músicos convocados',
        `<table style="width:100%;border-collapse:collapse;font-size:13px">
          <tr style="font-size:11px;color:#718096"><th style="text-align:left;padding:4px">Nombre</th><th style="text-align:left;padding:4px">Instrumento / voz</th><th style="text-align:left;padding:4px">Asistencia</th></tr>
          ${filasM}
        </table>`,
      );
    }
  }

  return `
  <div style="border-radius:16px;overflow:hidden;border:1px solid #e2e8f0;margin-bottom:24px">
    <div style="background:linear-gradient(135deg,${gc}ee,${gc});color:white;padding:16px 20px">
      <div style="font-size:20px;font-weight:800">🥁 Ensayo</div>
      <div style="opacity:0.95;font-size:14px;margin-top:4px">${escapeHtmlShare(formatFecha(ensayo.fecha))}${
  hora !== '—' ? ` · ${hora}` : ''
}</div>
    </div>
    <div style="padding:20px;background:#fff">
      <table style="width:100%;border-collapse:collapse;font-size:13px">
        <tbody>
          ${filas.join('')}
        </tbody>
      </table>
      ${notasBlock}
      ${repBlock}
      ${musBlock}
    </div>
  </div>`;
}

const PDF_INFORME_STYLES = `
  html {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    color-adjust: exact;
  }
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; padding: 0; background: #f7f5ff; color: #1a202c; }
  @page { margin: 16mm; size: A4 portrait; }
  @media print {
    html, body {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    body { background: #f7f5ff !important; }
    .no-print { display: none !important; }
  }
`;

function buildPdfInformeDocumentHtml(IMG_LOGO, headerTitle, mainInner) {
  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${escapeHtmlShare(headerTitle)}</title>
<style>${PDF_INFORME_STYLES}</style>
</head>
<body>
  <div style="background:linear-gradient(135deg,#553c9a 0%,#6b46c1 50%,#9f7aea 100%);padding:24px 32px;margin-bottom:24px;border-radius:0 0 20px 20px">
    <table style="width:100%;border-collapse:collapse"><tr>
      <td>${IMG_LOGO}</td>
      <td style="text-align:right;color:rgba(255,255,255,0.9);font-size:13px">
        <div style="font-size:18px;font-weight:800;color:white">📋 ${escapeHtmlShare(headerTitle)}</div>
        <div style="margin-top:4px">Generado el ${new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
      </td>
    </tr></table>
  </div>
  <div style="max-width:900px;margin:0 auto 20px;padding:0 16px;display:flex;gap:20px;flex-wrap:wrap">
    <span style="display:inline-flex;align-items:center;gap:6px;font-size:12px;color:#718096">
      <span style="display:inline-block;width:12px;height:12px;border-radius:50%;background:#6b46c1"></span> Ficha de ensayo
    </span>
  </div>
  <div style="max-width:900px;margin:0 auto;padding:0 16px">${mainInner}</div>
  <div style="background:linear-gradient(135deg,#553c9a 0%,#6b46c1 50%,#9f7aea 100%);color:rgba(255,255,255,0.9);text-align:center;padding:16px 32px;font-size:12px;font-weight:500;margin-top:32px;border-radius:20px 20px 0 0;box-shadow:0 -4px 24px rgba(107,70,193,0.15)">
    ${IMG_LOGO} <span style="display:inline-block;margin:0 8px;opacity:0.6">·</span> Gestión musical para bandas de Semana Santa
  </div>
  <div class="no-print" style="text-align:center;padding:20px">
    <button type="button" onclick="window.print()" style="background:#6b46c1;color:white;border:none;padding:12px 28px;border-radius:8px;font-size:15px;cursor:pointer;font-weight:700">
      🖨️ Imprimir / Guardar PDF
    </button>
  </div>
</body>
</html>`;
}

function carpetaIdsEnRepertorio(repertorio) {
  const flat = normalizeRepertorio(repertorio || []);
  return new Set(flat.filter(isObraCarpetaLine).map(r => r.carpetaId).filter(Boolean));
}

function grupoTieneRepertorio(grupo) {
  return grupo && normalizeRepertorio(grupo.repertorio || []).length > 0;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatFecha(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${DIAS_SEMANA_FULL[d.getDay()]}, ${d.getDate()} ${MESES[d.getMonth()]} ${d.getFullYear()}`;
}

function formatFechaCorta(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getDate()} ${MESES[d.getMonth()].slice(0, 3)}`;
}

function isSameMonth(dateStr, year, month) {
  const d = new Date(dateStr);
  return d.getFullYear() === year && d.getMonth() === month;
}

/** Día del evento estrictamente anterior a hoy (calendario local). */
function isFechaEventoPasada(fechaStr) {
  if (!fechaStr) return false;
  const d = String(fechaStr).slice(0, 10);
  const t = new Date();
  const today = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  return d < today;
}

/** monthOrder: 'asc' = meses de más antiguo a más nuevo; 'desc' = el más reciente primero (útil para histórico). */
function groupByMonth(ensayos, monthOrder = 'asc') {
  const groups = {};
  ensayos.forEach(e => {
    if (!e?.fecha) return;
    const d = new Date(e.fecha);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (!groups[key]) groups[key] = { year: d.getFullYear(), month: d.getMonth(), items: [] };
    groups[key].items.push(e);
  });
  return Object.values(groups).sort((a, b) => {
    const diff = a.year !== b.year ? a.year - b.year : a.month - b.month;
    return monthOrder === 'desc' ? -diff : diff;
  });
}

// ─── EnsayoCard ──────────────────────────────────────────────────────────────

function EnsayoCard({ ensayo, grupos, onEdit, onDelete, onViewDetail, onShare, isMvpMode }) {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const grupo = grupos.find(g => g.id === ensayo.grupoId);
  const estadoConf = ESTADO_CONFIG[ensayo.estado] ?? ESTADO_CONFIG.PROGRAMADO;
  const grupoColor = grupo?.color ?? '#6B46C1';

  return (
    <Card.Root
      bg={bg}
      border="1px"
      borderColor={borderColor}
      borderLeft="4px solid"
      borderLeftColor={grupoColor}
      borderRadius="xl"
      shadow="sm"
      _hover={{ shadow: 'md', transform: 'translateY(-1px)' }}
      transition="all 0.15s"
      cursor="pointer"
      onClick={() => onViewDetail(ensayo)}
    >
      <Card.Body py={3} px={4}>
        <Flex justify="space-between" align="flex-start" gap={2}>
          <VStack align="start" gap={1} flex={1} minW={0}>
            {/* Fecha + hora */}
            <HStack gap={2} flexWrap="wrap">
              <HStack gap={1}>
                <Icon as={FaCalendarAlt} color="gray.400" boxSize={3} />
                <Text fontSize="sm" fontWeight="700" color="gray.800">
                  {formatFechaCorta(ensayo.fecha)}
                </Text>
              </HStack>
              <HStack gap={1}>
                <Icon as={FaClock} color="gray.400" boxSize={3} />
                <Text fontSize="sm" color="gray.600">
                  {ensayo.horaInicio}{ensayo.horaFin ? ` – ${ensayo.horaFin}` : ''}
                </Text>
              </HStack>
              {ensayo.lugar && (
                <HStack gap={1}>
                  <Icon as={FaMapMarkerAlt} color="gray.400" boxSize={3} />
                  <Text fontSize="xs" color="gray.500" lineClamp={1}>{ensayo.lugar}</Text>
                </HStack>
              )}
            </HStack>

            {/* Grupo */}
            {grupo && (
              <HStack gap={1}>
                <Box w={2} h={2} rounded="full" bg={grupoColor} flexShrink={0} />
                <Text fontSize="xs" color="gray.500" lineClamp={1}>{grupo.nombre}</Text>
                {grupo.actuacionId && (
                  <Badge colorPalette="blue" fontSize="2xs" variant="subtle">vinculado</Badge>
                )}
              </HStack>
            )}

            {/* Badges */}
            <HStack gap={1} flexWrap="wrap">
              <Badge colorPalette={estadoConf.color} fontSize="2xs" variant="subtle">
                {estadoConf.label}
              </Badge>
              {ensayo.destacado && (
                <Badge colorPalette="yellow" fontSize="2xs" variant="subtle">
                  <Icon as={FaStar} mr={1} />Destacado
                </Badge>
              )}
              {!ensayo.obligatorio && (
                <Badge colorPalette="gray" fontSize="2xs" variant="subtle">Opcional</Badge>
              )}
              {ensayo.tipo === 'EXTRAORDINARIO' && (
                <Badge colorPalette="purple" fontSize="2xs" variant="subtle">Extraordinario</Badge>
              )}
            </HStack>
          </VStack>

          {/* Contadores + acciones */}
          <VStack align="end" gap={1} flexShrink={0}>
            {ensayo._count && (
              <HStack gap={2}>
                <Tooltip content="Convocados">
                  <HStack gap={1}>
                    <Icon as={FaUsers} color="gray.400" boxSize={3} />
                    <Text fontSize="xs" color="gray.500">{ensayo._count.convocados}</Text>
                  </HStack>
                </Tooltip>
              </HStack>
            )}
            <HStack gap={1}>
              {onShare && (
                <IconButton
                  size="xs"
                  variant="ghost"
                  colorPalette="green"
                  aria-label="Compartir ensayo (WhatsApp o PDF)"
                  onClick={e => { e.stopPropagation(); onShare(ensayo); }}><FaShareAlt /></IconButton>
              )}
              <IconButton
                size="xs"
                variant="ghost"
                colorPalette="blue"
                aria-label="Editar"
                onClick={e => { e.stopPropagation(); onEdit(ensayo); }}><FaEdit /></IconButton>
              {onDelete && !isFechaEventoPasada(ensayo.fecha) && (
                <IconButton
                  size="xs"
                  variant="ghost"
                  colorPalette="red"
                  aria-label="Eliminar"
                  onClick={e => { e.stopPropagation(); onDelete(ensayo); }}><FaTrash /></IconButton>
              )}
            </HStack>
          </VStack>
        </Flex>
      </Card.Body>
    </Card.Root>
  );
}

// ─── ActuacionSombreada ───────────────────────────────────────────────────────

function ActuacionSombreada({ actuacion }) {
  const bg = useColorModeValue('orange.50', 'orange.900');
  const borderColor = useColorModeValue('orange.200', 'orange.700');
  const fechaD = actuacion.fecha ? new Date(actuacion.fecha) : null;
  const fechaStr = fechaD
    ? `${DIAS_SEMANA_FULL[fechaD.getDay()]}, ${fechaD.getDate()} ${MESES[fechaD.getMonth()]} ${fechaD.getFullYear()}`
    : '';

  return (
    <Box
      px={4}
      py={3}
      bg={bg}
      border="1px dashed"
      borderColor={borderColor}
      borderLeft="4px solid"
      borderLeftColor="orange.400"
      borderRadius="xl"
      opacity={0.85}
    >
      <Flex align="center" gap={3} wrap="wrap">
        <Box
          w={8}
          h={8}
          rounded="full"
          bg="orange.100"
          border="2px solid"
          borderColor="orange.300"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          fontSize="md"
        >
          🎺
        </Box>
        <Box flex={1} minW={0}>
          <HStack gap={2} flexWrap="wrap">
            <Text fontSize="sm" fontWeight="700" color="orange.800" lineClamp={1}>{actuacion.nombre}</Text>
            <Badge colorPalette="orange" variant="subtle" fontSize="2xs">Actuación</Badge>
          </HStack>
          <HStack gap={3} mt={0.5} flexWrap="wrap">
            {fechaStr && (
              <HStack gap={1}>
                <Icon as={FaCalendarAlt} color="orange.400" boxSize={3} />
                <Text fontSize="xs" color="orange.700">{fechaStr}</Text>
              </HStack>
            )}
            {actuacion.horaInicio && (
              <HStack gap={1}>
                <Icon as={FaClock} color="orange.400" boxSize={3} />
                <Text fontSize="xs" color="orange.700">
                  {actuacion.horaInicio}{actuacion.horaFin ? ` – ${actuacion.horaFin}` : ''}
                </Text>
              </HStack>
            )}
            {actuacion.ubicacion || actuacion.lugar ? (
              <HStack gap={1}>
                <Icon as={FaMapMarkerAlt} color="orange.400" boxSize={3} />
                <Text fontSize="xs" color="orange.700" lineClamp={1}>{actuacion.ubicacion || actuacion.lugar}</Text>
              </HStack>
            ) : null}
          </HStack>
        </Box>
      </Flex>
    </Box>
  );
}

// ─── Vincular varios ensayos a un grupo ───────────────────────────────────────

function VincularEnsayosGrupoModal({ open, isOpen: isOpenProp, onClose, grupo, grupos, onSuccess }) {
  const isOpen = open ?? isOpenProp;
  
  const [ensayosLista, setEnsayosLista] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [busqueda, setBusqueda] = useState('');

  useEffect(() => {
    if (!isOpen || !grupo?.id) return;
    setSelected(new Set());
    setBusqueda('');
    setLoading(true);
    ensayosAPI.getAll({})
      .then(r => setEnsayosLista(r.data ?? []))
      .catch(() => setEnsayosLista([]))
      .finally(() => setLoading(false));
  }, [isOpen, grupo?.id]);

  const candidatos = useMemo(() => {
    if (!grupo?.id) return [];
    const q = busqueda.trim().toLowerCase();
    return (ensayosLista || [])
      .filter(e => e.grupoId !== grupo.id)
      .filter(e => {
        if (!q) return true;
        const fechaStr = e.fecha ? new Date(e.fecha).toLocaleDateString('es-ES') : '';
        const est = (e.estado && ESTADO_CONFIG[e.estado]) ? ESTADO_CONFIG[e.estado].label : '';
        return (
          fechaStr.toLowerCase().includes(q) ||
          (e.horaInicio && e.horaInicio.toLowerCase().includes(q)) ||
          (e.descripcion && e.descripcion.toLowerCase().includes(q)) ||
          (est && est.toLowerCase().includes(q))
        );
      })
      .sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
  }, [ensayosLista, grupo, busqueda]);

  const toggle = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const selectAll = () => setSelected(new Set(candidatos.map(c => c.id)));
  const clearSel = () => setSelected(new Set());

  const handleSubmit = async () => {
    if (!grupo?.id) return;
    if (!selected.size) {
      toaster.create({ title: 'Selecciona al menos un ensayo', type: 'warning', duration: 2500 });
      return;
    }
    setSaving(true);
    try {
      const res = await ensayosAPI.asignarGrupo({ grupoId: grupo.id, ensayoIds: [...selected] });
      const a = res.data?.actualizados ?? 0;
      toaster.create({ title: `${a} ensayo(s) vinculado(s) a «${grupo.nombre}»`, type: 'success', duration: 3000 });
      onSuccess?.();
      onClose();
    } catch {
      toaster.create({ title: 'Error al vincular ensayos', type: 'error', duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  if (!grupo) return null;

  return (
    <Dialog.Root open={isOpen} size='lg' scrollBehavior="inside" onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxH="90vh">
            <Dialog.Header pb={2}>
              <HStack gap={2} align="center">
                <Box w={3} h={3} rounded="full" bg={grupo.color || '#6B46C1'} flexShrink={0} />
                <Text fontSize="md" fontWeight="700">Añadir ensayos al grupo de ensayos</Text>
              </HStack>
              <Text fontSize="sm" color="gray.500" fontWeight="400" mt={1}>
                «{grupo.nombre}» — se copia el repertorio del grupo de ensayos en cada ensayo (salvo repertorio independiente).
              </Text>
            </Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body pt={0}>
              {loading ? (
                <Flex justify="center" py={10}><Spinner color="purple.500" size="lg" /></Flex>
              ) : (
                <VStack gap={3} align="stretch">
                  <Input
                    size="sm"
                    placeholder="Buscar por fecha, hora, estado, notas…"
                    value={busqueda}
                    onChange={e => setBusqueda(e.target.value)}
                    rounded="xl"
                  />
                  <HStack justify="space-between" flexWrap="wrap" gap={2}>
                    <Text fontSize="xs" color="gray.500">
                      {candidatos.length} ensayo{candidatos.length !== 1 ? 's' : ''} no vinculado{candidatos.length !== 1 ? 's' : ''} a este grupo
                    </Text>
                    <HStack>
                      <Button size="xs" variant="ghost" onClick={selectAll} disabled={!candidatos.length}>
                        Todos
                      </Button>
                      <Button size="xs" variant="ghost" onClick={clearSel} disabled={!selected.size}>
                        Ninguno
                      </Button>
                    </HStack>
                  </HStack>
                  {candidatos.length === 0 ? (
                    <Box py={8} textAlign="center" color="gray.400">
                      <Icon as={FaThList} boxSize={8} mb={2} />
                      <Text fontSize="sm">No hay ensayos que asignar</Text>
                      <Text fontSize="xs" mt={1}>
                        Todos los ensayos de la banda ya están en este grupo, o el filtro no deja resultados
                      </Text>
                    </Box>
                  ) : (
                    <VStack
                      align="stretch"
                      gap={1}
                      maxH="50vh"
                      overflowY="auto"
                      pr={1}
                      css={{
                        '& &::-webkit-scrollbar': { w: '6px' }
                      }}
                    >
                      {candidatos.map(ens => {
                        const gActual = ens.grupoId ? grupos.find(x => x.id === ens.grupoId) : null;
                        const checked = selected.has(ens.id);
                        return (
                          <Flex
                            key={ens.id}
                            align="center"
                            gap={2}
                            py={2}
                            px={2}
                            rounded="lg"
                            borderWidth="1px"
                            borderColor={checked ? 'purple.200' : 'gray.100'}
                            bg={checked ? 'purple.50' : 'white'}
                            cursor="pointer"
                            onClick={() => toggle(ens.id)}
                          >
                            <Checkbox.Root
                              onClick={(ev) => ev.stopPropagation()}
                              onCheckedChange={() => toggle(ens.id)}
                              colorPalette="purple"
                              checked={checked}><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control></Checkbox.Root>
                            <VStack align="start" gap={0} flex={1} minW={0}>
                              <HStack gap={2} flexWrap="wrap">
                                <Text fontSize="sm" fontWeight="600" lineClamp={1}>
                                  {ens.fecha ? formatFechaCorta(ens.fecha) : '—'}
                                  {ens.horaInicio && ` · ${ens.horaInicio}`}
                                  {ens.horaFin && `–${ens.horaFin}`}
                                </Text>
                                {ens.estado && (
                                  <Badge colorPalette={ESTADO_CONFIG[ens.estado]?.color || 'gray'} fontSize="2xs" variant="subtle">
                                    {ESTADO_CONFIG[ens.estado]?.label ?? ens.estado}
                                  </Badge>
                                )}
                              </HStack>
                              {gActual && (
                                <Text fontSize="2xs" color="gray.500" lineClamp={1}>
                                  Grupo de ensayos actual: {gActual.nombre}
                                </Text>
                              )}
                              {!gActual && (
                                <Text fontSize="2xs" color="gray.400">Sin grupo de ensayos</Text>
                              )}
                            </VStack>
                          </Flex>
                        );
                      })}
                    </VStack>
                  )}
                </VStack>
              )}
            </Dialog.Body>
            <Dialog.Footer gap={2}>
              <Text fontSize="sm" color="gray.500" mr="auto">
                {selected.size} seleccionado{selected.size !== 1 ? 's' : ''}
              </Text>
              <Button variant="ghost" onClick={onClose}>Cancelar</Button>
              <Button colorPalette="purple" loading={saving} disabled={!selected.size} onClick={handleSubmit}>
                Vincular al grupo de ensayos
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}

// ─── GrupoCard ───────────────────────────────────────────────────────────────

function GrupoCard({ grupo, onEdit, onDelete, onFilter, isFiltered, onVincularEnsayos }) {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  return (
    <Card.Root
      bg={isFiltered ? `${grupo.color}15` : bg}
      border="1px"
      borderColor={isFiltered ? grupo.color : borderColor}
      borderLeft="4px solid"
      borderLeftColor={grupo.color}
      borderRadius="xl"
      shadow="sm"
      cursor="pointer"
      onClick={() => onFilter(grupo)}
      _hover={{ shadow: 'md' }}
      transition="all 0.15s"
    >
      <Card.Body py={2} px={3}>
        <Flex justify="space-between" align="center">
          <VStack align="start" gap={0.5} flex={1} minW={0}>
            <Text fontSize="sm" fontWeight="700" lineClamp={1}>{grupo.nombre}</Text>
            <HStack gap={2}>
              <Text fontSize="xs" color="gray.500">
                {grupo._count?.ensayos ?? 0} ensayo{grupo._count?.ensayos !== 1 ? 's' : ''}
              </Text>
              {grupo.actuacion && (
                <Badge colorPalette="blue" fontSize="2xs" maxW="120px" isTruncated title={grupo.actuacion.nombre}>
                  🎺 {grupo.actuacion.nombre}
                </Badge>
              )}
            </HStack>
          </VStack>
          <HStack gap={1}>
            <Tooltip content="Añadir ensayos al grupo de ensayos" showArrow positioning={{
              placement: "top"
            }}>
              <IconButton
                size="xs"
                variant="ghost"
                colorPalette="purple"
                aria-label="Añadir ensayos al grupo de ensayos"
                onClick={e => { e.stopPropagation(); onVincularEnsayos?.(grupo); }}><FaLink /></IconButton>
            </Tooltip>
            <IconButton
              size="xs"
              variant="ghost"
              colorPalette="gray"
              aria-label="Editar grupo de ensayos"
              onClick={e => { e.stopPropagation(); onEdit(grupo); }}><FaEdit /></IconButton>
            <IconButton
              size="xs"
              variant="ghost"
              colorPalette="red"
              aria-label="Eliminar grupo de ensayos"
              onClick={e => { e.stopPropagation(); onDelete(grupo); }}><FaTrash /></IconButton>
          </HStack>
        </Flex>
      </Card.Body>
    </Card.Root>
  );
}

// ─── Modal Sedes ─────────────────────────────────────────────────────────────

const SERVICIOS_OPCIONES = ['WiFi', 'Parking', 'WC', 'Calefacción', 'Aire acondicionado', 'Piano', 'Atriles', 'Sillas', 'Vestuario', 'Megafonía', 'Proyector'];

function SedesModal({ isOpen, onClose, sedes, onSaved }) {
  
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const [vista, setVista] = useState('lista'); // 'lista' | 'form'
  const [editingSede, setEditingSede] = useState(null);
  const [form, setForm] = useState({ nombre: '', direccion: '', lat: '', lng: '', imagen: '', servicios: [], notas: '', predeterminada: false });
  const [saving, setSaving] = useState(false);
  const [sugerencias, setSugerencias] = useState([]);
  const [buscandoDir, setBuscandoDir] = useState(false);
  const debounceRef = useRef(null);
  const [imagenFile, setImagenFile] = useState(null);
  const [imagenPreview, setImagenPreview] = useState('');
  const fileInputRef = useRef(null);
  const [servicioCustom, setServicioCustom] = useState('');

  const resetForm = () => {
    setForm({ nombre: '', direccion: '', lat: '', lng: '', imagen: '', servicios: [], notas: '', predeterminada: false });
    setSugerencias([]);
    setServicioCustom('');
    setImagenFile(null);
    setImagenPreview('');
  };

  const openNew = () => {
    setEditingSede(null);
    resetForm();
    setVista('form');
  };

  const openEdit = (sede) => {
    setEditingSede(sede);
    setForm({
      nombre: sede.nombre ?? '',
      direccion: sede.direccion ?? '',
      lat: sede.lat ?? '',
      lng: sede.lng ?? '',
      imagen: sede.imagen ?? '',
      servicios: sede.servicios ?? [],
      notas: sede.notas ?? '',
      predeterminada: sede.predeterminada ?? false,
    });
    setImagenFile(null);
    setImagenPreview(sede.imagen ?? '');
    setVista('form');
  };

  const buscarDireccion = (valor) => {
    setForm(f => ({ ...f, direccion: valor, lat: '', lng: '' }));
    setSugerencias([]);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (valor.trim().length < 3) return;
    debounceRef.current = setTimeout(async () => {
      setBuscandoDir(true);
      try {
        const q = encodeURIComponent(valor);
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${q}&format=json&limit=5&addressdetails=1`,
          { headers: { 'Accept-Language': 'es' } }
        );
        const data = await res.json();
        setSugerencias(data);
      } catch {
        setSugerencias([]);
      } finally {
        setBuscandoDir(false);
      }
    }, 400);
  };

  const seleccionarSugerencia = (item) => {
    setForm(f => ({
      ...f,
      direccion: item.display_name,
      lat: parseFloat(item.lat).toFixed(6),
      lng: parseFloat(item.lon).toFixed(6),
    }));
    setSugerencias([]);
  };

  const addServicioCustom = () => {
    const val = servicioCustom.trim();
    if (!val) return;
    if (!form.servicios.includes(val)) {
      setForm(f => ({ ...f, servicios: [...f.servicios, val] }));
    }
    setServicioCustom('');
  };

  const toggleServicio = (s) => setForm(f => ({
    ...f,
    servicios: f.servicios.includes(s) ? f.servicios.filter(x => x !== s) : [...f.servicios, s],
  }));

  const handleSave = async () => {
    if (!form.nombre.trim()) { toaster.create({ title: 'El nombre es obligatorio', type: 'warning' }); return; }
    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre,
        direccion: form.direccion || undefined,
        lat: form.lat !== '' ? parseFloat(form.lat) : undefined,
        lng: form.lng !== '' ? parseFloat(form.lng) : undefined,
        servicios: form.servicios,
        notas: form.notas || undefined,
        predeterminada: form.predeterminada,
      };
      let sedeId;
      if (editingSede) {
        await ensayosAPI.updateSede(editingSede.id, payload);
        sedeId = editingSede.id;
        toaster.create({ title: 'Sede actualizada', type: 'success', duration: 2000 });
      } else {
        const res = await ensayosAPI.createSede(payload);
        sedeId = res.data.id;
        toaster.create({ title: 'Sede creada', type: 'success', duration: 2000 });
      }
      // Upload image if a new file was selected
      if (imagenFile && sedeId) {
        await ensayosAPI.uploadSedeImagen(sedeId, imagenFile);
      }
      await onSaved();
      setVista('lista');
    } catch {
      toaster.create({ title: 'Error al guardar', type: 'error', duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sede) => {
    try {
      await ensayosAPI.deleteSede(sede.id);
      toaster.create({ title: 'Sede eliminada', type: 'success', duration: 2000 });
      await onSaved();
    } catch {
      toaster.create({ title: 'Error al eliminar', type: 'error', duration: 3000 });
    }
  };

  const handleSetPredeterminada = async (sede) => {
    try {
      await ensayosAPI.updateSede(sede.id, { predeterminada: true });
      toaster.create({ title: `"${sede.nombre}" marcada como predeterminada`, type: 'success', duration: 2000 });
      await onSaved();
    } catch {
      toaster.create({ title: 'Error', type: 'error', duration: 2000 });
    }
  };

  const mapUrl = (lat, lng) =>
    `https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.005},${lat - 0.005},${parseFloat(lng) + 0.005},${parseFloat(lat) + 0.005}&layer=mapnik&marker=${lat},${lng}`;

  return (
    <Dialog.Root open={isOpen} size='xl' scrollBehavior="inside" onOpenChange={e => {
      if (!e.open)
        { setVista('lista'); onClose(); }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxH="90vh">
            <Dialog.Header>
              <HStack gap={2}>
                {vista === 'form' && (
                  <IconButton
                    size="xs"
                    variant="ghost"
                    aria-label="Volver"
                    onClick={() => setVista('lista')}><Icon as={FaChevronLeft} /></IconButton>
                )}
                <Icon as={FaMapMarkerAlt} color="teal.500" />
                <Text>{vista === 'lista' ? 'Sedes de ensayo' : (editingSede ? `Editar: ${editingSede.nombre}` : 'Nueva sede')}</Text>
              </HStack>
            </Dialog.Header>
            <Dialog.CloseTrigger />
            <Dialog.Body pb={4}>
              {/* ── Lista de sedes ── */}
              {vista === 'lista' && (
                <VStack gap={3} align="stretch">
                  {sedes.length === 0 ? (
                    <Flex direction="column" align="center" py={10} gap={3} color="gray.400">
                      <Icon as={FaMapMarkerAlt} boxSize={12} />
                      <Text fontWeight="600">Aún no tienes sedes configuradas</Text>
                      <Text fontSize="sm">Añade el local donde ensayáis para seleccionarlo fácilmente al crear ensayos.</Text>
                      <Button colorPalette="teal" onClick={openNew}><Icon as={FaPlus} />Añadir primera sede</Button>
                    </Flex>
                  ) : (
                    sedes.map(s => (
                      <Box key={s.id} p={4} rounded="xl" borderWidth="1px" borderColor={s.predeterminada ? 'teal.300' : borderColor} bg={s.predeterminada ? 'teal.50' : bg} position="relative">
                        <Flex justify="space-between" align="flex-start">
                          <HStack gap={3} align="flex-start">
                            <Box mt={0.5}>
                              {s.predeterminada ? (
                                <Badge colorPalette="teal" fontSize="2xs">⭐ Predeterminada</Badge>
                              ) : (
                                <Badge colorPalette="gray" variant="outline" fontSize="2xs" cursor="pointer" onClick={() => handleSetPredeterminada(s)}>
                                  Marcar predeterminada
                                </Badge>
                              )}
                            </Box>
                          </HStack>
                          <HStack gap={1}>
                            <IconButton
                              size="xs"
                              variant="ghost"
                              colorPalette="teal"
                              aria-label="Editar"
                              onClick={() => openEdit(s)}><Icon as={FaEdit} /></IconButton>
                            <IconButton
                              size="xs"
                              variant="ghost"
                              colorPalette="red"
                              aria-label="Eliminar"
                              onClick={() => handleDelete(s)}><Icon as={FaTrash} /></IconButton>
                          </HStack>
                        </Flex>
                        <Text fontWeight="700" fontSize="md" mt={1}>{s.nombre}</Text>
                        {s.direccion && <Text fontSize="sm" color="gray.500">📍 {s.direccion}</Text>}
                        {s.servicios?.length > 0 && (
                          <Wrap mt={2} gap={1}>
                            {s.servicios.map(sv => <WrapItem key={sv}><Badge colorPalette="gray" fontSize="2xs">{sv}</Badge></WrapItem>)}
                          </Wrap>
                        )}
                        {s.imagen && (
                          <Box mt={2} rounded="lg" overflow="hidden" maxH="120px">
                            <img src={s.imagen} alt={s.nombre} style={{ width: '100%', height: '120px', objectFit: 'cover' }} />
                          </Box>
                        )}
                      </Box>
                    ))
                  )}
                </VStack>
              )}

              {/* ── Formulario ── */}
              {vista === 'form' && (
                <VStack gap={4} align="stretch">
                  <Field.Root required>
                    <Field.Label fontSize="sm">Nombre del local</Field.Label>
                    <Input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Ej: Casa de la Cultura, Sede social..." />
                  </Field.Root>

                  <Field.Root>
                    <Field.Label fontSize="sm">Dirección</Field.Label>
                    <Box position="relative">
                      <InputGroup>
                        <Input
                          value={form.direccion}
                          onChange={e => buscarDireccion(e.target.value)}
                          placeholder="Empieza a escribir la dirección..."
                          autoComplete="off"
                        />
                        {buscandoDir && (
                          <InputLeftElement pointerEvents="none">
                            <Spinner size="xs" color="teal.400" />
                          </InputLeftElement>
                        )}
                      </InputGroup>
                      {sugerencias.length > 0 && (
                        <Box
                          position="absolute"
                          top="100%"
                          left={0}
                          right={0}
                          zIndex={1000}
                          bg={bg}
                          borderWidth="1px"
                          borderColor={borderColor}
                          rounded="xl"
                          shadow="lg"
                          mt={1}
                          overflow="hidden"
                        >
                          {sugerencias.map((s, i) => (
                            <Box
                              key={i}
                              px={3}
                              py={2}
                              cursor="pointer"
                              _hover={{ bg: 'teal.50' }}
                              borderBottom={i < sugerencias.length - 1 ? '1px solid' : 'none'}
                              borderBottomColor={borderColor}
                              onClick={() => seleccionarSugerencia(s)}
                            >
                              <Text fontSize="sm" lineClamp={1}>📍 {s.display_name}</Text>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                    {form.lat && form.lng && (
                      <Text fontSize="xs" color="teal.600" mt={1}>
                        ✓ Ubicación encontrada
                      </Text>
                    )}
                  </Field.Root>

                  {form.lat && form.lng && (
                    <Box rounded="xl" overflow="hidden" h="180px" borderWidth="1px" borderColor={borderColor}>
                      <iframe
                        title="mapa"
                        width="100%"
                        height="180"
                        frameBorder="0"
                        scrolling="no"
                        src={mapUrl(form.lat, form.lng)}
                        style={{ border: 'none' }}
                      />
                    </Box>
                  )}

                  <Field.Root>
                    <Field.Label fontSize="sm">Imagen del local</Field.Label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      style={{ display: 'none' }}
                      onChange={e => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        setImagenFile(f);
                        setImagenPreview(URL.createObjectURL(f));
                      }}
                    />
                    {imagenPreview ? (
                      <Box position="relative" rounded="xl" overflow="hidden" h="130px" cursor="pointer" onClick={() => fileInputRef.current?.click()}>
                        <img src={imagenPreview} alt="preview" style={{ width: '100%', height: '130px', objectFit: 'cover' }} />
                        <Box
                          position="absolute" inset={0}
                          bg="blackAlpha.400"
                          opacity={0}
                          _hover={{ opacity: 1 }}
                          display="flex" alignItems="center" justifyContent="center"
                          transition="opacity 0.2s"
                        >
                          <VStack gap={1}>
                            <Icon as={FaEdit} color="white" boxSize={5} />
                            <Text color="white" fontSize="xs" fontWeight="700">Cambiar imagen</Text>
                          </VStack>
                        </Box>
                        <IconButton
                          size="xs"
                          colorPalette="red"
                          position="absolute"
                          top={1}
                          right={1}
                          rounded="full"
                          aria-label="Quitar imagen"
                          onClick={async e => {
                            e.stopPropagation();
                            if (editingSede?.imagen && !imagenFile) {
                              try { await ensayosAPI.removeSedeImagen(editingSede.id); await onSaved(); } catch {}
                            }
                            setImagenFile(null);
                            setImagenPreview('');
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}><Icon as={FaTimes} /></IconButton>
                      </Box>
                    ) : (
                      <Box
                        border="2px dashed"
                        borderColor={borderColor}
                        rounded="xl"
                        h="100px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        cursor="pointer"
                        _hover={{ borderColor: 'teal.400', bg: 'teal.50' }}
                        transition="all 0.2s"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <VStack gap={1} color="gray.400">
                          <Icon as={FaPlus} boxSize={5} />
                          <Text fontSize="xs">Subir imagen (JPEG, PNG, WebP · máx. 8 MB)</Text>
                        </VStack>
                      </Box>
                    )}
                  </Field.Root>

                  <Field.Root>
                    <Field.Label fontSize="sm">Servicios disponibles</Field.Label>
                    {/* Opciones predefinidas */}
                    <Wrap gap={2} mb={3}>
                      {SERVICIOS_OPCIONES.map(s => (
                        <WrapItem key={s}>
                          <Badge
                            px={2} py={1}
                            rounded="full"
                            cursor="pointer"
                            colorPalette={form.servicios.includes(s) ? 'teal' : 'gray'}
                            variant={form.servicios.includes(s) ? 'solid' : 'outline'}
                            onClick={() => toggleServicio(s)}
                            userSelect="none"
                            _hover={{ opacity: 0.8 }}
                          >
                            {s}
                          </Badge>
                        </WrapItem>
                      ))}
                    </Wrap>
                    {/* Servicios personalizados ya añadidos */}
                    {form.servicios.filter(s => !SERVICIOS_OPCIONES.includes(s)).length > 0 && (
                      <Wrap gap={2} mb={3}>
                        {form.servicios.filter(s => !SERVICIOS_OPCIONES.includes(s)).map(s => (
                          <WrapItem key={s}>
                            <Badge
                              px={2} py={1}
                              rounded="full"
                              colorPalette="purple"
                              variant="solid"
                              display="flex"
                              alignItems="center"
                              gap={1}
                              cursor="default"
                            >
                              {s}
                              <Box
                                as="span"
                                ml={1}
                                cursor="pointer"
                                fontWeight="bold"
                                _hover={{ opacity: 0.7 }}
                                onClick={() => toggleServicio(s)}
                              >
                                ×
                              </Box>
                            </Badge>
                          </WrapItem>
                        ))}
                      </Wrap>
                    )}
                    {/* Añadir servicio personalizado */}
                    <InputGroup size="sm">
                      <Input
                        placeholder="Añadir servicio personalizado..."
                        value={servicioCustom}
                        onChange={e => setServicioCustom(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addServicioCustom(); } }}
                        rounded="full"
                        borderColor="purple.200"
                        _focus={{ borderColor: 'purple.400', boxShadow: '0 0 0 1px var(--chakra-colors-purple-400)' }}
                      />
                      <InputRightElement>
                        <IconButton
                          size="xs"
                          colorPalette="purple"
                          variant="ghost"
                          rounded="full"
                          aria-label="Añadir servicio"
                          onClick={addServicioCustom}
                          disabled={!servicioCustom.trim()}><Icon as={FaPlus} /></IconButton>
                      </InputRightElement>
                    </InputGroup>
                  </Field.Root>

                  <Field.Root>
                    <Field.Label fontSize="sm">Notas adicionales</Field.Label>
                    <Textarea value={form.notas} onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} placeholder="Información adicional sobre el local..." rows={2} />
                  </Field.Root>

                  <Field.Root display="flex" alignItems="center" gap={3}>
                    <Switch checked={form.predeterminada} onValueChange={e => setForm(f => ({ ...f, predeterminada: e.target.checked }))} colorPalette="teal" id="sede-pred" />
                    <Field.Label htmlFor="sede-pred" mb={0} fontSize="sm">Marcar como sede predeterminada</Field.Label>
                  </Field.Root>
                </VStack>
              )}
            </Dialog.Body>
            <Dialog.Footer gap={2}>
              {vista === 'lista' ? (
                <>
                  <Button variant="ghost" onClick={onClose}>Cerrar</Button>
                  <Button colorPalette="teal" onClick={openNew}><Icon as={FaPlus} />Nueva sede</Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" onClick={() => setVista('lista')} disabled={saving}>Cancelar</Button>
                  <Button colorPalette="teal" loading={saving} onClick={handleSave}>
                    {editingSede ? 'Guardar cambios' : 'Crear sede'}
                  </Button>
                </>
              )}
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}

// ─── Modal Grupo ─────────────────────────────────────────────────────────────

function GrupoModal({ isOpen, onClose, grupo, sedes = [], onSaved, onVincularEnsayosRequest }) {
  
  const [form, setForm] = useState({ nombre: '', descripcion: '', color: '#6B46C1', actuacionId: '', sedeId: '' });
  const [saving, setSaving] = useState(false);
  const [actuaciones, setActuaciones] = useState([]);
  const [modalTab, setModalTab] = useState(0);

  // Repertorio
  const [repertorio, setRepertorio] = useState([]);
  const [carpetas, setCarpetas] = useState([]);
  const [carpetasLoading, setCarpetasLoading] = useState(false);
  const [carpetasSeleccion, setCarpetasSeleccion] = useState([]);
  const { open: isCarpetasOpen, onOpen: onCarpetasOpen, onClose: onCarpetasClose } = useDisclosure();
  const [manualTitulo, setManualTitulo] = useState('');
  const [manualAutor, setManualAutor] = useState('');
  const actuacionIdsMergedRef = useRef(new Set());

  const [ensayosGrupo, setEnsayosGrupo] = useState([]);
  const [ensayosGrupoLoading, setEnsayosGrupoLoading] = useState(false);
  const [quitandoId, setQuitandoId] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setModalTab(0);
    actuacionIdsMergedRef.current = new Set();
    actuacionesAPI.getAll().then(r => setActuaciones(r.data ?? [])).catch(() => {});
    if (grupo) {
      setForm({
        nombre: grupo.nombre ?? '',
        descripcion: grupo.descripcion ?? '',
        color: grupo.color ?? '#6B46C1',
        actuacionId: grupo.actuacionId ?? '',
        sedeId: grupo.sedeId ?? '',
      });
      setRepertorio(normalizeRepertorio(grupo.repertorio));
      if (grupo.actuacionId) actuacionIdsMergedRef.current.add(grupo.actuacionId);
    } else {
      const defSede = sedes.find(s => s.predeterminada);
      setForm({ nombre: '', descripcion: '', color: '#6B46C1', actuacionId: '', sedeId: defSede?.id ?? '' });
      setRepertorio([]);
    }
  }, [grupo, isOpen]);

  useEffect(() => {
    if (!isOpen || !grupo?.id || modalTab !== 2) return;
    setEnsayosGrupoLoading(true);
    ensayosAPI
      .getAll({ grupoId: grupo.id })
      .then(r => setEnsayosGrupo(r.data ?? []))
      .catch(() => setEnsayosGrupo([]))
      .finally(() => setEnsayosGrupoLoading(false));
  }, [isOpen, grupo?.id, modalTab]);

  const quitarEnsayoDelGrupo = async (en) => {
    setQuitandoId(en.id);
    try {
      await ensayosAPI.update(en.id, { grupoId: null });
      setEnsayosGrupo(prev => prev.filter(x => x.id !== en.id));
      await onSaved?.();
      toaster.create({ title: 'Ensayo quitado del grupo de ensayos', type: 'success', duration: 2000 });
    } catch {
      toaster.create({ title: 'No se pudo quitar el ensayo', type: 'error', duration: 3000 });
    } finally {
      setQuitandoId(null);
    }
  };

  const onActuacionVinculadaChange = async (e) => {
    const newId = e.target.value;
    setForm(f => ({ ...f, actuacionId: newId }));
    if (!newId) return;
    if (actuacionIdsMergedRef.current.has(newId)) return;
    try {
      const { data: act } = await actuacionesAPI.getById(newId);
      const add = normalizeRepertorio(act?.repertorio || []).map(x => ({ ...x, id: repLineId() }));
      setRepertorio(prev => [...prev, ...add]);
      actuacionIdsMergedRef.current.add(newId);
      if (add.length) {
        toaster.create({ title: 'Repertorio de la actuación añadido', type: 'success', duration: 2000 });
      }
    } catch {
      toaster.create({ title: 'No se pudo cargar el repertorio de la actuación', type: 'error', duration: 3000 });
    }
  };

  const openCarpetasModal = async () => {
    setCarpetasSeleccion([]);
    onCarpetasOpen();
    setCarpetasLoading(true);
    try {
      const res = await bandasAPI.listPartiturasCarpetas();
      setCarpetas(Array.isArray(res.data) ? res.data : []);
    } catch {
      toaster.create({ title: 'No se pudieron cargar las carpetas', type: 'error', duration: 3000 });
    } finally {
      setCarpetasLoading(false);
    }
  };

  const handleImportarCarpetas = () => {
    const ya = carpetaIdsEnRepertorio(repertorio);
    const toAdd = carpetasSeleccion.filter(id => !ya.has(id));
    const lineas = [];
    const noListoTitles = [];
    for (const id of toAdd) {
      const c = carpetas.find(x => x.id === id);
      if (!c) continue;
      const sorted = [...(c.obras || [])].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
      for (const row of sorted) {
        if (row?.obra?.estado && row.obra.estado !== 'LISTO') {
          const t = String(row.obra?.titulo || row.titulo || '').trim();
          if (t) noListoTitles.push(t);
        }
        const oid = row.obra?.id ?? row.obraId ?? null;
        lineas.push({
          id: repLineId(),
          tipo: 'obra_carpeta',
          titulo: row.obra?.titulo || row.titulo || 'Sin título',
          autor: row.obra?.autor || row.autor || null,
          nombreCarpeta: c.nombre,
          carpetaId: c.id,
          ...(oid ? { obraId: oid } : {}),
        });
      }
    }
    setRepertorio(prev => [...prev, ...lineas]);
    onCarpetasClose();
    toaster.create({ title: `${lineas.length} obra${lineas.length === 1 ? '' : 's'} añadida${lineas.length === 1 ? '' : 's'}`, type: 'success', duration: 2000 });
    if (noListoTitles.length) {
      const uniq = Array.from(new Set(noListoTitles));
      const shown = uniq.slice(0, 5);
      const more = Math.max(0, uniq.length - shown.length);
      toaster.create({
        title: 'Atención',
        description:
          `Las obras ${shown.map((x) => `“${x}”`).join(', ')}${more ? ` y ${more} más` : ''} no están pasadas a LISTO y por lo tanto si compartes este repertorio no serán visibles.`,
        type: 'warning',
        duration: 9000,
        closable: true,
      });
    }
  };

  const addManualObra = () => {
    if (!manualTitulo.trim()) return;
    setRepertorio(prev => [...prev, { id: repLineId(), titulo: manualTitulo.trim(), autor: manualAutor.trim() || null }]);
    setManualTitulo('');
    setManualAutor('');
  };

  const removeRepertorioItem = (id) => setRepertorio(prev => prev.filter(r => r.id !== id));

  const handleSave = async () => {
    if (!form.nombre.trim()) { toaster.create({ title: 'El nombre es obligatorio', type: 'warning' }); return; }
    setSaving(true);
    try {
      const payload = { ...form, actuacionId: form.actuacionId || undefined, sedeId: form.sedeId || undefined, repertorio };
      if (grupo?.id) {
        await ensayosAPI.updateGrupo(grupo.id, payload);
        toaster.create({ title: 'Grupo de ensayos actualizado', type: 'success', duration: 2000 });
      } else {
        await ensayosAPI.createGrupo(payload);
        toaster.create({ title: 'Grupo de ensayos creado', type: 'success', duration: 2000 });
      }
      await onSaved?.();
      onClose();
    } catch {
      toaster.create({ title: 'Error al guardar', type: 'error', duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog.Root open={isOpen} size='lg' scrollBehavior="inside" onOpenChange={e => {
        if (!e.open) {
          onClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content maxH="90vh">
              <Dialog.Header pb={2}>{grupo?.id ? 'Editar grupo de ensayos' : 'Nuevo grupo de ensayos'}</Dialog.Header>
              <Dialog.CloseTrigger />

              <Tabs.Root value={tabsControlledValue(modalTab)} onValueChange={tabsOnValueChange(setModalTab)} colorPalette="purple" variant='enclosed' px={4} pb={0}>
                <Tabs.List flexWrap="wrap">
                  <Tab value="0" fontSize="sm">Datos</Tab>
                  <Tab value="1" fontSize="sm">
                    Repertorio
                    {repertorio.length > 0 && (
                      <Badge ml={2} colorPalette="purple" fontSize="2xs" borderRadius="full">{repertorio.length}</Badge>
                    )}
                  </Tab>
                  {grupo?.id && (
                    <Tab value="2" fontSize="sm">
                      <HStack as="span" gap={1}>
                        <Text as="span">Ensayos</Text>
                        {typeof grupo._count?.ensayos === 'number' && (
                          <Badge colorPalette="purple" fontSize="2xs" borderRadius="full">{grupo._count.ensayos}</Badge>
                        )}
                      </HStack>
                    </Tab>
                  )}
                </Tabs.List>
              </Tabs.Root>

              <Dialog.Body pt={3}>
                {/* ─── Tab Datos ─── */}
                {modalTab === 0 && (
                  <VStack gap={4}>
                    <Field.Root required>
                      <Field.Label>Nombre del grupo de ensayos</Field.Label>
                      <Input
                        value={form.nombre}
                        onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                        placeholder="Ej: Ensayos concierto agosto 2026"
                      />
                    </Field.Root>
                    <Field.Root>
                      <Field.Label>Descripción</Field.Label>
                      <Textarea
                        value={form.descripcion}
                        onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
                        placeholder="Descripción opcional del grupo de ensayos..."
                        rows={2}
                      />
                    </Field.Root>
                    <Field.Root>
                      <Field.Label>Vinculado a una actuación <Text as="span" fontSize="xs" color="gray.500">(opcional)</Text></Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          value={form.actuacionId}
                          onChange={onActuacionVinculadaChange}
                          placeholder="Sin actuación vinculada">
                          {actuaciones.map(a => (
                            <option key={a.id} value={a.id}>
                              {a.nombre}{a.fecha ? ` – ${new Date(a.fecha).toLocaleDateString('es-ES')}` : ''}
                            </option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                      {form.actuacionId && (
                        <Text fontSize="xs" color="blue.500" mt={1}>
                          Los ensayos de este grupo afectan al porcentaje de asistencia de la actuación vinculada.
                        </Text>
                      )}
                    </Field.Root>
                    {sedes.length > 0 && (
                      <Field.Root>
                        <Field.Label>Sede habitual <Text as="span" fontSize="xs" color="gray.500">(opcional)</Text></Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            value={form.sedeId}
                            onChange={e => setForm(f => ({ ...f, sedeId: e.target.value }))}
                            placeholder="Sin sede fija">
                            {sedes.map(s => (
                              <option key={s.id} value={s.id}>
                                {s.predeterminada ? '⭐ ' : ''}{s.nombre}{s.direccion ? ` – ${s.direccion}` : ''}
                              </option>
                            ))}
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </Field.Root>
                    )}
                    <Field.Root>
                      <Field.Label>Color identificativo</Field.Label>
                      <HStack gap={2} flexWrap="wrap">
                        {PALETTE.map(c => (
                          <Box
                            key={c}
                            w={7} h={7}
                            rounded="full"
                            bg={c}
                            cursor="pointer"
                            border={form.color === c ? '3px solid' : '2px solid transparent'}
                            borderColor={form.color === c ? 'gray.600' : 'transparent'}
                            onClick={() => setForm(f => ({ ...f, color: c }))}
                            transition="transform 0.1s"
                            _hover={{ transform: 'scale(1.2)' }}
                          />
                        ))}
                        <Input
                          type="color"
                          value={form.color}
                          onChange={e => setForm(f => ({ ...f, color: e.target.value }))}
                          w={8} h={8} p={0} border="none" cursor="pointer"
                        />
                      </HStack>
                    </Field.Root>
                  </VStack>
                )}

                {/* ─── Tab Repertorio ─── */}
                {modalTab === 1 && (
                  <VStack gap={4} align="stretch">
                    {/* Acciones */}
                    <HStack gap={2} flexWrap="wrap">
                      <Button
                        size="sm"
                        colorPalette="purple"
                        variant="outline"
                        onClick={openCarpetasModal}><Icon as={FaFolder} />Importar carpeta
                                          </Button>
                      <Text fontSize="xs" color="gray.500" flex={1}>
                        Importa obras directamente desde tu Archivo Musical
                      </Text>
                    </HStack>

                    {/* Añadir manual */}
                    <Box p={3} bg="gray.50" rounded="xl" borderWidth="1px" borderColor="gray.200">
                      <Text fontSize="xs" fontWeight="600" color="gray.600" mb={2}>Añadir obra manualmente</Text>
                      <HStack gap={2}>
                        <Input
                          size="sm"
                          placeholder="Título *"
                          value={manualTitulo}
                          onChange={e => setManualTitulo(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && addManualObra()}
                          flex={2}
                        />
                        <Input
                          size="sm"
                          placeholder="Compositor"
                          value={manualAutor}
                          onChange={e => setManualAutor(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && addManualObra()}
                          flex={1}
                        />
                        <IconButton
                          size="sm"
                          colorPalette="purple"
                          aria-label="Añadir"
                          onClick={addManualObra}
                          disabled={!manualTitulo.trim()}><Icon as={FaPlus} /></IconButton>
                      </HStack>
                    </Box>

                    {/* Lista */}
                    {repertorio.length === 0 ? (
                      <Box py={8} textAlign="center" color="gray.400">
                        <Icon as={FaListUl} boxSize={8} mb={2} />
                        <Text fontSize="sm">Sin obras en el repertorio</Text>
                        <Text fontSize="xs">Importa carpetas o añade obras manualmente</Text>
                      </Box>
                    ) : (
                      <VStack gap={1} align="stretch" maxH="40vh" overflowY="auto">
                        {repertorio.map((item, idx) => (
                          <Flex
                            key={item.id}
                            align="center"
                            gap={2}
                            py={2}
                            px={3}
                            rounded="lg"
                            borderWidth="1px"
                            borderColor="gray.100"
                            bg="white"
                          >
                            <Text fontSize="xs" color="gray.400" w="20px" flexShrink={0}>{idx + 1}.</Text>
                            <Box flex={1} minW={0}>
                              <HStack gap={2} flexWrap="wrap">
                                <Text fontSize="sm" fontWeight="600" lineClamp={2} flex={1}>{repItemTitulo(item)}</Text>
                                {isObraCarpetaLine(item) && item.nombreCarpeta && (
                                  <Badge colorPalette="purple" fontSize="2xs" display="inline-flex" alignItems="center" gap="4px">
                                    <Icon as={FaFolder} boxSize="9px" />
                                    {item.nombreCarpeta}
                                  </Badge>
                                )}
                              </HStack>
                              {item.autor && <Text fontSize="xs" color="gray.500">{item.autor}</Text>}
                            </Box>
                            <IconButton
                              size="xs"
                              variant="ghost"
                              colorPalette="red"
                              aria-label="Eliminar"
                              onClick={() => removeRepertorioItem(item.id)}
                              flexShrink={0}><Icon as={FaTrash} /></IconButton>
                          </Flex>
                        ))}
                      </VStack>
                    )}
                  </VStack>
                )}

                {modalTab === 2 && grupo?.id && (
                  <VStack gap={4} align="stretch" py={1}>
                    <Text fontSize="sm" color="gray.600">
                      Ensayos que en este momento pertenecen a «{grupo.nombre}». Puedes quitarlos del grupo de ensayos
                      (no se borra el ensayo) o añadir más con el botón de abajo.
                    </Text>

                    <Box>
                      <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={2}>
                        En este grupo de ensayos ({ensayosGrupo.length})
                      </Text>
                      {ensayosGrupoLoading ? (
                        <Flex justify="center" py={6}><Spinner color="purple.500" size="md" /></Flex>
                      ) : ensayosGrupo.length === 0 ? (
                        <Text fontSize="sm" color="gray.500" py={2}>
                          Aún no hay ensayos vinculados. Usa «Añadir ensayos al grupo de ensayos…».
                        </Text>
                      ) : (
                        <VStack
                          align="stretch"
                          gap={1}
                          maxH="40vh"
                          overflowY="auto"
                          pr={1}
                        >
                          {ensayosGrupo.map(ens => (
                            <Flex
                              key={ens.id}
                              align="center"
                              gap={2}
                              py={2}
                              px={2}
                              rounded="lg"
                              borderWidth="1px"
                              borderColor="gray.100"
                              bg="gray.50"
                            >
                              <VStack align="start" gap={0} flex={1} minW={0}>
                                <HStack gap={2} flexWrap="wrap">
                                  <Text fontSize="sm" fontWeight="600" lineClamp={1}>
                                    {ens.fecha ? formatFechaCorta(ens.fecha) : '—'}
                                    {ens.horaInicio && ` · ${ens.horaInicio}`}
                                    {ens.horaFin && `–${ens.horaFin}`}
                                  </Text>
                                  {ens.estado && (
                                    <Badge colorPalette={ESTADO_CONFIG[ens.estado]?.color || 'gray'} fontSize="2xs" variant="subtle">
                                      {ESTADO_CONFIG[ens.estado]?.label ?? ens.estado}
                                    </Badge>
                                  )}
                                </HStack>
                                {ens.descripcion && (
                                  <Text fontSize="2xs" color="gray.500" lineClamp={1}>{ens.descripcion}</Text>
                                )}
                              </VStack>
                              <Tooltip content="Quitar de este grupo de ensayos (el ensayo sigue existiendo)" showArrow>
                                <IconButton
                                  size="sm"
                                  variant="ghost"
                                  colorPalette="red"
                                  aria-label="Quitar ensayo del grupo de ensayos"
                                  loading={quitandoId === ens.id}
                                  disabled={!!quitandoId}
                                  onClick={() => quitarEnsayoDelGrupo(ens)}
                                  flexShrink={0}><Icon as={FaTimes} /></IconButton>
                              </Tooltip>
                            </Flex>
                          ))}
                        </VStack>
                      )}
                    </Box>

                    <Separator />

                    <VStack align="stretch" gap={1}>
                      <Text fontSize="sm" color="gray.600">
                        Añade varios a la vez (p. ej. una serie recurrente). Se aplica el mismo criterio de repertorio que al
                        editar un ensayo individual.
                      </Text>
                      <Button
                        colorPalette="purple"
                        onClick={() => {
                          onVincularEnsayosRequest?.(grupo);
                          onClose();
                        }}><Icon as={FaLink} />Añadir ensayos al grupo de ensayos…
                                          </Button>
                    </VStack>
                  </VStack>
                )}
              </Dialog.Body>

              <Dialog.Footer gap={2}>
                <Button variant="ghost" onClick={onClose}>Cancelar</Button>
                <Button colorPalette="purple" onClick={handleSave} loading={saving}>
                  {grupo?.id ? 'Guardar cambios' : 'Crear grupo de ensayos'}
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Modal selector de carpetas del Archivo Musical */}
      <Dialog.Root open={isCarpetasOpen} size='md' onOpenChange={e => {
        if (!e.open) {
          onCarpetasClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>Importar carpetas del Archivo Musical</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <Text fontSize="sm" color="gray.600" mb={3}>
                  Selecciona una o más carpetas. Todas las obras que contengan se añadirán al repertorio del grupo de ensayos.
                </Text>
                {carpetasLoading ? (
                  <Flex justify="center" py={8}><Spinner color="purple.500" /></Flex>
                ) : carpetas.length === 0 ? (
                  <Text fontSize="sm" color="gray.500">
                    No hay carpetas creadas en tu Archivo Musical. Créalas desde la sección Archivo Musical → Partituras.
                  </Text>
                ) : (
                  <IsolateCheckboxesFromField>
                  <VStack align="stretch" gap={2} maxH="50vh" overflowY="auto">
                    {carpetas.map(c => {
                      const ya = carpetaIdsEnRepertorio(repertorio).has(c.id);
                      const checked = carpetasSeleccion.includes(c.id);
                      return (
                        <Checkbox.Root
                          key={c.id}
                          disabled={ya}
                          onCheckedChange={() => !ya && setCarpetasSeleccion(prev =>
                            prev.includes(c.id) ? prev.filter(x => x !== c.id) : [...prev, c.id]
                          )}
                          checked={checked}
                        ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                            <HStack gap={2}>
                              <Icon as={FaFolder} color="purple.500" boxSize={4} />
                              <Text as="span" fontWeight="600" fontSize="sm">{c.nombre}</Text>
                              {ya ? (
                                <Text as="span" fontSize="xs" color="gray.500">(ya añadida)</Text>
                              ) : (
                                <Text as="span" fontSize="xs" color="gray.500">({(c.obras || []).length} obra{(c.obras || []).length === 1 ? '' : 's'})</Text>
                              )}
                            </HStack>
                          </Checkbox.Label></Checkbox.Root>
                      );
                    })}
                  </VStack>
                  </IsolateCheckboxesFromField>
                )}
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={2} onClick={onCarpetasClose}>Cancelar</Button>
                <Button
                  colorPalette="purple"
                  onClick={handleImportarCarpetas}
                  disabled={!carpetasSeleccion.length}
                >
                  Importar {carpetasSeleccion.length > 0 ? `(${carpetasSeleccion.length})` : ''}
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
    </>
  );
}

// ─── Modal Ensayo ─────────────────────────────────────────────────────────────

const EMPTY_ENSAYO = {
  fecha: '', horaInicio: '20:00', horaFin: '22:00',
  sedeId: '',
  tipo: 'REGULAR', obligatorio: true, destacado: false,
  descripcion: '', grupoId: '', actuacionId: '', plantillaFormacionId: '',
  convocados: [], estado: 'PROGRAMADO',
};

const EMPTY_RECURRENTE = {
  fechaInicio: '', fechaFin: '', diasSemana: [],
  horaInicio: '20:00', horaFin: '22:00',
  sedeId: '',
  tipo: 'REGULAR', obligatorio: true, destacado: false,
  descripcion: '', grupoId: '',
  convocados: [],
};

function EnsayoModal({ open, isOpen: isOpenProp, onClose, ensayo, grupos, musicos, sedes = [], actuaciones = [], onSaved }) {
  const isOpen = open ?? isOpenProp;
  const navigate = useNavigate();
  const { user } = useAuth();
  const mostrarFlujoSinGrupos =
    !ensayo?.id &&
    grupos.length === 0 &&
    (user?.userType === 'BANDA' || user?.userType === 'BANDA_COLABORADOR');
  const [modo, setModo] = useState('individual'); // 'individual' | 'recurrente'
  const [form, setForm] = useState(EMPTY_ENSAYO);
  const [recForm, setRecForm] = useState(EMPTY_RECURRENTE);
  const [saving, setSaving] = useState(false);
  const [ensayoTab, setEnsayoTab] = useState(0);
  const [repertorio, setRepertorio] = useState([]);
  const [carpetas, setCarpetas] = useState([]);
  const [carpetasLoading, setCarpetasLoading] = useState(false);
  const [carpetasSeleccion, setCarpetasSeleccion] = useState([]);
  const { open: isCarpetasOpen, onOpen: onCarpetasOpen, onClose: onCarpetasClose } = useDisclosure();
  const [manualTitulo, setManualTitulo] = useState('');
  const [manualAutor, setManualAutor] = useState('');
  const [repertorioIndependiente, setRepertorioIndependiente] = useState(false);
  const syncGrupoRepRef = useRef('');
  const actuacionIdsMergedRef = useRef(new Set());
  const [gruposMusicos, setGruposMusicos] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    musicosAPI.getGrupos().then(r => setGruposMusicos(r.data || [])).catch(() => {});
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setEnsayoTab(0);
    actuacionIdsMergedRef.current = new Set();
    if (ensayo) {
      setModo('individual');
      const fecha = ensayo.fecha ? ensayo.fecha.slice(0, 10) : '';
      setForm({
        fecha,
        horaInicio: ensayo.horaInicio ?? '20:00',
        horaFin: ensayo.horaFin ?? '22:00',
        sedeId: ensayo.sedeId ?? '',
        tipo: ensayo.tipo ?? 'REGULAR',
        obligatorio: ensayo.obligatorio ?? true,
        destacado: ensayo.destacado ?? false,
        descripcion: ensayo.descripcion ?? '',
        grupoId: ensayo.grupoId ?? '',
        actuacionId: ensayo.actuacionId ?? '',
        plantillaFormacionId: ensayo.plantillaFormacionId ?? '',
        convocados: (ensayo.convocados ?? []).map(c => c.musicoId ?? c.musico?.id),
        estado: ensayo.estado ?? 'PROGRAMADO',
      });
      setRepertorio(normalizeRepertorio(ensayo.repertorioPropio));
      setRepertorioIndependiente(ensayo.repertorioIndependiente ?? false);
      syncGrupoRepRef.current = ensayo.grupoId ?? '';
      if (ensayo.actuacionId) actuacionIdsMergedRef.current.add(ensayo.actuacionId);
    } else {
      const defSede = sedes.find(s => s.predeterminada);
      const defSedeId = defSede?.id ?? '';
      const todosIds = (musicos || []).map(m => m.id);
      setForm({ ...EMPTY_ENSAYO, sedeId: defSedeId, convocados: todosIds });
      setRecForm({ ...EMPTY_RECURRENTE, sedeId: defSedeId, convocados: [...todosIds] });
      setRepertorio([]);
      setRepertorioIndependiente(false);
      syncGrupoRepRef.current = '';
    }
  }, [ensayo, isOpen, sedes]); // musicos se lee al abrir; no resetear el form si cambia la referencia

  const autoConvocadosDoneRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      autoConvocadosDoneRef.current = false;
      return;
    }
    if (ensayo?.id || autoConvocadosDoneRef.current || !musicos?.length) return;
    autoConvocadosDoneRef.current = true;
    const todosIds = musicos.map(m => m.id);
    setForm(f => (f.convocados.length === 0 ? { ...f, convocados: todosIds } : f));
    setRecForm(f => (f.convocados.length === 0 ? { ...f, convocados: todosIds } : f));
  }, [isOpen, ensayo?.id, musicos]);

  // La lista de ensayos no trae `convocados`; al editar rellenamos checkboxes desde el detalle
  useEffect(() => {
    if (!isOpen || !ensayo?.id) return;
    let cancelled = false;
    ensayosAPI.getOne(ensayo.id)
      .then(res => {
        if (cancelled) return;
        const e = res.data;
        if (!e) return;
        setForm(f => ({
          ...f,
          actuacionId: e.actuacionId ?? '',
          convocados: (e.convocados ?? []).map(c => c.musicoId ?? c.musico?.id).filter(Boolean),
        }));
        if (e.actuacionId) actuacionIdsMergedRef.current.add(e.actuacionId);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen, ensayo?.id]);

  // Al elegir o cambiar de grupo: copia repertorio del grupo (salvo modo independiente)
  useEffect(() => {
    if (!isOpen || repertorioIndependiente) return;
    const gid = modo === 'individual' ? form.grupoId : recForm.grupoId;
    if (gid === syncGrupoRepRef.current) return;
    syncGrupoRepRef.current = gid;
    if (gid) {
      const g = grupos.find(x => x.id === gid);
      if (g) setRepertorio(normalizeRepertorio(g.repertorio));
    } else {
      setRepertorio([]);
    }
  }, [form.grupoId, recForm.grupoId, modo, isOpen, grupos, repertorioIndependiente]);

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setRecField = (key, val) => setRecForm(f => ({ ...f, [key]: val }));

  const activeGrupoId = modo === 'individual' ? form.grupoId : recForm.grupoId;
  const repBadgeCount = repertorio.length;

  const onActuacionVinculadaChange = async (e) => {
    const newId = e.target.value;
    setField('actuacionId', newId);
    if (!newId) return;
    if (actuacionIdsMergedRef.current.has(newId)) return;
    try {
      const { data: act } = await actuacionesAPI.getById(newId);
      const add = normalizeRepertorio(act?.repertorio || []).map(x => ({ ...x, id: repLineId() }));
      if (add.length) {
        setRepertorioIndependiente(true);
        setRepertorio(prev => [...prev, ...add]);
        toaster.create({ title: 'Repertorio de la actuación añadido', type: 'success', duration: 2000 });
      }
      actuacionIdsMergedRef.current.add(newId);
    } catch {
      toaster.create({ title: 'No se pudo cargar el repertorio de la actuación', type: 'error', duration: 3000 });
    }
  };

  const openCarpetasModalEnsayo = async () => {
    setCarpetasSeleccion([]);
    onCarpetasOpen();
    setCarpetasLoading(true);
    try {
      const res = await bandasAPI.listPartiturasCarpetas();
      setCarpetas(Array.isArray(res.data) ? res.data : []);
    } catch {
      toaster.create({ title: 'No se pudieron cargar las carpetas', type: 'error', duration: 3000 });
    } finally {
      setCarpetasLoading(false);
    }
  };

  const handleImportarCarpetasEnsayo = () => {
    const ya = carpetaIdsEnRepertorio(repertorio);
    const toAdd = carpetasSeleccion.filter(id => !ya.has(id));
    const lineas = [];
    const noListoTitles = [];
    for (const id of toAdd) {
      const c = carpetas.find(x => x.id === id);
      if (!c) continue;
      const sorted = [...(c.obras || [])].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
      for (const row of sorted) {
        if (row?.obra?.estado && row.obra.estado !== 'LISTO') {
          const t = String(row.obra?.titulo || row.titulo || '').trim();
          if (t) noListoTitles.push(t);
        }
        const oid = row.obra?.id ?? row.obraId ?? null;
        lineas.push({
          id: repLineId(),
          tipo: 'obra_carpeta',
          titulo: row.obra?.titulo || row.titulo || 'Sin título',
          autor: row.obra?.autor || row.autor || null,
          nombreCarpeta: c.nombre,
          carpetaId: c.id,
          ...(oid ? { obraId: oid } : {}),
        });
      }
    }
    setRepertorioIndependiente(true);
    setRepertorio(prev => [...prev, ...lineas]);
    onCarpetasClose();
    toaster.create({ title: `${lineas.length} obra${lineas.length === 1 ? '' : 's'} añadida${lineas.length === 1 ? '' : 's'}`, type: 'success', duration: 2000 });
    if (noListoTitles.length) {
      const uniq = Array.from(new Set(noListoTitles));
      const shown = uniq.slice(0, 5);
      const more = Math.max(0, uniq.length - shown.length);
      toaster.create({
        title: 'Atención',
        description:
          `Las obras ${shown.map((x) => `“${x}”`).join(', ')}${more ? ` y ${more} más` : ''} no están pasadas a LISTO y por lo tanto si compartes este repertorio no serán visibles.`,
        type: 'warning',
        duration: 9000,
        closable: true,
      });
    }
  };

  const addManualObraEnsayo = () => {
    if (!manualTitulo.trim()) return;
    setRepertorioIndependiente(true);
    setRepertorio(prev => [...prev, { id: repLineId(), titulo: manualTitulo.trim(), autor: manualAutor.trim() || null }]);
    setManualTitulo('');
    setManualAutor('');
  };

  const removeRepertorioItemEnsayo = (id) => {
    setRepertorioIndependiente(true);
    setRepertorio(prev => prev.filter(r => r.id !== id));
  };

  const handleSave = async () => {
    if (modo === 'individual') {
      if (!form.fecha) { toaster.create({ title: 'La fecha es obligatoria', type: 'warning' }); return; }
      if (!form.horaInicio) { toaster.create({ title: 'La hora de inicio es obligatoria', type: 'warning' }); return; }
      if (musicos.length > 0 && !(form.convocados?.length > 0)) {
        toaster.create({
          title: 'Selecciona músicos convocados',
          description: 'Marca Todos, un grupo o al menos un músico.',
          type: 'warning',
          duration: 4000,
        });
        return;
      }
      setSaving(true);
      try {
        const payload = {
          ...form,
          grupoId: ensayo?.id ? (form.grupoId || null) : (form.grupoId || undefined),
          actuacionId: ensayo?.id ? (form.actuacionId || null) : (form.actuacionId || undefined),
          sedeId: ensayo?.id ? (form.sedeId || null) : (form.sedeId || undefined),
          plantillaFormacionId: form.plantillaFormacionId || undefined,
          repertorioPropio: repertorio,
          repertorioIndependiente,
        };
        if (ensayo?.id) {
          await ensayosAPI.update(ensayo.id, payload);
          toaster.create({ title: 'Ensayo actualizado', type: 'success', duration: 2000 });
        } else {
          await ensayosAPI.create(payload);
          toaster.create({ title: 'Ensayo creado', type: 'success', duration: 2000 });
        }
        onSaved();
        onClose();
      } catch {
        toaster.create({ title: 'Error al guardar', type: 'error', duration: 3000 });
      } finally {
        setSaving(false);
      }
    } else {
      if (!recForm.fechaInicio || !recForm.fechaFin) {
        toaster.create({ title: 'Las fechas de inicio y fin son obligatorias', type: 'warning' }); return;
      }
      if (!recForm.diasSemana.length) {
        toaster.create({ title: 'Selecciona al menos un día de la semana', type: 'warning' }); return;
      }
      if (musicos.length > 0 && !(recForm.convocados?.length > 0)) {
        toaster.create({
          title: 'Selecciona músicos convocados',
          description: 'Marca Todos, un grupo o al menos un músico para los ensayos generados.',
          type: 'warning',
          duration: 4000,
        });
        return;
      }
      setSaving(true);
      try {
        const payload = {
          ...recForm,
          grupoId: recForm.grupoId || undefined,
          sedeId: recForm.sedeId || undefined,
          diasSemana: recForm.diasSemana.map(Number),
          repertorioPropio: repertorio,
          repertorioIndependiente,
        };
        const res = await ensayosAPI.createRecurrentes(payload);
        const data = res.data;
        toaster.create({ title: `${data.creados} ensayos creados`, type: 'success', duration: 3000 });
        onSaved();
        onClose();
      } catch {
        toaster.create({ title: 'Error al crear ensayos recurrentes', type: 'error', duration: 3000 });
      } finally {
        setSaving(false);
      }
    }
  };

  return (
    <>
      <Dialog.Root open={isOpen} size='xl' scrollBehavior="inside" onOpenChange={e => {
        if (!e.open) {
          onClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content maxH="90vh">
              <Dialog.Header pb={2}>
                {ensayo?.id ? 'Editar ensayo' : 'Nuevo ensayo'}
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                {!ensayo?.id && (
                  <HStack gap={2} mb={4} p={1} bg="gray.100" rounded="xl">
                    {[{ v: 'individual', label: 'Individual' }, { v: 'recurrente', label: 'Recurrente' }].map(({ v, label }) => (
                      <Button
                        key={v}
                        size="sm"
                        flex={1}
                        colorPalette={modo === v ? 'purple' : 'gray'}
                        variant={modo === v ? 'solid' : 'ghost'}
                        onClick={() => setModo(v)}
                        rounded="lg"
                      >
                        {label}
                      </Button>
                    ))}
                  </HStack>
                )}

                {mostrarFlujoSinGrupos && (
                  <Alert.Root
                    status="info"
                    variant='subtle'
                    borderRadius="lg"
                    mb={4}
                    align="flex-start"
                    borderStartWidth='3px'
                    borderStartColor='colorPalette.solid'>
                    <Alert.Indicator />
                    <Box>
                      <Alert.Title fontSize="sm" mb={1}>
                        Flujo recomendado: actuaciones → grupos de ensayos → ensayos
                      </Alert.Title>
                      <Alert.Description fontSize="sm" display="block">
                        <Text mb={2}>
                          Todavía no tienes <strong>grupos de ensayos</strong>. Sirven para agrupar ensayos y, si quieres,{' '}
                          <strong>vincularlos a una actuación concreta</strong> (misma obra o preparación). Así mantienes el
                          repertorio y el contexto alineados. No confundir con los <strong>grupos de músicos</strong> de Mis músicos.
                        </Text>
                        <List.Root as='ol' gap={2} pl={1} mb={3}>
                          <List.Item>
                            <strong>Actuaciones</strong> — En <strong>Gestión → Actuaciones</strong>, revisa fechas y obras
                            que vais a preparar.
                          </List.Item>
                          <List.Item>
                            <strong>Grupos de ensayos</strong> — Vuelve a <strong>Ensayos</strong> y, en la columna «Grupos de ensayos» (panel
                            izquierdo en escritorio), crea uno o más. Ahí podrás enlazar cada grupo de ensayos a una actuación si
                            lo necesitas.
                          </List.Item>
                          <List.Item>
                            <strong>Ensayos</strong> — Por último, programa los ensayos y asígnalos al grupo de ensayos que toque; el
                            repertorio del grupo se puede reutilizar en cada ensayo.
                          </List.Item>
                        </List.Root>
                        <HStack gap={3} flexWrap="wrap">
                          <Button
                            size="sm"
                            colorPalette="purple"
                            onClick={() => {
                              onClose();
                              navigate('/banda/actuaciones');
                            }}
                          >
                            Ir a Actuaciones
                          </Button>
                          <Button size="sm" variant="outline" onClick={onClose}>
                            Cerrar y crear un grupo de ensayos primero
                          </Button>
                        </HStack>
                      </Alert.Description>
                    </Box>
                  </Alert.Root>
                )}

                <Tabs.Root value={tabsControlledValue(ensayoTab)} onValueChange={tabsOnValueChange(setEnsayoTab)} colorPalette="purple" variant='enclosed' mt={ensayo?.id ? 2 : 0}>
                  <Tabs.List>
                    <Tab value="0" fontSize="sm">Datos</Tab>
                    <Tab value="1" fontSize="sm">
                      Repertorio
                      {repBadgeCount > 0 && (
                        <Badge ml={2} colorPalette="purple" fontSize="2xs" borderRadius="full">{repBadgeCount}</Badge>
                      )}
                    </Tab>
                  </Tabs.List>
                  <TabPanels>
                    <TabPanel value="0" px={0} pt={4}>
                {modo === 'individual' ? (
                  <VStack gap={4} align="stretch" w="full">
                    <SimpleGrid columns={2} gap={3} w="full">
                      <Field.Root required>
                        <Field.Label fontSize="sm">Fecha</Field.Label>
                        <Input type="date" value={form.fecha} onChange={e => setField('fecha', e.target.value)} />
                      </Field.Root>
                      <Field.Root>
                        <Field.Label fontSize="sm">Grupo de ensayos</Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            value={form.grupoId}
                            onChange={e => setField('grupoId', e.target.value)}
                            placeholder="Sin grupo de ensayos">
                            {grupos.map(g => (
                              <option key={g.id} value={g.id}>{g.nombre}</option>
                            ))}
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </Field.Root>
                      <Field.Root required>
                        <Field.Label fontSize="sm">Hora inicio</Field.Label>
                        <Input type="time" value={form.horaInicio} onChange={e => setField('horaInicio', e.target.value)} />
                      </Field.Root>
                      <Field.Root>
                        <Field.Label fontSize="sm">Hora fin</Field.Label>
                        <Input type="time" value={form.horaFin} onChange={e => setField('horaFin', e.target.value)} />
                      </Field.Root>
                    </SimpleGrid>

                    <Field.Root>
                      <Field.Label fontSize="sm">Actuación de preparación</Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          value={form.actuacionId}
                          onChange={onActuacionVinculadaChange}
                          placeholder="Sin actuación vinculada">
                          {actuaciones.map(a => (
                            <option key={a.id} value={a.id}>
                              {a.nombre}{a.fecha ? ` (${formatFecha(a.fecha)})` : ''}
                            </option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                      <Field.HelperText fontSize="xs">
                        Al elegir una actuación se añade su repertorio a este ensayo (puedes editarlo después).
                      </Field.HelperText>
                    </Field.Root>

                    <Field.Root>
                      <Field.Label fontSize="sm">Sede</Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          value={form.sedeId}
                          onChange={e => setField('sedeId', e.target.value)}
                          placeholder="Sin sede fija">
                          {sedes.map(s => (
                            <option key={s.id} value={s.id}>
                              {s.predeterminada ? '⭐ ' : ''}{s.nombre}{s.direccion ? ` – ${s.direccion}` : ''}
                            </option>
                          ))}
                        </NativeSelect.Field>
                        <NativeSelect.Indicator />
                      </NativeSelect.Root>
                    </Field.Root>

                    <SimpleGrid columns={2} gap={3} w="full">
                      <Field.Root>
                        <Field.Label fontSize="sm">Tipo</Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field value={form.tipo} onChange={e => setField('tipo', e.target.value)}>
                            <option value="REGULAR">Regular</option>
                            <option value="EXTRAORDINARIO">Extraordinario</option>
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </Field.Root>
                      <Field.Root>
                        <Field.Label fontSize="sm">Estado</Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            value={form.estado}
                            onChange={e => setField('estado', e.target.value)}>
                            <option value="PROGRAMADO">Programado</option>
                            <option value="REALIZADO">Realizado</option>
                            <option value="CANCELADO">Cancelado</option>
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </Field.Root>
                    </SimpleGrid>

                    <HStack gap={8} w="full" justify="flex-start" align="center">
                      <HStack gap={2} align="center">
                        <Text fontSize="sm">Obligatorio</Text>
                        <Switch checked={form.obligatorio} onValueChange={e => setField('obligatorio', e.target.checked)} colorPalette="purple" />
                      </HStack>
                      <HStack gap={2} align="center">
                        <Text fontSize="sm">Destacado</Text>
                        <Switch checked={form.destacado} onValueChange={e => setField('destacado', e.target.checked)} colorPalette="yellow" />
                      </HStack>
                    </HStack>

                    <Field.Root>
                      <Field.Label fontSize="sm">Descripción / notas</Field.Label>
                      <Textarea
                        value={form.descripcion}
                        onChange={e => setField('descripcion', e.target.value)}
                        placeholder="Notas del ensayo..."
                        rows={2}
                      />
                    </Field.Root>

                    {musicos.length > 0 && (
                      <MultiCheckboxField
                        w="full"
                        textAlign="left"
                        label={
                          <Text as="div" fontSize="sm" fontWeight="medium" mb={1} textAlign="left">
                            Músicos convocados <Text as="span" color="red.500">*</Text>
                          </Text>
                        }
                      >
                        <Box border="1px" borderColor="gray.200" rounded="md" p={2} w="full">
                          <Tabs.Root
                            size="sm"
                            variant="subtle"
                            colorPalette="purple"
                            defaultValue="0"
                            onValueChange={(details) => {
                              const v = details?.value ?? details;
                              if (String(v) === '0') {
                                setField('convocados', musicos.map(m => m.id));
                              }
                            }}
                          >
                            <Tabs.List mb={2} justifyContent="flex-start">
                              <Tab value="0" fontSize="xs" px={3}>Todos</Tab>
                              <Tab value="1" fontSize="xs" px={3}>Grupos de músicos</Tab>
                            </Tabs.List>
                            <TabPanels>
                              <TabPanel value="0" px={0} py={0}>
                                <Box maxH="180px" overflowY="auto">
                                  <VStack align="stretch" gap={1}>
                                    {(() => {
                                      const allIds = musicos.map(m => m.id);
                                      const allChecked = allIds.length > 0 && allIds.every(id => form.convocados.includes(id));
                                      const someChecked = !allChecked && allIds.some(id => form.convocados.includes(id));
                                      return (
                                        <Checkbox.Root
                                          colorPalette="purple"
                                          size="sm"
                                          checked={someChecked ? 'indeterminate' : allChecked}
                                          onCheckedChange={(details) => {
                                            setForm(f => ({
                                              ...f,
                                              convocados: details.checked === true ? musicos.map(m => m.id) : [],
                                            }));
                                          }}
                                        ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                                          <Text fontSize="sm" fontWeight="600">Todos</Text>
                                        </Checkbox.Label></Checkbox.Root>
                                      );
                                    })()}
                                    {musicos.map(m => (
                                      <Checkbox.Root key={m.id} onCheckedChange={details => {
                                        setForm(f => ({
                                          ...f,
                                          convocados: details.checked === true
                                            ? [...f.convocados, m.id]
                                            : f.convocados.filter(id => id !== m.id),
                                        }));
                                      }}
                                        colorPalette="purple" size="sm" checked={form.convocados.includes(m.id)}><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                                        <Text fontSize="sm">{m.nombre} {m.apellidos || ''}</Text>
                                      </Checkbox.Label></Checkbox.Root>
                                    ))}
                                  </VStack>
                                </Box>
                              </TabPanel>
                              <TabPanel value="1" px={0} py={0}>
                                <Box maxH="180px" overflowY="auto">
                                  {gruposMusicos.length === 0 ? (
                                    <Text fontSize="xs" color="gray.400" textAlign="left" py={4}>No hay grupos de músicos creados</Text>
                                  ) : (
                                    <VStack align="stretch" gap={2}>
                                      {gruposMusicos.map(g => {
                                        const memberIds = (g.miembros || []).map(gm => gm.musico?.id || gm.musicoId).filter(Boolean);
                                        const activeMemberIds = memberIds.filter(id => musicos.some(m => m.id === id));
                                        const allChecked = activeMemberIds.length > 0 && activeMemberIds.every(id => form.convocados.includes(id));
                                        const someChecked = !allChecked && activeMemberIds.some(id => form.convocados.includes(id));
                                        return (
                                          <Box key={g.id} w="full">
                                            <Checkbox.Root
                                              onCheckedChange={details => {
                                                setForm(f => {
                                                  if (details.checked === true) {
                                                    return { ...f, convocados: [...new Set([...f.convocados, ...activeMemberIds])] };
                                                  }
                                                  return { ...f, convocados: f.convocados.filter(id => !activeMemberIds.includes(id)) };
                                                });
                                              }}
                                              colorPalette="purple"
                                              size="sm"
                                              checked={someChecked ? 'indeterminate' : allChecked}><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                                              <HStack gap={1}>
                                                <Text fontSize="sm" fontWeight="600">{g.nombre}</Text>
                                                <Badge colorPalette="purple" fontSize="2xs">{activeMemberIds.length}</Badge>
                                              </HStack>
                                            </Checkbox.Label></Checkbox.Root>
                                            <Text fontSize="2xs" color="gray.500" ml={6} textAlign="left">
                                              {activeMemberIds.map(id => { const m = musicos.find(x => x.id === id); return m ? `${m.nombre} ${m.apellidos || ''}`.trim() : ''; }).filter(Boolean).join(', ')}
                                            </Text>
                                          </Box>
                                        );
                                      })}
                                    </VStack>
                                  )}
                                </Box>
                              </TabPanel>
                            </TabPanels>
                          </Tabs.Root>
                        </Box>
                        <Text fontSize="xs" color={form.convocados.length ? 'gray.500' : 'red.500'} mt={1} textAlign="left">
                          {form.convocados.length} músico{form.convocados.length !== 1 ? 's' : ''} convocado{form.convocados.length !== 1 ? 's' : ''}
                          {!form.convocados.length ? ' (obligatorio)' : ''}
                        </Text>
                      </MultiCheckboxField>
                    )}
                  </VStack>
                ) : (
                  <VStack gap={4} align="stretch" w="full">
                    <SimpleGrid columns={2} gap={3} w="full">
                      <Field.Root required>
                        <Field.Label fontSize="sm">Fecha inicio</Field.Label>
                        <Input type="date" value={recForm.fechaInicio} onChange={e => setRecField('fechaInicio', e.target.value)} />
                      </Field.Root>
                      <Field.Root required>
                        <Field.Label fontSize="sm">Fecha fin</Field.Label>
                        <Input type="date" value={recForm.fechaFin} onChange={e => setRecField('fechaFin', e.target.value)} />
                      </Field.Root>
                      <Field.Root required>
                        <Field.Label fontSize="sm">Hora inicio</Field.Label>
                        <Input type="time" value={recForm.horaInicio} onChange={e => setRecField('horaInicio', e.target.value)} />
                      </Field.Root>
                      <Field.Root>
                        <Field.Label fontSize="sm">Hora fin</Field.Label>
                        <Input type="time" value={recForm.horaFin} onChange={e => setRecField('horaFin', e.target.value)} />
                      </Field.Root>
                    </SimpleGrid>

                    <Field.Root required>
                      <Field.Label fontSize="sm">Días de la semana</Field.Label>
                      <HStack gap={2} flexWrap="wrap">
                        {DIAS_SEMANA.map(d => {
                          const sel = recForm.diasSemana.includes(d.value);
                          return (
                            <Button
                              key={d.value}
                              size="sm"
                              colorPalette={sel ? 'purple' : 'gray'}
                              variant={sel ? 'solid' : 'outline'}
                              minW="48px"
                              onClick={() => {
                                setRecField(
                                  'diasSemana',
                                  sel ? recForm.diasSemana.filter(v => v !== d.value) : [...recForm.diasSemana, d.value],
                                );
                              }}
                            >
                              {d.label}
                            </Button>
                          );
                        })}
                      </HStack>
                    </Field.Root>

                    <SimpleGrid columns={2} gap={3} w="full">
                      <Field.Root>
                        <Field.Label fontSize="sm">Sede</Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            value={recForm.sedeId}
                            onChange={e => setRecField('sedeId', e.target.value)}
                            placeholder="Sin sede fija">
                            {sedes.map(s => (
                              <option key={s.id} value={s.id}>
                                {s.predeterminada ? '⭐ ' : ''}{s.nombre}{s.direccion ? ` – ${s.direccion}` : ''}
                              </option>
                            ))}
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </Field.Root>
                      <Field.Root>
                        <Field.Label fontSize="sm">Grupo de ensayos</Field.Label>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            value={recForm.grupoId}
                            onChange={e => setRecField('grupoId', e.target.value)}
                            placeholder="Sin grupo de ensayos">
                            {grupos.map(g => <option key={g.id} value={g.id}>{g.nombre}</option>)}
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </Field.Root>
                    </SimpleGrid>

                    <HStack gap={8} w="full" justify="flex-start" align="center">
                      <HStack gap={2} align="center">
                        <Text fontSize="sm">Obligatorio</Text>
                        <Switch checked={recForm.obligatorio} onValueChange={e => setRecField('obligatorio', e.target.checked)} colorPalette="purple" />
                      </HStack>
                      <HStack gap={2} align="center">
                        <Text fontSize="sm">Destacado</Text>
                        <Switch checked={recForm.destacado} onValueChange={e => setRecField('destacado', e.target.checked)} colorPalette="yellow" />
                      </HStack>
                    </HStack>

                    <Field.Root>
                      <Field.Label fontSize="sm">Descripción</Field.Label>
                      <Textarea value={recForm.descripcion} onChange={e => setRecField('descripcion', e.target.value)} rows={2} />
                    </Field.Root>

                    {musicos.length > 0 && (
                      <MultiCheckboxField
                        w="full"
                        textAlign="left"
                        label={
                          <Text as="div" fontSize="sm" fontWeight="medium" mb={1} textAlign="left">
                            Músicos convocados (todos los ensayos generados) <Text as="span" color="red.500">*</Text>
                          </Text>
                        }
                      >
                        <Box border="1px" borderColor="gray.200" rounded="md" p={2} w="full">
                          <Tabs.Root
                            size="sm"
                            variant="subtle"
                            colorPalette="purple"
                            defaultValue="0"
                            onValueChange={(details) => {
                              const v = details?.value ?? details;
                              if (String(v) === '0') {
                                setRecField('convocados', musicos.map(m => m.id));
                              }
                            }}
                          >
                            <Tabs.List mb={2} justifyContent="flex-start">
                              <Tab value="0" fontSize="xs" px={3}>Todos</Tab>
                              <Tab value="1" fontSize="xs" px={3}>Grupos de músicos</Tab>
                            </Tabs.List>
                            <TabPanels>
                              <TabPanel value="0" px={0} py={0}>
                                <Box maxH="160px" overflowY="auto">
                                  <VStack align="stretch" gap={1}>
                                    {(() => {
                                      const allIds = musicos.map(m => m.id);
                                      const allChecked = allIds.length > 0 && allIds.every(id => recForm.convocados.includes(id));
                                      const someChecked = !allChecked && allIds.some(id => recForm.convocados.includes(id));
                                      return (
                                        <Checkbox.Root
                                          colorPalette="purple"
                                          size="sm"
                                          checked={someChecked ? 'indeterminate' : allChecked}
                                          onCheckedChange={(details) => {
                                            setRecForm(f => ({
                                              ...f,
                                              convocados: details.checked === true ? musicos.map(m => m.id) : [],
                                            }));
                                          }}
                                        ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                                          <Text fontSize="sm" fontWeight="600">Todos</Text>
                                        </Checkbox.Label></Checkbox.Root>
                                      );
                                    })()}
                                    {musicos.map(m => (
                                      <Checkbox.Root key={m.id} onCheckedChange={details => {
                                        setRecForm(f => ({
                                          ...f,
                                          convocados: details.checked === true
                                            ? [...f.convocados, m.id]
                                            : f.convocados.filter(id => id !== m.id),
                                        }));
                                      }}
                                        colorPalette="purple" size="sm" checked={recForm.convocados.includes(m.id)}><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                                        <Text fontSize="sm">{m.nombre} {m.apellidos || ''}</Text>
                                      </Checkbox.Label></Checkbox.Root>
                                    ))}
                                  </VStack>
                                </Box>
                              </TabPanel>
                              <TabPanel value="1" px={0} py={0}>
                                <Box maxH="160px" overflowY="auto">
                                  {gruposMusicos.length === 0 ? (
                                    <Text fontSize="xs" color="gray.400" textAlign="left" py={4}>No hay grupos de músicos creados</Text>
                                  ) : (
                                    <VStack align="stretch" gap={2}>
                                      {gruposMusicos.map(g => {
                                        const memberIds = (g.miembros || []).map(gm => gm.musico?.id || gm.musicoId).filter(Boolean);
                                        const activeMemberIds = memberIds.filter(id => musicos.some(m => m.id === id));
                                        const allChecked = activeMemberIds.length > 0 && activeMemberIds.every(id => recForm.convocados.includes(id));
                                        const someChecked = !allChecked && activeMemberIds.some(id => recForm.convocados.includes(id));
                                        return (
                                          <Box key={g.id} w="full">
                                            <Checkbox.Root
                                              onCheckedChange={details => {
                                                setRecForm(f => {
                                                  if (details.checked === true) {
                                                    return { ...f, convocados: [...new Set([...f.convocados, ...activeMemberIds])] };
                                                  }
                                                  return { ...f, convocados: f.convocados.filter(id => !activeMemberIds.includes(id)) };
                                                });
                                              }}
                                              colorPalette="purple"
                                              size="sm"
                                              checked={someChecked ? 'indeterminate' : allChecked}><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                                              <HStack gap={1}>
                                                <Text fontSize="sm" fontWeight="600">{g.nombre}</Text>
                                                <Badge colorPalette="purple" fontSize="2xs">{activeMemberIds.length}</Badge>
                                              </HStack>
                                            </Checkbox.Label></Checkbox.Root>
                                            <Text fontSize="2xs" color="gray.500" ml={6} textAlign="left">
                                              {activeMemberIds.map(id => { const m = musicos.find(x => x.id === id); return m ? `${m.nombre} ${m.apellidos || ''}`.trim() : ''; }).filter(Boolean).join(', ')}
                                            </Text>
                                          </Box>
                                        );
                                      })}
                                    </VStack>
                                  )}
                                </Box>
                              </TabPanel>
                            </TabPanels>
                          </Tabs.Root>
                        </Box>
                        <Text fontSize="xs" color={recForm.convocados.length ? 'gray.500' : 'red.500'} mt={1} textAlign="left">
                          {recForm.convocados.length} músico{recForm.convocados.length !== 1 ? 's' : ''} convocado{recForm.convocados.length !== 1 ? 's' : ''}
                          {!recForm.convocados.length ? ' (obligatorio)' : ''}
                        </Text>
                      </MultiCheckboxField>
                    )}
                  </VStack>
                )}
                    </TabPanel>

                    <TabPanel value="1" px={0} pt={4}>
                      <VStack gap={4} align="stretch">
                        {activeGrupoId && (
                          <Box p={3} bg="purple.50" borderWidth="1px" borderColor="purple.200" rounded="lg">
                            <Text fontSize="xs" color="purple.800" mb={2}>
                              Con un grupo de ensayos asignado, al guardar se copia en el ensayo el repertorio del grupo. Si cambias de grupo de ensayos, se vuelve a copiar (salvo modo independiente).
                            </Text>
                            <Field.Root display="flex" alignItems="center">
                              <Switch
                                id="ens-rep-indep"
                                checked={repertorioIndependiente}
                                onValueChange={e => {
                                  const v = e.target.checked;
                                  setRepertorioIndependiente(v);
                                  if (!v && activeGrupoId) {
                                    const g = grupos.find(x => x.id === activeGrupoId);
                                    if (g) {
                                      setRepertorio(normalizeRepertorio(g.repertorio));
                                      syncGrupoRepRef.current = activeGrupoId;
                                    }
                                  }
                                }}
                                colorPalette="purple"
                              />
                              <Field.Label htmlFor="ens-rep-indep" mb={0} ml={2} fontSize="sm" cursor="pointer">
                                Repertorio independiente del grupo de ensayos (no sustituir al cambiar de grupo)
                              </Field.Label>
                            </Field.Root>
                          </Box>
                        )}
                        <HStack gap={2} flexWrap="wrap">
                          <Button
                            size="sm"
                            colorPalette="purple"
                            variant="outline"
                            onClick={openCarpetasModalEnsayo}><Icon as={FaFolder} />Importar carpeta
                                                  </Button>
                          <Text fontSize="xs" color="gray.500" flex={1}>
                            Desde tu Archivo Musical, o añade obras manualmente.
                          </Text>
                        </HStack>
                        <Box p={3} bg="gray.50" rounded="xl" borderWidth="1px" borderColor="gray.200">
                          <Text fontSize="xs" fontWeight="600" color="gray.600" mb={2}>Añadir obra manualmente</Text>
                          <HStack gap={2}>
                            <Input
                              size="sm"
                              placeholder="Título *"
                              value={manualTitulo}
                              onChange={e => setManualTitulo(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && addManualObraEnsayo()}
                              flex={2}
                            />
                            <Input
                              size="sm"
                              placeholder="Compositor"
                              value={manualAutor}
                              onChange={e => setManualAutor(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && addManualObraEnsayo()}
                              flex={1}
                            />
                            <IconButton
                              size="sm"
                              colorPalette="purple"
                              aria-label="Añadir"
                              onClick={addManualObraEnsayo}
                              disabled={!manualTitulo.trim()}><Icon as={FaPlus} /></IconButton>
                          </HStack>
                        </Box>
                        {repertorio.length === 0 ? (
                          <Box py={6} textAlign="center" color="gray.400">
                            <Icon as={FaListUl} boxSize={8} mb={2} />
                            <Text fontSize="sm">Sin obras en el repertorio de este ensayo</Text>
                            <Text fontSize="xs">Elige un grupo de ensayos con obras, importa carpetas o añade títulos a mano</Text>
                          </Box>
                        ) : (
                          <VStack gap={1} align="stretch" maxH="36vh" overflowY="auto">
                            {repertorio.map((item, idx) => (
                              <Flex
                                key={item.id}
                                align="center"
                                gap={2}
                                py={2}
                                px={3}
                                rounded="lg"
                                borderWidth="1px"
                                borderColor="gray.100"
                                bg="white"
                              >
                                <Text fontSize="xs" color="gray.400" w="20px" flexShrink={0}>{idx + 1}.</Text>
                                <Box flex={1} minW={0}>
                                  <HStack gap={2} flexWrap="wrap">
                                    <Text fontSize="sm" fontWeight="600" lineClamp={2} flex={1}>{repItemTitulo(item)}</Text>
                                    {isObraCarpetaLine(item) && item.nombreCarpeta && (
                                      <Badge colorPalette="purple" fontSize="2xs" display="inline-flex" alignItems="center" gap="4px">
                                        <Icon as={FaFolder} boxSize="9px" />
                                        {item.nombreCarpeta}
                                      </Badge>
                                    )}
                                  </HStack>
                                  {item.autor && <Text fontSize="xs" color="gray.500">{item.autor}</Text>}
                                </Box>
                                <IconButton
                                  size="xs"
                                  variant="ghost"
                                  colorPalette="red"
                                  aria-label="Eliminar"
                                  onClick={() => removeRepertorioItemEnsayo(item.id)}
                                  flexShrink={0}><Icon as={FaTrash} /></IconButton>
                              </Flex>
                            ))}
                          </VStack>
                        )}
                      </VStack>
                    </TabPanel>
                  </TabPanels>
                </Tabs.Root>
              </Dialog.Body>
              <Dialog.Footer gap={2}>
                <Button variant="ghost" onClick={onClose}>Cancelar</Button>
                <Button colorPalette="purple" onClick={handleSave} loading={saving}>
                  {modo === 'recurrente' ? 'Crear ensayos recurrentes' : ensayo?.id ? 'Guardar cambios' : 'Crear ensayo'}
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root open={isCarpetasOpen} size='md' onOpenChange={e => {
        if (!e.open) {
          onCarpetasClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>Importar carpetas del Archivo Musical</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <Text fontSize="sm" color="gray.600" mb={3}>
                  Selecciona una o más carpetas. Las obras se añadirán al repertorio de este ensayo.
                </Text>
                {carpetasLoading ? (
                  <Flex justify="center" py={8}><Spinner color="purple.500" /></Flex>
                ) : carpetas.length === 0 ? (
                  <Text fontSize="sm" color="gray.500">
                    No hay carpetas creadas en tu Archivo Musical. Créalas desde Archivo Musical → Partituras.
                  </Text>
                ) : (
                  <VStack align="stretch" gap={2} maxH="50vh" overflowY="auto">
                    {carpetas.map(c => {
                      const ya = carpetaIdsEnRepertorio(repertorio).has(c.id);
                      const checked = carpetasSeleccion.includes(c.id);
                      return (
                        <Checkbox.Root
                          key={c.id}
                          disabled={ya}
                          onCheckedChange={() => !ya && setCarpetasSeleccion(prev =>
                            prev.includes(c.id) ? prev.filter(x => x !== c.id) : [...prev, c.id]
                          )}
                          checked={checked}
                        ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                            <HStack gap={2}>
                              <Icon as={FaFolder} color="purple.500" boxSize={4} />
                              <Text as="span" fontWeight="600" fontSize="sm">{c.nombre}</Text>
                              {ya ? (
                                <Text as="span" fontSize="xs" color="gray.500">(ya añadida)</Text>
                              ) : (
                                <Text as="span" fontSize="xs" color="gray.500">({(c.obras || []).length} obra{(c.obras || []).length === 1 ? '' : 's'})</Text>
                              )}
                            </HStack>
                          </Checkbox.Label></Checkbox.Root>
                      );
                    })}
                  </VStack>
                )}
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={2} onClick={onCarpetasClose}>Cancelar</Button>
                <Button
                  colorPalette="purple"
                  onClick={handleImportarCarpetasEnsayo}
                  disabled={!carpetasSeleccion.length}
                >
                  Importar {carpetasSeleccion.length > 0 ? `(${carpetasSeleccion.length})` : ''}
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
    </>
  );
}

// ─── Modal Detalle Ensayo (asistencia + convocados) ──────────────────────────

function EnsayoDetalleModal({ open, isOpen: isOpenProp, onClose, ensayoId, grupos, musicos, actuaciones = [], onEdit, onDelete, onUpdated }) {
  const isOpen = open ?? isOpenProp;
  
  const navigate = useNavigate();
  const [ensayo, setEnsayo] = useState(null);
  const [asistencia, setAsistencia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [busqueda, setBusqueda] = useState('');
  const [notificaciones, setNotificaciones] = useState([]);
  const [loadingNotif, setLoadingNotif] = useState(false);
  const [enviandoNotif, setEnviandoNotif] = useState(false);
  const [actuacionSeleccion, setActuacionSeleccion] = useState('');
  const [actuacionAccion, setActuacionAccion] = useState(false);
  const bg = useColorModeValue('white', 'gray.800');
  const actuacionSectionBg = useColorModeValue('orange.50', 'orange.900');
  const { open: isPasarListaOpen, onOpen: onPasarListaOpen, onClose: onPasarListaClose } = useDisclosure();

  const load = useCallback(async () => {
    if (!ensayoId) return;
    setLoading(true);
    try {
      const [detRes, asistRes] = await Promise.all([
        ensayosAPI.getOne(ensayoId),
        ensayosAPI.getAsistencia(ensayoId),
      ]);
      setEnsayo(detRes.data);
      setAsistencia(asistRes.data);
    } catch {
      toaster.create({ title: 'Error al cargar el ensayo', type: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [ensayoId]);

  const loadNotificaciones = useCallback(async () => {
    if (!ensayoId) return;
    setLoadingNotif(true);
    try {
      const res = await ensayosAPI.getNotificaciones(ensayoId);
      setNotificaciones(res.data ?? []);
    } catch {
      setNotificaciones([]);
    } finally {
      setLoadingNotif(false);
    }
  }, [ensayoId]);

  const handleEnviarAhora = async (horas) => {
    setEnviandoNotif(true);
    try {
      const res = await ensayosAPI.enviarNotificaciones(horas);
      toaster.create({
        title: `Notificaciones enviadas: ${res.data?.enviadas ?? 0}`,
        description: res.data?.omitidas > 0 ? `${res.data.omitidas} omitidas (ya enviadas o sin email)` : undefined,
        type: 'success',
        duration: 4000,
      });
      loadNotificaciones();
    } catch {
      toaster.create({ title: 'Error al enviar notificaciones', type: 'error', duration: 3000 });
    } finally {
      setEnviandoNotif(false);
    }
  };

  useEffect(() => {
    if (isOpen && ensayoId) load();
  }, [isOpen, ensayoId]);

  const handleVincularActuacion = async () => {
    if (!actuacionSeleccion || !ensayoId) return;
    setActuacionAccion(true);
    try {
      const res = await ensayosAPI.update(ensayoId, { actuacionId: actuacionSeleccion });
      setEnsayo(res.data);
      setActuacionSeleccion('');
      onUpdated?.();
      toaster.create({ title: 'Actuación vinculada', type: 'success', duration: 2000 });
    } catch {
      toaster.create({ title: 'No se pudo vincular la actuación', type: 'error', duration: 3000 });
    } finally {
      setActuacionAccion(false);
    }
  };

  const handleDesvincularActuacion = async () => {
    if (!ensayoId) return;
    setActuacionAccion(true);
    try {
      const res = await ensayosAPI.update(ensayoId, { actuacionId: null });
      setEnsayo(res.data);
      onUpdated?.();
      toaster.create({ title: 'Actuación desvinculada', type: 'info', duration: 2000 });
    } catch {
      toaster.create({ title: 'No se pudo desvincular', type: 'error', duration: 3000 });
    } finally {
      setActuacionAccion(false);
    }
  };

  const handleImportarRepertorioActuacion = async () => {
    if (!ensayoId) return;
    setActuacionAccion(true);
    try {
      const res = await ensayosAPI.importRepertorioActuacion(ensayoId);
      setEnsayo(res.data);
      onUpdated?.();
      toaster.create({ title: 'Repertorio de la actuación importado', type: 'success', duration: 2500 });
    } catch {
      toaster.create({ title: 'No se pudo importar el repertorio', type: 'error', duration: 3000 });
    } finally {
      setActuacionAccion(false);
    }
  };

  const handleAsistencia = async (musicoId, estado) => {
    setSavingId(musicoId);
    try {
      await ensayosAPI.setAsistencia(ensayoId, musicoId, { estado });
      setAsistencia(prev => {
        const existing = prev.find(a => a.musicoId === musicoId);
        if (existing) return prev.map(a => a.musicoId === musicoId ? { ...a, estado } : a);
        return [...prev, { musicoId, estado }];
      });
    } catch {
      toaster.create({ title: 'Error al guardar asistencia', type: 'error', duration: 2000 });
    } finally {
      setSavingId(null);
    }
  };

  const grupo = grupos.find(g => g.id === ensayo?.grupoId);
  const grupoColor = grupo?.color ?? '#6B46C1';

  const convocadoIds = (ensayo?.convocados ?? []).map(c => c.musicoId ?? c.musico?.id);
  const convocadosInfo = convocadoIds.map(id => {
    const m = musicos.find(mu => mu.id === id);
    const a = asistencia.find(a => a.musicoId === id);
    return { id, musico: m, estado: a?.estado ?? 'PENDIENTE' };
  }).filter(c => c.musico);

  const filtrados = convocadosInfo.filter(c =>
    busqueda
      ? `${c.musico.nombre} ${c.musico.apellidos || ''}`.toLowerCase().includes(busqueda.toLowerCase())
      : true
  );

  const resumen = { PRESENTE: 0, AUSENTE: 0, JUSTIFICADO: 0, PENDIENTE: 0 };
  convocadosInfo.forEach(c => { resumen[c.estado] = (resumen[c.estado] ?? 0) + 1; });

  return (
    <>
      <Dialog.Root open={isOpen} size='xl' scrollBehavior="inside" onOpenChange={e => {
        if (!e.open) {
          onClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content maxH="90vh">
              <Dialog.Header pb={2} borderLeft="4px solid" borderLeftColor={grupoColor} pl={4}>
                {loading ? <Spinner size="sm" /> : (
                  <VStack align="start" gap={0.5}>
                    <Text fontSize="lg">{ensayo ? formatFecha(ensayo.fecha) : '–'}</Text>
                    {ensayo && (
                      <Text fontSize="sm" color="gray.500" fontWeight="normal">
                        {ensayo.horaInicio}{ensayo.horaFin ? ` – ${ensayo.horaFin}` : ''}
                        {ensayo.lugar ? ` · ${ensayo.lugar}` : ''}
                      </Text>
                    )}
                  </VStack>
                )}
              </Dialog.Header>
              <Dialog.CloseTrigger />

              <Dialog.Body p={0}>
                {loading ? (
                  <Flex justify="center" align="center" h="200px">
                    <Spinner size="xl" color="purple.500" />
                  </Flex>
                ) : ensayo ? (
                  <Tabs.Root value={tabsControlledValue(tabIndex)} onValueChange={tabsOnValueChange((index) => { setTabIndex(index); if (index === 3) loadNotificaciones(); })} colorPalette="purple">
                    <Tabs.List px={4}>
                      <Tab value="0" fontSize="sm">Asistencia</Tab>
                      <Tab value="1" fontSize="sm">Info</Tab>
                      <Tab value="2" fontSize="sm">
                        Repertorio
                        {(() => {
                          const rep = normalizeRepertorio(ensayo.repertorioPropio);
                          return rep.length > 0 ? <Badge ml={1} colorPalette="purple" fontSize="2xs" borderRadius="full">{rep.length}</Badge> : null;
                        })()}
                      </Tab>
                      <Tab value="3" fontSize="sm">Notificaciones</Tab>
                    </Tabs.List>
                    <TabPanels>
                      {/* Asistencia */}
                      <TabPanel value="0" px={4}>
                        {/* Resumen */}
                        <Flex justify="space-between" align="center" mb={3} wrap="wrap" gap={2}>
                          <Text fontSize="sm" color="gray.500" fontWeight="600">
                            {convocadoIds.length} convocado{convocadoIds.length === 1 ? '' : 's'}
                          </Text>
                          {convocadoIds.length > 0 ? (
                            <Button size="xs" colorPalette="purple" onClick={onPasarListaOpen}><Icon as={FaUserCheck} boxSize={3} />Pasar lista
                                                      </Button>
                          ) : null}
                        </Flex>
                        <SimpleGrid columns={4} gap={2} mb={4}>
                          {Object.entries(ASISTENCIA_CONFIG).map(([key, cfg]) => (
                            <Box key={key} p={2} bg={`${cfg.color}.50`} rounded="lg" textAlign="center">
                              <Text fontSize="xl" fontWeight="800" color={`${cfg.color}.600`}>{resumen[key] ?? 0}</Text>
                              <Text fontSize="xs" color={`${cfg.color}.500`}>{cfg.label}</Text>
                            </Box>
                          ))}
                        </SimpleGrid>

                        {convocadoIds.length === 0 ? (
                          <Box py={6} textAlign="center" color="gray.400">
                            <Icon as={FaUsers} boxSize={8} mb={2} />
                            <Text>No hay músicos convocados</Text>
                            <Text fontSize="sm">Edita el ensayo para añadir convocados</Text>
                          </Box>
                        ) : (
                          <>
                            <InputGroup mb={3} size="sm">
                              <InputLeftElement pointerEvents="none">
                                <Icon as={FaSearch} color="gray.400" />
                              </InputLeftElement>
                              <Input
                                placeholder="Buscar músico..."
                                value={busqueda}
                                onChange={e => setBusqueda(e.target.value)}
                                rounded="xl"
                              />
                            </InputGroup>
                            <VStack gap={2} align="stretch">
                              {filtrados.map(({ id, musico, estado }) => {
                                const cfg = ASISTENCIA_CONFIG[estado];
                                return (
                                  <Flex
                                    key={id}
                                    align="center"
                                    justify="space-between"
                                    p={2}
                                    rounded="xl"
                                    bg={useColorModeValue(`${cfg.color}.50`, `${cfg.color}.900`)}
                                    border="1px"
                                    borderColor={`${cfg.color}.100`}
                                  >
                                    <HStack gap={2}>
                                      <Avatar.Root size="xs"><Avatar.Fallback name={`${musico.nombre} ${musico.apellidos || ''}`} /><Avatar.Image src={musico.foto} /></Avatar.Root>
                                      <Text fontSize="sm" fontWeight="600">{musico.nombre} {musico.apellidos || ''}</Text>
                                    </HStack>
                                    <HStack gap={1}>
                                      {savingId === id ? <Spinner size="xs" /> : (
                                        Object.entries(ASISTENCIA_CONFIG).map(([key, cfg]) => (
                                          <Tooltip key={key} content={cfg.label} showArrow>
                                            <IconButton
                                              size="xs"
                                              colorPalette={cfg.color}
                                              variant={estado === key ? 'solid' : 'ghost'}
                                              aria-label={cfg.label}
                                              onClick={() => handleAsistencia(id, key)}
                                              borderRadius="full"><Icon as={cfg.icon} /></IconButton>
                                          </Tooltip>
                                        ))
                                      )}
                                    </HStack>
                                  </Flex>
                                );
                              })}
                            </VStack>
                          </>
                        )}
                      </TabPanel>

                      {/* Info */}
                      <TabPanel value="1" px={4}>
                        <VStack align="start" gap={3}>
                          {grupo && (
                            <HStack gap={2}>
                              <Box w={3} h={3} rounded="full" bg={grupoColor} />
                              <Text fontSize="sm" fontWeight="700">{grupo.nombre}</Text>
                            </HStack>
                          )}
                          <SimpleGrid columns={2} gap={3} w="full">
                            <Box>
                              <Text fontSize="xs" color="gray.500" mb={0.5}>Tipo</Text>
                              <Badge colorPalette={ensayo.tipo === 'EXTRAORDINARIO' ? 'purple' : 'gray'}>
                                {ensayo.tipo === 'EXTRAORDINARIO' ? 'Extraordinario' : 'Regular'}
                              </Badge>
                            </Box>
                            <Box>
                              <Text fontSize="xs" color="gray.500" mb={0.5}>Estado</Text>
                              <Badge colorPalette={ESTADO_CONFIG[ensayo.estado]?.color ?? 'gray'}>
                                {ESTADO_CONFIG[ensayo.estado]?.label ?? ensayo.estado}
                              </Badge>
                            </Box>
                            <Box>
                              <Text fontSize="xs" color="gray.500" mb={0.5}>Asistencia</Text>
                              <Badge colorPalette={ensayo.obligatorio ? 'red' : 'gray'}>
                                {ensayo.obligatorio ? 'Obligatorio' : 'Opcional'}
                              </Badge>
                            </Box>
                            {ensayo.destacado && (
                              <Box>
                                <Badge colorPalette="yellow"><Icon as={FaStar} mr={1} />Destacado</Badge>
                              </Box>
                            )}
                          </SimpleGrid>

                          <Box w="full" p={3} rounded="xl" borderWidth="1px" borderColor="orange.200" bg={actuacionSectionBg}>
                            <Text fontSize="xs" fontWeight="700" color="orange.600" textTransform="uppercase" letterSpacing="wider" mb={2}>
                              Actuación de preparación
                            </Text>
                            {ensayo.actuacion ? (
                              <VStack align="stretch" gap={3}>
                                <ActuacionSombreada actuacion={ensayo.actuacion} />
                                <HStack gap={2} flexWrap="wrap">
                                  <Button
                                    size="xs"
                                    colorPalette="orange"
                                    variant="outline"
                                    onClick={handleImportarRepertorioActuacion}
                                    loading={actuacionAccion}><Icon as={FaSync} />Importar repertorio
                                                                  </Button>
                                  <Button
                                    size="xs"
                                    variant="ghost"
                                    colorPalette="orange"
                                    onClick={() => { onClose(); navigate('/banda/actuaciones'); }}
                                  >
                                    Ir a Actuaciones
                                  </Button>
                                  <Button
                                    size="xs"
                                    variant="ghost"
                                    colorPalette="gray"
                                    onClick={handleDesvincularActuacion}
                                    loading={actuacionAccion}
                                  >
                                    Desvincular
                                  </Button>
                                </HStack>
                              </VStack>
                            ) : (
                              <VStack align="stretch" gap={2}>
                                <Text fontSize="xs" color="gray.600">
                                  Vincula este ensayo a una actuación para ver su contexto e importar su repertorio.
                                </Text>
                                <HStack gap={2} align="end">
                                  <Field.Root flex={1}>
                                    <NativeSelect.Root>
                                      <NativeSelect.Field
                                        size="sm"
                                        placeholder="Seleccionar actuación…"
                                        value={actuacionSeleccion}
                                        onChange={e => setActuacionSeleccion(e.target.value)}>
                                        {actuaciones.map(a => (
                                          <option key={a.id} value={a.id}>
                                            {a.nombre}{a.fecha ? ` (${formatFecha(a.fecha)})` : ''}
                                          </option>
                                        ))}
                                      </NativeSelect.Field>
                                      <NativeSelect.Indicator />
                                    </NativeSelect.Root>
                                  </Field.Root>
                                  <Button
                                    size="sm"
                                    colorPalette="orange"
                                    onClick={handleVincularActuacion}
                                    disabled={!actuacionSeleccion}
                                    loading={actuacionAccion}
                                  >
                                    Vincular
                                  </Button>
                                </HStack>
                                {grupo?.actuacionId && !ensayo.actuacionId && (
                                  <Text fontSize="2xs" color="gray.500">
                                    El grupo de ensayos «{grupo.nombre}» ya está vinculado a una actuación; aquí puedes asociar este ensayo concreto.
                                  </Text>
                                )}
                              </VStack>
                            )}
                          </Box>

                          {/* Sede */}
                          {(() => {
                            const sede = ensayo.sede;
                            const lat = sede?.lat != null ? Number(sede.lat) : null;
                            const lng = sede?.lng != null ? Number(sede.lng) : null;
                            const hasCoords = lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng);

                            const direccionStr = (sede?.direccion || '').trim() || null;
                            const lugarStr = (ensayo.lugar || '').trim() || null;

                            // Google Maps: primero coordenadas; si no, búsqueda por dirección / lugar / nombre
                            let googleMapsUrl = null;
                            if (hasCoords) {
                              googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
                            } else if (direccionStr) {
                              googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(direccionStr)}`;
                            } else if (lugarStr) {
                              googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(lugarStr)}`;
                            } else {
                              const nombreSolo = (sede?.nombre || ensayo.lugar || '').trim();
                              if (nombreSolo) {
                                googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(nombreSolo)}`;
                              }
                            }

                            const imagenSede = (sede?.imagen && String(sede.imagen).trim()) || null;

                            const nombre = sede?.nombre ?? ensayo.lugar;
                            const direccion = sede?.direccion ?? (ensayo.lugar !== sede?.nombre ? ensayo.lugar : null);

                            if (!nombre) return null;

                            return (
                              <Box w="full" rounded="xl" borderWidth="1px" borderColor={sede ? 'teal.200' : 'gray.200'} overflow="hidden">
                                {/* Cabecera */}
                                <Box px={3} pt={3} pb={2} bg={sede ? 'teal.50' : 'gray.50'}>
                                  <HStack gap={2} mb={1}>
                                    <Icon as={FaBuilding} color={sede ? 'teal.600' : 'gray.400'} boxSize={4} />
                                    <Text fontSize="xs" fontWeight="700" color={sede ? 'teal.500' : 'gray.400'} textTransform="uppercase" letterSpacing="wider">
                                      Sede
                                    </Text>
                                  </HStack>
                                  <Text fontSize="md" fontWeight="800" color={sede ? 'teal.800' : 'gray.700'}>{nombre}</Text>
                                  {direccion && (
                                    <HStack gap={1} mt={1}>
                                      <Icon as={FaMapMarkerAlt} color={sede ? 'teal.500' : 'gray.400'} boxSize={3} flexShrink={0} />
                                      <Text fontSize="xs" color={sede ? 'teal.600' : 'gray.500'}>{direccion}</Text>
                                    </HStack>
                                  )}
                                  {sede && Array.isArray(sede.servicios) && sede.servicios.length > 0 && (
                                    <Box mt={2} pt={2} borderTopWidth="1px" borderColor={sede ? 'teal.100' : 'gray.200'}>
                                      <Text fontSize="2xs" fontWeight="700" color={sede ? 'teal.600' : 'gray.500'} textTransform="uppercase" letterSpacing="wider" mb={1.5}>
                                        Servicios disponibles
                                      </Text>
                                      <Wrap gap={1.5}>
                                        {sede.servicios.map(sv => (
                                          <WrapItem key={sv}>
                                            <Badge colorPalette="teal" variant="subtle" fontSize="xs" px={2} py={0.5} rounded="md">
                                              {sv}
                                            </Badge>
                                          </WrapItem>
                                        ))}
                                      </Wrap>
                                    </Box>
                                  )}
                                </Box>

                                {/* Clic a Google Maps: fondo = imagen de la sede si existe, si no degradado teal */}
                                {googleMapsUrl && (
                                  <Box
                                    as="a"
                                    href={googleMapsUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    display="block"
                                    h="180px"
                                    position="relative"
                                    overflow="hidden"
                                    bg={imagenSede ? 'gray.800' : 'linear-gradient(160deg, #e0f2f1 0%, #b2dfdb 45%, #80cbc4 100%)'}
                                    bgImage={imagenSede ? `url(${JSON.stringify(imagenSede)})` : undefined}
                                    bgSize="cover"
                                    bgPos="center"
                                    borderTop="1px solid"
                                    borderColor={sede ? 'teal.100' : 'gray.200'}
                                    _hover={{ filter: 'brightness(1.05)' }}
                                    role="group"
                                  >
                                    {imagenSede && (
                                      <Box
                                        position="absolute"
                                        inset={0}
                                        bgGradient="linear(to-b, blackAlpha.500, blackAlpha.700)"
                                        pointerEvents="none"
                                      />
                                    )}
                                    <VStack
                                      h="100%"
                                      justify="center"
                                      gap={2}
                                      px={4}
                                      textAlign="center"
                                      position="relative"
                                      zIndex={1}
                                    >
                                      <Icon
                                        as={FaMapMarkerAlt}
                                        color={imagenSede ? 'white' : 'teal.700'}
                                        boxSize={10}
                                        filter={imagenSede ? 'drop-shadow(0 2px 6px rgba(0,0,0,0.6))' : 'drop-shadow(0 2px 4px rgba(0,0,0,0.12))'}
                                      />
                                      <Text fontSize="sm" fontWeight="700" color={imagenSede ? 'white' : 'teal.800'} textShadow={imagenSede ? '0 1px 3px rgba(0,0,0,0.8)' : undefined}>
                                        Abrir en Google Maps
                                      </Text>
                                      <Text fontSize="2xs" color={imagenSede ? 'whiteAlpha.900' : 'teal.700'} opacity={imagenSede ? 1 : 0.9} textShadow={imagenSede ? '0 1px 2px rgba(0,0,0,0.9)' : undefined}>
                                        {hasCoords
                                          ? 'Ubicación con coordenadas guardadas'
                                          : direccionStr
                                            ? 'Búsqueda con la dirección de la sede'
                                            : 'Búsqueda con el nombre del lugar'}
                                      </Text>
                                      <HStack gap={1} fontSize="2xs" color={imagenSede ? 'whiteAlpha.800' : 'teal.600'}>
                                        <Icon as={FaExternalLinkAlt} boxSize={3} />
                                        <Text>Nueva ventana</Text>
                                      </HStack>
                                    </VStack>
                                  </Box>
                                )}

                                {googleMapsUrl && (
                                  <Box px={3} py={2} bg={sede ? 'teal.50' : 'gray.50'} borderTop="1px solid" borderTopColor={sede ? 'teal.100' : 'gray.100'}>
                                    <Text
                                      as="a"
                                      href={googleMapsUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      fontSize="xs"
                                      color={sede ? 'teal.600' : 'gray.500'}
                                      fontWeight="600"
                                      _hover={{ textDecoration: 'underline' }}
                                    >
                                      <Icon as={FaExternalLinkAlt} mr={1} display="inline" />
                                      {hasCoords ? 'Abrir en Google Maps →' : 'Buscar en Google Maps →'}
                                    </Text>
                                  </Box>
                                )}
                              </Box>
                            );
                          })()}

                          {ensayo.descripcion && (
                            <Box>
                              <Text fontSize="xs" color="gray.500" mb={0.5}>Notas</Text>
                              <Text fontSize="sm">{ensayo.descripcion}</Text>
                            </Box>
                          )}
                        </VStack>
                      </TabPanel>

                      {/* Repertorio */}
                      <TabPanel value="2" px={4}>
                        {(() => {
                          const rep = normalizeRepertorio(ensayo.repertorioPropio);
                          const indep = ensayo.repertorioIndependiente === true;
                          if (rep.length === 0) {
                            return (
                              <Box py={8} textAlign="center" color="gray.400">
                                <Icon as={FaListUl} boxSize={8} mb={2} />
                                <Text fontSize="sm">Sin repertorio asignado a este ensayo</Text>
                                {ensayo.actuacion ? (
                                  <Button
                                    size="sm"
                                    mt={3}
                                    colorPalette="orange"
                                    variant="outline"
                                    onClick={handleImportarRepertorioActuacion}
                                    loading={actuacionAccion}><Icon as={FaSync} />Importar repertorio de «{ensayo.actuacion.nombre}»
                                                                  </Button>
                                ) : ensayo.grupo ? (
                                  <Text fontSize="xs" mt={1}>
                                    Edita el ensayo o el grupo de ensayos «{ensayo.grupo.nombre}» para añadir obras
                                  </Text>
                                ) : null}
                              </Box>
                            );
                          }
                          return (
                            <VStack gap={3} align="stretch">
                              {indep && (
                                <Badge colorPalette="orange" w="fit-content" fontSize="2xs">Repertorio editado de forma individual</Badge>
                              )}
                              {!indep && ensayo.grupo && (
                                <Text fontSize="xs" color="gray.500" px={1}>
                                  Copia al vincular al grupo de ensayos «{ensayo.grupo.nombre}»; puedes marcar repertorio independiente al editar
                                </Text>
                              )}
                              <VStack gap={1} align="stretch">
                                {rep.map((item, idx) => (
                                  <Flex
                                    key={item.id}
                                    align="center"
                                    gap={3}
                                    py={2}
                                    px={3}
                                    rounded="lg"
                                    borderWidth="1px"
                                    borderColor="gray.100"
                                    bg="gray.50"
                                  >
                                    <Text fontSize="xs" color="gray.400" w="20px" flexShrink={0} textAlign="right">{idx + 1}.</Text>
                                    <Box flex={1} minW={0}>
                                      <HStack gap={2} flexWrap="wrap">
                                        <Text fontSize="sm" fontWeight="600" lineClamp={2} flex={1}>{repItemTitulo(item)}</Text>
                                        {isObraCarpetaLine(item) && item.nombreCarpeta && (
                                          <Badge colorPalette="purple" fontSize="2xs" display="inline-flex" alignItems="center" gap="4px">
                                            <Icon as={FaFolder} boxSize="9px" />
                                            {item.nombreCarpeta}
                                          </Badge>
                                        )}
                                      </HStack>
                                      {item.autor && <Text fontSize="xs" color="gray.500">{item.autor}</Text>}
                                      {item.enlace && (
                                        <Link
                                          href={item.enlace}
                                          fontSize="xs"
                                          color="purple.500"
                                          display="block"
                                          mt={0.5}
                                          target='_blank'
                                          rel='noopener noreferrer'>
                                          <Icon as={FaExternalLinkAlt} mr={1} />Ver partitura
                                        </Link>
                                      )}
                                    </Box>
                                  </Flex>
                                ))}
                              </VStack>
                              <Text fontSize="xs" color="gray.400" textAlign="center">
                                {rep.length} obra{rep.length === 1 ? '' : 's'} en total
                              </Text>
                            </VStack>
                          );
                        })()}
                      </TabPanel>

                      {/* Notificaciones */}
                      <TabPanel value="3" px={4}>
                        <VStack gap={4} align="stretch">
                          {/* Botones de disparo manual */}
                          <Box p={4} bg="orange.50" rounded="xl" borderWidth="1px" borderColor="orange.200">
                            <Text fontWeight="700" fontSize="sm" color="orange.700" mb={1}>Enviar recordatorio ahora</Text>
                            <Text fontSize="xs" color="orange.600" mb={3}>
                              Envía un email de recordatorio a todos los convocados que tengan email y aún no lo hayan recibido.
                            </Text>
                            <HStack gap={2}>
                              <Button
                                size="sm"
                                colorPalette="orange"
                                loading={enviandoNotif}
                                onClick={() => handleEnviarAhora(24)}><Icon as={FaBell} />Aviso 24 h antes
                                                          </Button>
                              <Button
                                size="sm"
                                colorPalette="orange"
                                variant="outline"
                                loading={enviandoNotif}
                                onClick={() => handleEnviarAhora(2)}><Icon as={FaBell} />Aviso 2 h antes
                                                          </Button>
                            </HStack>
                          </Box>

                          {/* Historial */}
                          <Box>
                            <Text fontWeight="600" fontSize="sm" mb={2}>Historial de envíos</Text>
                            {loadingNotif ? (
                              <Flex justify="center" py={6}><Spinner /></Flex>
                            ) : notificaciones.length === 0 ? (
                              <Box py={6} textAlign="center" color="gray.400">
                                <Icon as={FaBell} boxSize={6} mb={2} />
                                <Text fontSize="sm">No se han enviado notificaciones aún</Text>
                              </Box>
                            ) : (
                              <VStack gap={2} align="stretch">
                                {notificaciones.map(n => (
                                  <Flex key={n.id} justify="space-between" align="center" px={3} py={2} bg="gray.50" rounded="lg">
                                    <VStack align="start" gap={0}>
                                      <Text fontSize="sm" fontWeight="600">
                                        {n.musico?.nombre} {n.musico?.apellidos || ''}
                                      </Text>
                                      <Text fontSize="xs" color="gray.500">{n.musico?.email}</Text>
                                    </VStack>
                                    <VStack align="end" gap={0}>
                                      <Badge colorPalette="orange" fontSize="2xs">{n.tipo}</Badge>
                                      <Text fontSize="xs" color="gray.400">
                                        {n.enviadaAt ? new Date(n.enviadaAt).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '–'}
                                      </Text>
                                    </VStack>
                                  </Flex>
                                ))}
                              </VStack>
                            )}
                          </Box>
                        </VStack>
                      </TabPanel>
                    </TabPanels>
                  </Tabs.Root>
                ) : null}
              </Dialog.Body>

              <Dialog.Footer gap={2}>
                {ensayo && onDelete && !isFechaEventoPasada(ensayo.fecha) && (
                  <IconButton
                    colorPalette="red"
                    variant="ghost"
                    aria-label="Eliminar ensayo"
                    mr="auto"
                    onClick={() => { onClose(); onDelete(ensayo); }}><Icon as={FaTrash} /></IconButton>
                )}
                {ensayo && onEdit && (
                  <Button
                    colorPalette="purple"
                    variant="outline"
                    onClick={() => { onClose(); onEdit(ensayo); }}><Icon as={FaEdit} />Editar
                                  </Button>
                )}
                <Button colorPalette="purple" variant="ghost" onClick={onClose}>Cerrar</Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <PasarListaModal
        isOpen={isPasarListaOpen}
        onClose={onPasarListaClose}
        title="Pasar lista"
        items={convocadosInfo.map(({ id, musico, estado }) => ({
          id,
          name: musicoNombreCompleto(musico),
          subtitle: musicoInstrumentoResumen(musico) || undefined,
          foto: musico?.foto || null,
          estado,
        }))}
        statuses={Object.entries(ASISTENCIA_CONFIG).map(([key, cfg]) => ({
          key,
          label: cfg.label,
          color: cfg.color,
          icon: cfg.icon,
        }))}
        onMark={async (musicoId, estado) => {
          await ensayosAPI.setAsistencia(ensayoId, musicoId, { estado });
          setAsistencia((prev) => {
            const existing = prev.find((a) => a.musicoId === musicoId);
            if (existing) return prev.map((a) => (a.musicoId === musicoId ? { ...a, estado } : a));
            return [...prev, { musicoId, estado }];
          });
        }}
      />
    </>
  );
}

// ─── Componente principal ────────────────────────────────────────────────────

export default function Ensayos() {
  const { user } = useAuth();
  useProductTour('banda-ensayos', bandaEnsayosSteps, { autoStart: false });
  const navigate = useNavigate();
  const location = useLocation();
  
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const subtleBg = useColorModeValue('gray.50', 'gray.900');

  // Data
  const [grupos, setGrupos] = useState([]);
  const [ensayos, setEnsayos] = useState([]);
  const [musicos, setMusicos] = useState([]);
  const [actuaciones, setActuaciones] = useState([]);
  const [vacaciones, setVacaciones] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros
  const [filtroGrupo, setFiltroGrupo] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroBusqueda, setFiltroBusqueda] = useState('');

  // Vista
  const [vistaActiva, setVistaActiva] = useState('lista'); // 'lista' | 'calendario' | 'estadisticas'
  const [listaPestana, setListaPestana] = useState('proximos'); // 'proximos' | 'pasados' — solo vista lista
  const [mesVista, setMesVista] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  // Estadísticas
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsFiltroGrupo, setStatsFiltroGrupo] = useState('');
  const [statsDesde, setStatsDesde] = useState('');
  const [statsHasta, setStatsHasta] = useState('');
  const [statsBusqueda, setStatsBusqueda] = useState('');
  const [statsOrden, setStatsOrden] = useState('porcentaje'); // 'porcentaje' | 'nombre' | 'ausencias'

  // Compartir
  const { open: isShareOpen, onOpen: onShareOpen, onClose: onShareClose } = useDisclosure();
  const [sharePaso, setSharePaso] = useState(1); // 1=formato, 2=opciones, 3=mensaje (WA)
  const [shareFormato, setShareFormato] = useState('pdf'); // 'pdf' | 'wa'
  const [shareVistaInforme, setShareVistaInforme] = useState('lista'); // 'lista' | 'calendario'
  const [shareMeses, setShareMeses] = useState([]); // array de 'YYYY-MM'
  const [shareSecciones, setShareSecciones] = useState({
    ensayos: true,
    actuaciones: true,
    grupos: true,
    estadisticas: false,
  });
  const [shareEnsayoDetalle, setShareEnsayoDetalle] = useState({
    repertorio: false,
    sede: false,
    notas: false,
  });
  const [shareWaMensaje, setShareWaMensaje] = useState('');
  const [sharePdfGenerando, setSharePdfGenerando] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const { open: isEnsayoShareOpen, onOpen: onEnsayoShareOpen, onClose: onEnsayoShareClose } = useDisclosure();
  const [ensayoShareDet, setEnsayoShareDet] = useState(null);
  const [ensayoShareAsist, setEnsayoShareAsist] = useState([]);
  const [ensayoShareLoading, setEnsayoShareLoading] = useState(false);
  const [ensayoShareIncluirMusicos, setEnsayoShareIncluirMusicos] = useState(false);
  const [ensayoShareRepertorioUrl, setEnsayoShareRepertorioUrl] = useState('');
  const [ensayoSharePdfGenerando, setEnsayoSharePdfGenerando] = useState(false);

  const loadEstadisticas = useCallback(async (filtros = {}) => {
    setStatsLoading(true);
    try {
      const params = {};
      if (filtros.grupoId) params.grupoId = filtros.grupoId;
      if (filtros.desde) params.desde = filtros.desde;
      if (filtros.hasta) params.hasta = filtros.hasta;
      const res = await ensayosAPI.getEstadisticas(params);
      setStatsData(res.data);
    } catch {
      setStatsData(null);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Modals
  const { open: isGrupoOpen, onOpen: onGrupoOpen, onClose: onGrupoClose } = useDisclosure();
  const { open: isVincularGrupoOpen, onOpen: onVincularGrupoOpen, onClose: onVincularGrupoClose } = useDisclosure();
  const { open: isEnsayoOpen, onOpen: onEnsayoOpen, onClose: onEnsayoClose } = useDisclosure();
  const { open: isDetalleOpen, onOpen: onDetalleOpen, onClose: onDetalleClose } = useDisclosure();
  const { open: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { open: isVacacionOpen, onOpen: onVacacionOpen, onClose: onVacacionClose } = useDisclosure();
  const { open: isSedesOpen, onOpen: onSedesOpen, onClose: onSedesClose } = useDisclosure();
  const cancelRef = useRef();

  // Vacaciones form state
  const [editingVacacion, setEditingVacacion] = useState(null);
  const [vacacionForm, setVacacionForm] = useState({ fechaInicio: '', fechaFin: '', motivo: '', color: '#F6AD55' });
  const [vacacionSaving, setVacacionSaving] = useState(false);

  const [editingGrupo, setEditingGrupo] = useState(null);
  const [vincularGrupoTarget, setVincularGrupoTarget] = useState(null);
  const [editingEnsayo, setEditingEnsayo] = useState(null);
  const [detalleId, setDetalleId] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null); // { tipo: 'ensayo'|'grupo', item }
  const [sidebarTab, setSidebarTab] = useState(0); // 0=grupos, 1=filtros

  useEffect(() => {
    const openEnsayoId = location.state?.openEnsayoId;
    if (openEnsayoId) {
      setDetalleId(openEnsayoId);
      onDetalleOpen();
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state?.openEnsayoId, location.pathname, navigate, onDetalleOpen]);

  // Cargar datos
  const loadGrupos = useCallback(async () => {
    const res = await ensayosAPI.getGrupos();
    setGrupos(res.data ?? []);
  }, []);

  const loadEnsayos = useCallback(async () => {
    const params = {};
    if (filtroGrupo) params.grupoId = filtroGrupo;
    const res = await ensayosAPI.getAll(params);
    setEnsayos(res.data ?? []);
  }, [filtroGrupo]);

  const loadMusicos = useCallback(async () => {
    const res = await musicosAPI.getAll();
    setMusicos(res.data ?? []);
  }, []);

  const loadActuaciones = useCallback(async () => {
    try {
      const res = await actuacionesAPI.getAll();
      setActuaciones(res.data ?? []);
    } catch {
      setActuaciones([]);
    }
  }, []);

  const loadVacaciones = useCallback(async () => {
    try {
      const res = await ensayosAPI.getVacaciones();
      setVacaciones(res.data ?? []);
    } catch {
      setVacaciones([]);
    }
  }, []);

  const loadSedes = useCallback(async () => {
    try {
      const res = await ensayosAPI.getSedes();
      setSedes(res.data ?? []);
    } catch {
      setSedes([]);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadGrupos(), loadEnsayos(), loadMusicos(), loadActuaciones(), loadVacaciones(), loadSedes()]);
    } catch {
      toaster.create({ title: 'Error al cargar datos', type: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [loadGrupos, loadEnsayos, loadMusicos, loadActuaciones, loadVacaciones, loadSedes]);

  // ─── Compartir helpers ───────────────────────────────────────────────────────

  /** Devuelve los meses únicos disponibles en los datos (YYYY-MM) */
  const getMesesDisponibles = useCallback(() => {
    const all = [
      ...ensayos.map(e => e.fecha?.slice(0, 7)),
      ...actuaciones.map(a => a.fecha?.slice(0, 7)),
    ].filter(Boolean);
    return [...new Set(all)].sort();
  }, [ensayos, actuaciones]);

  const openShareModal = () => {
    if (!requiereMusicosEnsayosFn(musicos)) return;
    const meses = getMesesDisponibles();
    const now = new Date().toISOString().slice(0, 7);
    // Preseleccionar mes actual o los primeros 2 meses disponibles
    const preselect = meses.filter(m => m >= now).slice(0, 3);
    setShareMeses(preselect.length ? preselect : meses.slice(0, 3));
    setSharePaso(1);
    setShareFormato('pdf');
    setShareVistaInforme('lista');
    setShareSecciones({ ensayos: true, actuaciones: true, grupos: true, estadisticas: false });
    setShareEnsayoDetalle({ repertorio: false, sede: false, notas: false });
    setShareWaMensaje('');
    onShareOpen();
  };

  /** Construye el mensaje de WhatsApp (texto plano con formato WA) */
  const buildShareWaMensaje = () => {
    const mesesSel = shareMeses.sort();
    const lines = [];
    lines.push('🥁 *PROGRAMACIÓN DE ENSAYOS Y ACTUACIONES*');
    lines.push('');

    for (const mes of mesesSel) {
      const [year, month] = mes.split('-').map(Number);
      const mesLabel = `${MESES[month - 1].toUpperCase()} ${year}`;
      lines.push(`📅 *${mesLabel}*`);
      lines.push('');

      const itemsDeMes = [];
      if (shareSecciones.ensayos) {
        ensayos
          .filter(e => e.fecha?.slice(0, 7) === mes)
          .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
          .forEach(e => {
            const grupo = grupos.find(g => g.id === e.grupoId);
            const fechaD = new Date(e.fecha);
            const dia = `${fechaD.getDate()} ${MESES[fechaD.getMonth()].slice(0, 3)}`;
            const horaStr = e.horaInicio ? ` · ${e.horaInicio}h` : '';
            const lugarStr = e.lugar ? ` · ${e.lugar}` : '';
            const grupoStr = (shareSecciones.grupos && grupo) ? ` _(${grupo.nombre})_` : '';
            const lineasBloque = [
              `🥁 *Ensayo* · ${dia}${horaStr}${lugarStr}${grupoStr}`,
            ];
            if (shareEnsayoDetalle.sede) {
              const sl = lineaSedeEnsayoShare(e);
              if (sl) lineasBloque.push(`   📍 ${sl}`);
            }
            if (shareEnsayoDetalle.notas && e.descripcion) {
              lineasBloque.push(`   ${e.descripcion.split('\n').join('\n   ')}`);
            }
            if (shareEnsayoDetalle.repertorio) {
              lineasRepertorioEnsayoShare(e).forEach((l) => {
                lineasBloque.push(`   ${l}`);
              });
            }
            itemsDeMes.push({
              fecha: new Date(e.fecha),
              lineas: lineasBloque,
            });
          });
      }
      if (shareSecciones.actuaciones) {
        actuaciones
          .filter(a => a.fecha?.slice(0, 7) === mes)
          .sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
          .forEach(a => {
            const fechaD = new Date(a.fecha);
            const dia = `${fechaD.getDate()} ${MESES[fechaD.getMonth()].slice(0, 3)}`;
            const horaStr = a.horaInicio ? ` · ${a.horaInicio}h` : '';
            const lugarStr = a.lugar ? ` · ${a.lugar}` : '';
            itemsDeMes.push({
              fecha: new Date(a.fecha),
              lineas: [`🎺 *Actuación:* ${a.nombre} · ${dia}${horaStr}${lugarStr}`],
            });
          });
      }
      itemsDeMes.sort((a, b) => a.fecha - b.fecha).forEach((i) => {
        i.lineas.forEach((ln) => lines.push(ln));
      });
      lines.push('');
    }

    lines.push('_Enviado desde Cadencya 🎵_');
    return lines.join('\n');
  };

  const handleShareSiguiente = () => {
    if (shareFormato === 'wa') {
      setShareWaMensaje(buildShareWaMensaje());
      setSharePaso(3);
    } else {
      handleSharePdf();
    }
  };

  const closeEnsayoShare = () => {
    setEnsayoShareDet(null);
    setEnsayoShareAsist([]);
    setEnsayoShareIncluirMusicos(false);
    setEnsayoShareRepertorioUrl('');
    onEnsayoShareClose();
  };

  const openEnsayoShare = async (ensayo) => {
    if (!ensayo?.id) return;
    setEnsayoShareIncluirMusicos(false);
    setEnsayoShareRepertorioUrl('');
    setEnsayoShareDet(null);
    setEnsayoShareAsist([]);
    onEnsayoShareOpen();
    setEnsayoShareLoading(true);
    try {
      const [detRes, asistRes, shareRes] = await Promise.all([
        ensayosAPI.getOne(ensayo.id),
        ensayosAPI.getAsistencia(ensayo.id),
        bandasAPI.getRepertorioShare().catch(() => ({ data: null })),
      ]);
      setEnsayoShareDet(detRes.data);
      setEnsayoShareAsist(asistRes.data ?? []);
      setEnsayoShareRepertorioUrl(String(shareRes.data?.shareUrl || '').trim());
    } catch {
      toaster.create({ title: 'Error al cargar el ensayo', type: 'error', duration: 3000 });
      closeEnsayoShare();
    } finally {
      setEnsayoShareLoading(false);
    }
  };

  const handleEnsayoShareWhatsapp = () => {
    if (!ensayoShareDet) return;
    const text = buildEnsayoShareWhatsAppText(ensayoShareDet, ensayoShareAsist, {
      incluirMusicos: ensayoShareIncluirMusicos,
      repertorioUrl: ensayoShareRepertorioUrl,
    });
    openWhatsappChat(null, text);
  };

  const handleEnsayoSharePdf = async () => {
    if (!ensayoShareDet) return;
    setEnsayoSharePdfGenerando(true);
    const w = window.open('', '_blank');
    if (!w) {
      toaster.create({ title: 'Permite ventanas emergentes', description: 'Se abre una ventana para imprimir o guardar en PDF.', type: 'warning' });
      setEnsayoSharePdfGenerando(false);
      return;
    }
    const origin = window.location.origin;
    const fetchImg = async (path) => {
      try {
        const res = await fetch(`${origin}${path}`);
        if (!res.ok) return null;
        const blob = await res.blob();
        return await new Promise((resolve) => {
          const fr = new FileReader();
          fr.onloadend = () => resolve(String(fr.result || ''));
          fr.onerror = () => resolve(null);
          fr.readAsDataURL(blob);
        });
      } catch {
        return null;
      }
    };
    const [logoDataUrl] = await Promise.all([fetchImg('/logo%20y%20texto%20cadencya%20blanco.png')]);
    const IMG_LOGO = logoDataUrl
      ? `<img src="${logoDataUrl}" alt="Cadencya" style="height:36px;display:block">`
      : `<span style="font-size:20px;font-weight:900;color:#fff">Cadencya</span>`;
    const mainInner = buildEnsayoFichaBodyHtml(ensayoShareDet, ensayoShareAsist, {
      incluirMusicos: ensayoShareIncluirMusicos,
    });
    const html = buildPdfInformeDocumentHtml(IMG_LOGO, 'Ficha de ensayo', mainInner);
    w.document.write(html);
    w.document.close();
    try {
      w.focus();
      setTimeout(() => w.print(), 800);
    } catch {}
    setEnsayoSharePdfGenerando(false);
  };

  const handleSharePdf = async () => {
    setSharePdfGenerando(true);
    onShareClose();

    const w = window.open('', '_blank');
    if (!w) {
      toaster.create({ title: 'Permite ventanas emergentes', description: 'El PDF se abre en una nueva ventana.', type: 'warning' });
      setSharePdfGenerando(false);
      return;
    }

    const mesesSel = [...shareMeses].sort();
    const det = { ...shareEnsayoDetalle };
    const seccionesSnap = { ...shareSecciones };
    const vistaSnap = shareVistaInforme;
    const ensayosSnap = ensayos;
    const actuacionesSnap = actuaciones;
    const gruposSnap = grupos;

    const origin = window.location.origin;
    const fetchImg = async (path) => {
      try {
        const res = await fetch(`${origin}${path}`);
        if (!res.ok) return null;
        const blob = await res.blob();
        return await new Promise(resolve => {
          const fr = new FileReader();
          fr.onloadend = () => resolve(String(fr.result || ''));
          fr.onerror = () => resolve(null);
          fr.readAsDataURL(blob);
        });
      } catch { return null; }
    };

    const [logoDataUrl] = await Promise.all([
      fetchImg('/logo%20y%20texto%20cadencya%20blanco.png'),
    ]);

    const IMG_LOGO = logoDataUrl
      ? `<img src="${logoDataUrl}" alt="Cadencya" style="height:36px;display:block">`
      : `<span style="font-size:20px;font-weight:900;color:#fff">Cadencya</span>`;

    const detalleEnsayoPdfHtml = (item) => {
      if (item._tipo !== 'ensayo') return '';
      if (!det.sede && !det.notas && !det.repertorio) return '';
      let extra = '';
      if (det.sede) {
        const sl = lineaSedeEnsayoShare(item);
        if (sl) extra += `<div style="font-size:11px;color:#4a5568;margin-top:6px"><span style="font-weight:600">Sede</span> · ${escapeHtmlShare(sl)}</div>`;
      }
      if (det.notas && item.descripcion) {
        extra += `<div style="color:#718096;font-size:11px;margin-top:4px;font-style:italic">${escapeHtmlShare(item.descripcion).replace(/\n/g, '<br>')}</div>`;
      }
      if (det.repertorio) {
        const rLines = lineasRepertorioEnsayoShare(item);
        if (rLines.length) {
          const lis = rLines
            .map((l) => `<li style="margin:1px 0 1px 0">${escapeHtmlShare(l)}</li>`)
            .join('');
          extra += `<div style="margin-top:6px;font-size:10px;color:#4a5568"><div style="font-weight:600;margin-bottom:2px">Repertorio</div><ol style="margin:0 0 0 18px;padding:0;list-style:decimal">${lis}</ol></div>`;
        }
      }
      return extra;
    };

    const buildMesHtml = (mes) => {
      const [year, month] = mes.split('-').map(Number);
      const mesLabel = `${MESES[month - 1]} ${year}`;

      const itemsDeMes = [];
      if (seccionesSnap.ensayos) {
        ensayosSnap.filter(e => e.fecha?.slice(0, 7) === mes).forEach(e => itemsDeMes.push({ ...e, _tipo: 'ensayo' }));
      }
      if (seccionesSnap.actuaciones) {
        actuacionesSnap.filter(a => a.fecha?.slice(0, 7) === mes).forEach(a => itemsDeMes.push({ ...a, _tipo: 'actuacion' }));
      }
      itemsDeMes.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

      if (vistaSnap === 'lista') {
        const rows = itemsDeMes.map(item => {
          const d = new Date(item.fecha);
          const diaStr = `${DIAS_SEMANA_FULL[d.getDay()]} ${d.getDate()} ${MESES[d.getMonth()]}`;
          if (item._tipo === 'actuacion') {
            return `
              <tr style="background:#fff8f0;border-bottom:1px solid #fed7aa">
                <td style="padding:10px 12px;width:32px;text-align:center;font-size:18px">🎺</td>
                <td style="padding:10px 12px">
                  <div style="font-weight:700;color:#c05621;font-size:14px">${item.nombre}</div>
                  <div style="color:#718096;font-size:12px;margin-top:2px">${diaStr}${item.horaInicio ? ` · ${item.horaInicio}h` : ''}${item.lugar ? ` · ${item.lugar}` : ''}</div>
                </td>
                <td style="padding:10px 12px">
                  <span style="background:#fed7aa;color:#c05621;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:700">Actuación</span>
                </td>
              </tr>`;
          } else {
            const grupo = gruposSnap.find(g => g.id === item.grupoId);
            const grupoColor = grupo?.color ?? '#6B46C1';
            return `
              <tr style="background:#fff;border-bottom:1px solid #f3f4f6">
                <td style="padding:10px 12px;width:32px;text-align:center">
                  <div style="width:12px;height:12px;border-radius:50%;background:${grupoColor};margin:0 auto"></div>
                </td>
                <td style="padding:10px 12px">
                  <div style="font-weight:600;color:#1a202c;font-size:13px">🥁 Ensayo</div>
                  <div style="color:#718096;font-size:12px;margin-top:2px">${diaStr}${item.horaInicio ? ` · ${item.horaInicio}h` : ''}${item.horaFin ? `–${item.horaFin}h` : ''}${item.lugar ? ` · ${item.lugar}` : ''}</div>
                  ${detalleEnsayoPdfHtml(item)}
                </td>
                <td style="padding:10px 12px">
                  ${seccionesSnap.grupos && grupo ? `<span style="background:${grupoColor}22;color:${grupoColor};padding:3px 10px;border-radius:20px;font-size:11px;font-weight:600">${grupo.nombre}</span>` : ''}
                  <div style="margin-top:4px">
                    <span style="background:${item.estado === 'REALIZADO' ? '#c6f6d5' : item.estado === 'CANCELADO' ? '#fed7d7' : '#e9d8fd'};color:${item.estado === 'REALIZADO' ? '#276749' : item.estado === 'CANCELADO' ? '#9b2c2c' : '#553c9a'};padding:2px 8px;border-radius:20px;font-size:10px">${item.estado}</span>
                  </div>
                </td>
              </tr>`;
          }
        }).join('');

        return `
          <div style="page-break-inside:avoid;margin-bottom:24px">
            <div style="background:linear-gradient(135deg,#553c9a,#6b46c1);color:white;padding:10px 16px;border-radius:10px 10px 0 0">
              <span style="font-weight:800;font-size:16px">${mesLabel}</span>
              <span style="margin-left:10px;background:rgba(255,255,255,0.2);padding:2px 10px;border-radius:20px;font-size:12px">${itemsDeMes.length} evento${itemsDeMes.length !== 1 ? 's' : ''}</span>
            </div>
            ${itemsDeMes.length === 0
              ? `<div style="padding:20px;text-align:center;color:#a0aec0;font-style:italic;background:#f9f9fb;border-radius:0 0 10px 10px">Sin eventos este mes</div>`
              : `<table style="width:100%;border-collapse:collapse;border-radius:0 0 10px 10px;overflow:hidden">${rows}</table>`
            }
          </div>`;
      } else {
        // Vista calendario: una <tr> por semana (7 columnas), no toda la cuadrícula en una fila
        const diasEnMes = new Date(year, month, 0).getDate();
        const primerDia = new Date(year, month - 1, 1).getDay();
        const diasSemana = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
        const hoyYmd = (() => {
          const t = new Date();
          return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
        })();

        const toMinDia = (h) => {
          if (h == null || h === '') return 24 * 60;
          const p = String(h).split(':');
          return (parseInt(p[0], 10) || 0) * 60 + (parseInt(p[1] || '0', 10) || 0);
        };
        const sortDelDia = (arr) => [...arr].sort((a, b) => {
          const t = toMinDia(a.horaInicio) - toMinDia(b.horaInicio);
          if (t !== 0) return t;
          if (a._tipo !== b._tipo) return a._tipo === 'ensayo' ? -1 : 1;
          return 0;
        });

        const eventoCalPdfHtml = (e) => {
          if (e._tipo === 'actuacion') {
            const time = e.horaInicio ? ` · ${e.horaInicio}h` : '';
            const sub = e.lugar ? ` · ${String(e.lugar).slice(0, 30)}` : '';
            return `<div style="background:#fff8f0;border-left:3px solid #f6ad55;padding:2px 4px;border-radius:3px;font-size:8px;margin-top:2px;color:#7c2d12;word-wrap:break-word;line-height:1.25">🎺 <span style="font-weight:600">${escapeHtmlShare(e.nombre)}</span>${escapeHtmlShare(time)}${escapeHtmlShare(sub)}</div>`;
          }
          const gc = gruposSnap.find(g => g.id === e.grupoId)?.color ?? '#6B46C1';
          const grupo = gruposSnap.find(g => g.id === e.grupoId);
          const hRango = (e.horaInicio && e.horaFin)
            ? `${e.horaInicio}–${e.horaFin}h`
            : (e.horaInicio ? `${e.horaInicio}h` : '—');
          const grupoTxt = (seccionesSnap.grupos && grupo) ? ` ${escapeHtmlShare(grupo.nombre)}` : '';
          const lugarTxt = e.lugar ? ` · ${escapeHtmlShare(String(e.lugar).slice(0, 26))}` : '';
          let out = `<div style="background:${gc};color:white;padding:2px 4px;border-radius:3px;font-size:8px;margin-top:2px;word-wrap:break-word;line-height:1.25"><span style="font-weight:700">${escapeHtmlShare(hRango)}</span>${grupoTxt}${lugarTxt}</div>`;
          if (det.sede) {
            const sl = lineaSedeEnsayoShare(e);
            if (sl) {
              out += `<div style="font-size:7px;color:#2d3748;margin-top:1px;line-height:1.2;word-wrap:break-word">📍 ${escapeHtmlShare(String(sl).slice(0, 64))}</div>`;
            }
          }
          if (det.notas && e.descripcion) {
            const first = String(e.descripcion).split('\n')[0];
            const n = first.slice(0, 90);
            out += `<div style="font-size:7px;color:#4a5568;font-style:italic;margin-top:1px;line-height:1.2;word-wrap:break-word">${escapeHtmlShare(n)}${first.length > 90 ? '…' : ''}</div>`;
          }
          if (det.repertorio) {
            const rLines = lineasRepertorioEnsayoShare(e);
            if (rLines.length) {
              const head = rLines.length <= 2
                ? rLines.map((l) => escapeHtmlShare(l)).join(' · ')
                : `${rLines.length} obras — ${escapeHtmlShare(rLines[0].slice(0, 36))}…`;
              out += `<div style="font-size:7px;color:#2d3748;margin-top:1px;line-height:1.2;word-wrap:break-word">🎼 ${head}</div>`;
            }
          }
          return out;
        };

        const gridCells = [];
        for (let i = 0; i < primerDia; i++) {
          gridCells.push({ kind: 'empty' });
        }
        for (let d = 1; d <= diasEnMes; d++) {
          const fechaStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          const delDia = itemsDeMes.filter((ev) => {
            if (!ev.fecha) return false;
            return String(ev.fecha).slice(0, 10) === fechaStr;
          });
          const sorted = sortDelDia(delDia);
          gridCells.push({ kind: 'day', d, delDia: sorted, hoy: hoyYmd === fechaStr });
        }
        while (gridCells.length % 7 !== 0) {
          gridCells.push({ kind: 'empty' });
        }

        const tds = gridCells.map((cell) => {
          if (cell.kind === 'empty') {
            return '<td style="width:14.28%;padding:4px;vertical-align:top;border:1px solid #e2e8f0;background:#f7fafc;min-height:80px"></td>';
          }
          const { d, delDia, hoy } = cell;
          const events = delDia.map((e) => eventoCalPdfHtml(e)).join('');
          return `<td style="width:14.28%;padding:4px;vertical-align:top;border:1px solid #e2e8f0;background:${hoy ? '#faf5ff' : '#fff'};min-height:80px">
            <div style="font-size:11px;font-weight:${hoy ? '800' : '500'};color:${hoy ? '#553c9a' : '#718096'}">${d}</div>
            ${events}
          </td>`;
        });

        const weekRows = [];
        for (let r = 0; r < tds.length; r += 7) {
          weekRows.push(`<tr>${tds.slice(r, r + 7).join('')}</tr>`);
        }
        const headers = diasSemana.map(s => `<th style="background:#f7f4ff;color:#6b46c1;font-size:11px;padding:6px;text-align:center">${s}</th>`).join('');
        return `
          <div style="page-break-inside:avoid;margin-bottom:24px">
            <div style="background:linear-gradient(135deg,#553c9a,#6b46c1);color:white;padding:10px 16px;border-radius:10px 10px 0 0">
              <span style="font-weight:800;font-size:16px">${mesLabel}</span>
              <span style="margin-left:10px;background:rgba(255,255,255,0.2);padding:2px 10px;border-radius:20px;font-size:12px">${itemsDeMes.length} evento${itemsDeMes.length !== 1 ? 's' : ''}</span>
            </div>
            <table style="width:100%;table-layout:fixed;border-collapse:collapse;border:1px solid #e2e8f0;border-radius:0 0 10px 10px;overflow:hidden">
              <tr>${headers}</tr>
              ${weekRows.join('')}
            </table>
          </div>`;
      }
    };

    const body = mesesSel.map(buildMesHtml).join('');

    const html = `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>Programación de Ensayos</title>
<style>${PDF_INFORME_STYLES}</style>
</head>
<body>
  <!-- HEADER -->
  <div style="background:linear-gradient(135deg,#553c9a 0%,#6b46c1 50%,#9f7aea 100%);padding:24px 32px;margin-bottom:24px;border-radius:0 0 20px 20px">
    <table style="width:100%;border-collapse:collapse"><tr>
      <td>${IMG_LOGO}</td>
      <td style="text-align:right;color:rgba(255,255,255,0.85);font-size:13px">
        <div style="font-size:18px;font-weight:800;color:white">📋 Programación de Ensayos</div>
        <div style="margin-top:4px">Generado el ${new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
      </td>
    </tr></table>
  </div>

  <!-- LEYENDA -->
  <div style="max-width:900px;margin:0 auto 20px;padding:0 16px;display:flex;gap:20px;flex-wrap:wrap">
    <span style="display:inline-flex;align-items:center;gap:6px;font-size:12px;color:#718096">
      <span style="display:inline-block;width:12px;height:12px;border-radius:50%;background:#6b46c1"></span> Ensayo
    </span>
    <span style="display:inline-flex;align-items:center;gap:6px;font-size:12px;color:#718096">
      <span style="display:inline-block;width:12px;height:12px;border-radius:50%;background:#f6ad55"></span> Actuación
    </span>
  </div>

  <!-- CONTENIDO -->
  <div style="max-width:900px;margin:0 auto;padding:0 16px">${body}</div>

  <!-- FOOTER (misma familia cromática que la cabecera, no gris/negro suelto) -->
  <div style="background:linear-gradient(135deg,#553c9a 0%,#6b46c1 50%,#9f7aea 100%);color:rgba(255,255,255,0.9);text-align:center;padding:16px 32px;font-size:12px;font-weight:500;margin-top:32px;border-radius:20px 20px 0 0;box-shadow:0 -4px 24px rgba(107,70,193,0.15)">
    ${IMG_LOGO} <span style="display:inline-block;margin:0 8px;opacity:0.6">·</span> Gestión musical para bandas de Semana Santa
  </div>

  <div class="no-print" style="text-align:center;padding:20px">
    <button onclick="window.print()" style="background:#6b46c1;color:white;border:none;padding:12px 28px;border-radius:8px;font-size:15px;cursor:pointer;font-weight:700">
      🖨️ Imprimir / Guardar PDF
    </button>
  </div>
</body>
</html>`;

    w.document.write(html);
    w.document.close();
    try { w.focus(); setTimeout(() => w.print(), 800); } catch {}
    setSharePdfGenerando(false);
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (!loading) loadEnsayos(); }, [filtroGrupo]);

  // Handlers
  const handleEditGrupo = (grupo) => {
    if (!requiereMusicosEnsayosFn(musicos)) return;
    setEditingGrupo(grupo);
    onGrupoOpen();
  };
  const handleNewGrupo = () => {
    if (!requiereMusicosEnsayosFn(musicos)) return;
    setEditingGrupo(null);
    onGrupoOpen();
  };
  const handleEditEnsayo = (ensayo) => {
    if (!requiereMusicosEnsayosFn(musicos)) return;
    setEditingEnsayo(ensayo);
    onEnsayoOpen();
  };
  const handleNewEnsayo = () => {
    if (!requiereMusicosEnsayosFn(musicos)) return;
    if (sedes.length === 0) {
      toaster.create({
        title: 'Primero configura una sede',
        description: 'Añade al menos una sede de ensayo antes de crear tu primer ensayo. Pulsa el botón "Sedes" en la cabecera.',
        type: 'info',
        duration: 6000,
        closable: true,
        position: 'top',
      });
      onSedesOpen();
      return;
    }
    setEditingEnsayo(null);
    onEnsayoOpen();
  };
  const handleViewDetail = (ensayo) => {
    if (!requiereMusicosEnsayosFn(musicos)) return;
    setDetalleId(ensayo.id);
    onDetalleOpen();
  };

  const handleDeleteRequest = (tipo, item) => {
    if (tipo === 'ensayo' && item?.fecha && isFechaEventoPasada(item.fecha)) {
      toaster.create({
        title: 'No se pueden eliminar ensayos pasados',
        description: 'El histórico se mantiene para consulta y estadísticas.',
        type: 'info',
        duration: 4000,
      });
      return;
    }
    setDeletingItem({ tipo, item });
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!deletingItem) return;
    if (
      deletingItem.tipo === 'ensayo' &&
      deletingItem.item?.fecha &&
      isFechaEventoPasada(deletingItem.item.fecha)
    ) {
      toaster.create({
        title: 'No se pueden eliminar ensayos pasados',
        type: 'info',
        duration: 3000,
      });
      onDeleteClose();
      setDeletingItem(null);
      return;
    }
    try {
      if (deletingItem.tipo === 'ensayo') {
        await ensayosAPI.remove(deletingItem.item.id);
        toaster.create({ title: 'Ensayo eliminado', type: 'success', duration: 2000 });
      } else {
        await ensayosAPI.deleteGrupo(deletingItem.item.id);
        toaster.create({ title: 'Grupo de ensayos eliminado', type: 'success', duration: 2000 });
      }
      await loadAll();
    } catch {
      toaster.create({ title: 'Error al eliminar', type: 'error', duration: 3000 });
    }
    onDeleteClose();
    setDeletingItem(null);
  };

  const handleSaveVacacion = async () => {
    if (!vacacionForm.fechaInicio || !vacacionForm.fechaFin) {
      toaster.create({ title: 'Indica las fechas de inicio y fin', type: 'warning', duration: 3000 });
      return;
    }
    if (new Date(vacacionForm.fechaFin) < new Date(vacacionForm.fechaInicio)) {
      toaster.create({ title: 'La fecha de fin debe ser igual o posterior a la de inicio', type: 'warning', duration: 3000 });
      return;
    }
    setVacacionSaving(true);
    try {
      if (editingVacacion) {
        await ensayosAPI.updateVacacion(editingVacacion.id, vacacionForm);
        toaster.create({ title: 'Periodo actualizado', type: 'success', duration: 2000 });
      } else {
        await ensayosAPI.createVacacion(vacacionForm);
        toaster.create({ title: 'Periodo de vacaciones añadido', type: 'success', duration: 2000 });
      }
      await loadVacaciones();
      onVacacionClose();
    } catch {
      toaster.create({ title: 'Error al guardar', type: 'error', duration: 3000 });
    } finally {
      setVacacionSaving(false);
    }
  };

  const handleDeleteVacacion = async () => {
    if (!editingVacacion) return;
    setVacacionSaving(true);
    try {
      await ensayosAPI.deleteVacacion(editingVacacion.id);
      toaster.create({ title: 'Periodo eliminado', type: 'success', duration: 2000 });
      await loadVacaciones();
      onVacacionClose();
    } catch {
      toaster.create({ title: 'Error al eliminar', type: 'error', duration: 3000 });
    } finally {
      setVacacionSaving(false);
    }
  };

  // Filtrar ensayos
  const ensayosFiltrados = ensayos.filter(e => {
    if (filtroEstado && e.estado !== filtroEstado) return false;
    if (filtroBusqueda) {
      const q = filtroBusqueda.toLowerCase();
      const grupo = grupos.find(g => g.id === e.grupoId);
      if (!(e.lugar?.toLowerCase().includes(q) || e.descripcion?.toLowerCase().includes(q) || grupo?.nombre?.toLowerCase().includes(q))) return false;
    }
    return true;
  });

  // Agrupar por mes para vista lista (mezclando ensayos + actuaciones)
  const actuacionesNorm = useMemo(
    () => actuaciones
      .filter((a) => a.fecha)
      .map((a) => ({ ...a, _tipo: 'actuacion', fecha: a.fecha })),
    [actuaciones],
  );

  const ensayosListaItems = useMemo(
    () => ensayosFiltrados.map(e => ({ ...e, _tipo: 'ensayo' })),
    [ensayosFiltrados],
  );

  const todosProximos = useMemo(() => {
    const part = [
      ...ensayosListaItems.filter((e) => !isFechaEventoPasada(e.fecha)),
      ...actuacionesNorm.filter((a) => !isFechaEventoPasada(a.fecha)),
    ];
    return part.sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
  }, [ensayosListaItems, actuacionesNorm]);

  const todosPasados = useMemo(() => {
    const part = [
      ...ensayosListaItems.filter((e) => isFechaEventoPasada(e.fecha)),
      ...actuacionesNorm.filter((a) => isFechaEventoPasada(a.fecha)),
    ];
    return part.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  }, [ensayosListaItems, actuacionesNorm]);

  const itemsPorMes = useMemo(() => groupByMonth(todosProximos, 'asc'), [todosProximos]);
  const itemsPorMesPasados = useMemo(() => groupByMonth(todosPasados, 'desc'), [todosPasados]);

  const nEventosProximos = todosProximos.length;
  const nEventosPasados = todosPasados.length;
  const itemsPorMesListado = listaPestana === 'proximos' ? itemsPorMes : itemsPorMesPasados;

  // Helper: returns the vacation period that includes this date, or null
  const getVacacionDesFecha = useCallback((fechaStr) => {
    const d = new Date(fechaStr + 'T12:00:00');
    return vacaciones.find(v => {
      const ini = new Date(v.fechaInicio);
      const fin = new Date(v.fechaFin);
      return d >= ini && d <= fin;
    }) ?? null;
  }, [vacaciones]);

  // Vista calendario
  const ensayosDelMes = ensayosFiltrados.filter(e => isSameMonth(e.fecha, mesVista.year, mesVista.month));
  const actuacionesDelMes = actuacionesNorm.filter(a => isSameMonth(a.fecha, mesVista.year, mesVista.month));
  const diasEnMes = new Date(mesVista.year, mesVista.month + 1, 0).getDate();
  const primerDia = new Date(mesVista.year, mesVista.month, 1).getDay();

  const ensayosBloqueados = !loading && musicos.length === 0;

  return (
    <AppLayout userType="banda">
      <Box minH="calc(100vh - 180px)">
        {/* Top bar */}
        <Box bg={bg} borderBottom="1px" borderColor={borderColor} px={6} py={4} data-tour="ensayos-header">
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
            <HStack gap={3}>
              <Icon as={FaDrum} color="pink.500" boxSize={6} />
              <VStack align="start" gap={0}>
                <Text fontSize="xl" fontWeight="800">Ensayos</Text>
                <Text fontSize="xs" color="gray.500">
                  {ensayosBloqueados
                    ? 'Añade músicos en Mis músicos para habilitar esta sección'
                    : `${ensayos.length} ensayo${ensayos.length !== 1 ? 's' : ''} · ${grupos.length} grupo${grupos.length !== 1 ? 's' : ''}`}
                </Text>
              </VStack>
            </HStack>

            <HStack gap={2}>
              {/* Vista toggle */}
              <HStack gap={0} bg="gray.100" rounded="xl" p={0.5} data-tour="ensayos-vistas">
                {[
                  { v: 'lista', icon: FaThList, label: 'Lista' },
                  { v: 'calendario', icon: FaCalendarAlt, label: 'Calendario' },
                  { v: 'estadisticas', icon: FaChartBar, label: 'Estadísticas' },
                ].map(({ v, icon, label }) => (
                  <Tooltip key={v} content={ensayosBloqueados ? 'Disponible cuando haya músicos en Mis músicos' : label} showArrow>
                    <IconButton
                      size="sm"
                      colorPalette={vistaActiva === v ? 'purple' : 'gray'}
                      variant={vistaActiva === v ? 'solid' : 'ghost'}
                      disabled={ensayosBloqueados}
                      onClick={() => {
                        setVistaActiva(v);
                        if (v === 'estadisticas') loadEstadisticas({ grupoId: statsFiltroGrupo, desde: statsDesde, hasta: statsHasta });
                      }}
                      aria-label={label}
                      rounded="lg"><Icon as={icon} /></IconButton>
                  </Tooltip>
                ))}
              </HStack>

              {(() => {
                const sedePred = sedes.find(s => s.predeterminada);
                const tooltipLabel = ensayosBloqueados
                  ? 'Añade músicos en Mis músicos primero'
                  : sedePred
                  ? `Sede predeterminada: ${sedePred.nombre}`
                  : sedes.length > 0
                    ? 'Gestionar sedes de ensayo (sin predeterminada)'
                    : 'Gestionar sedes de ensayo';
                return (
                  <Tooltip content={tooltipLabel} showArrow>
                    <Box
                      as="button"
                      type="button"
                      disabled={ensayosBloqueados}
                      aria-disabled={ensayosBloqueados}
                      onClick={() => { if (!ensayosBloqueados) onSedesOpen(); }}
                      data-tour="ensayos-sedes"
                      px={3}
                      py={1.5}
                      rounded="xl"
                      border="1.5px solid"
                      borderColor={sedePred ? 'teal.400' : sedes.length > 0 ? 'orange.300' : 'teal.300'}
                      bg={sedePred ? 'teal.50' : sedes.length > 0 ? 'orange.50' : 'teal.50'}
                      _hover={ensayosBloqueados ? {} : { bg: sedePred ? 'teal.100' : sedes.length > 0 ? 'orange.100' : 'teal.100' }}
                      _active={ensayosBloqueados ? {} : { bg: sedePred ? 'teal.200' : 'orange.200' }}
                      opacity={ensayosBloqueados ? 0.55 : 1}
                      cursor={ensayosBloqueados ? 'not-allowed' : 'pointer'}
                      transition="all 0.15s"
                      display="flex"
                      alignItems="center"
                      gap={2}
                      minW="110px"
                      position="relative"
                    >
                      <Icon as={FaBuilding} color={sedePred ? 'teal.600' : sedes.length > 0 ? 'orange.500' : 'teal.600'} boxSize={5} />
                      <Box flex={1} textAlign="left">
                        <Text fontSize="sm" fontWeight="600" color={sedePred ? 'teal.700' : sedes.length > 0 ? 'orange.700' : 'teal.700'} lineHeight="short">
                          Sedes
                          {sedes.length > 0 && (
                            <Text as="span" fontSize="xs" fontWeight="400" ml={1} color={sedePred ? 'teal.500' : 'orange.500'}>
                              ({sedes.length})
                            </Text>
                          )}
                        </Text>
                        {sedePred && (
                          <Text fontSize="2xs" color="teal.500" fontWeight="500" lineClamp={1} maxW="120px">
                            ✓ {sedePred.nombre}
                          </Text>
                        )}
                        {!sedePred && sedes.length > 0 && (
                          <Text fontSize="2xs" color="orange.500" fontWeight="500">
                            Sin predeterminada
                          </Text>
                        )}
                      </Box>
                      <Flex
                        align="center"
                        justify="center"
                        w={5}
                        h={5}
                        rounded="full"
                        bg={sedePred ? 'teal.500' : sedes.length > 0 ? 'orange.400' : 'teal.500'}
                        flexShrink={0}
                      >
                        <Icon as={FaPlus} color="white" boxSize={2.5} />
                      </Flex>
                    </Box>
                  </Tooltip>
                );
              })()}

              <Tooltip content={ensayosBloqueados ? 'Añade músicos en Mis músicos primero' : 'Compartir / Generar informe'} showArrow>
                <IconButton
                  size="sm"
                  rounded="xl"
                  colorPalette="green"
                  variant="outline"
                  aria-label="Compartir"
                  disabled={ensayosBloqueados}
                  onClick={openShareModal}
                  loading={sharePdfGenerando}><Icon as={FaShareAlt} /></IconButton>
              </Tooltip>

              <Button
                colorPalette="purple"
                size="sm"
                rounded="xl"
                disabled={ensayosBloqueados}
                onClick={handleNewEnsayo}
                data-tour="ensayos-nuevo"><Icon as={FaPlus} />Nuevo ensayo
                              </Button>
            </HStack>
          </Flex>
        </Box>

        {ensayosBloqueados && (
          <Box px={{ base: 4, md: 6 }} py={3} bg={subtleBg} borderBottom="1px" borderColor={borderColor}>
            <Alert.Root
              status="warning"
              variant='subtle'
              borderRadius="lg"
              borderStartWidth='3px'
              borderStartColor='colorPalette.solid'>
              <Alert.Indicator />
              <Box>
                <Alert.Title fontSize="sm">Esta sección está bloqueada hasta tener músicos</Alert.Title>
                <Alert.Description fontSize="sm" display="block">
                  <Text mb={3}>
                    En <strong>Mis músicos</strong> aún no hay ningún integrante registrado. Añade antes al menos un
                    músico al censo de la banda; después podrás crear grupos de ensayos, programar ensayos, sedes, vacaciones y el
                    resto de acciones de esta pantalla.
                  </Text>
                  <Button size="sm" colorPalette="orange" onClick={() => navigate('/banda/musicos')}>
                    Ir a Mis músicos
                  </Button>
                </Alert.Description>
              </Box>
            </Alert.Root>
          </Box>
        )}

        <Flex
          opacity={ensayosBloqueados ? 0.5 : 1}
          pointerEvents={ensayosBloqueados ? 'none' : 'auto'}
          transition="opacity 0.2s ease"
        >
          {/* Sidebar */}
          <Box
            w={{ base: 'full', md: '260px' }}
            flexShrink={0}
            display={{ base: 'none', md: 'block' }}
            borderRight="1px"
            borderColor={borderColor}
            bg={bg}
            minH="calc(100vh - 240px)"
            p={4}
          >
            {/* Grupos de ensayos */}
            <Flex justify="space-between" align="center" mb={3} data-tour="ensayos-grupos">
              <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider">
                Grupos de ensayos
              </Text>
              <IconButton
                size="xs"
                variant="ghost"
                colorPalette="purple"
                aria-label="Nuevo grupo de ensayos"
                onClick={handleNewGrupo}><FaPlus /></IconButton>
            </Flex>

            {grupos.length === 0 ? (
              <Box
                py={4}
                textAlign="center"
                color="gray.400"
                border="1px dashed"
                borderColor="gray.200"
                rounded="xl"
                mb={4}
              >
                <Icon as={FaLayerGroup} boxSize={5} mb={2} />
                <Text fontSize="xs">Sin grupos de ensayos</Text>
                <Button size="xs" colorPalette="purple" variant="ghost" mt={1} onClick={handleNewGrupo}>
                  Crear grupos de ensayos
                </Button>
              </Box>
            ) : (
              <VStack gap={1.5} align="stretch" mb={4}>
                <Button
                  size="sm"
                  variant={!filtroGrupo ? 'solid' : 'ghost'}
                  colorPalette={!filtroGrupo ? 'purple' : 'gray'}
                  justifyContent="flex-start"
                  rounded="lg"
                  onClick={() => setFiltroGrupo(null)}
                  fontWeight={!filtroGrupo ? '700' : '500'}
                >
                  Todos los ensayos
                </Button>
                {grupos.map(g => (
                  <GrupoCard
                    key={g.id}
                    grupo={g}
                    isFiltered={filtroGrupo === g.id}
                    onFilter={grupo => setFiltroGrupo(filtroGrupo === grupo.id ? null : grupo.id)}
                    onEdit={handleEditGrupo}
                    onDelete={grupo => handleDeleteRequest('grupo', grupo)}
                    onVincularEnsayos={grupo => {
                      setVincularGrupoTarget(grupo);
                      onVincularGrupoOpen();
                    }}
                  />
                ))}
              </VStack>
            )}

            <Separator mb={3} />

            {/* Filtros */}
            <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
              Filtros
            </Text>

            <VStack gap={2} align="stretch">
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <Icon as={FaSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Buscar..."
                  value={filtroBusqueda}
                  onChange={e => setFiltroBusqueda(e.target.value)}
                  rounded="xl"
                />
              </InputGroup>

              <NativeSelect.Root>
                <NativeSelect.Field
                  size="sm"
                  value={filtroEstado}
                  onChange={e => setFiltroEstado(e.target.value)}
                  rounded="xl"
                  placeholder="Todos los estados">
                  {Object.entries(ESTADO_CONFIG).map(([key, cfg]) => (
                    <option key={key} value={key}>{cfg.label}</option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </VStack>

            {(filtroGrupo || filtroEstado || filtroBusqueda) && (
              <Button
                size="xs"
                variant="ghost"
                colorPalette="red"
                mt={3}
                onClick={() => { setFiltroGrupo(null); setFiltroEstado(''); setFiltroBusqueda(''); }}><Icon as={FaTimes} />Limpiar filtros
                              </Button>
            )}

            <Separator mt={4} mb={3} />

            {/* Vacaciones */}
            <Flex justify="space-between" align="center" mb={2} data-tour="ensayos-vacaciones">
              <HStack gap={1}>
                <Icon as={FaSun} color="orange.400" boxSize={3} />
                <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider">
                  Vacaciones
                </Text>
              </HStack>
              <IconButton
                size="xs"
                variant="ghost"
                colorPalette="orange"
                aria-label="Añadir periodo de vacaciones"
                onClick={() => {
                  setEditingVacacion(null);
                  const hoy = new Date().toISOString().slice(0, 10);
                  setVacacionForm({ fechaInicio: hoy, fechaFin: hoy, motivo: '', color: '#F6AD55' });
                  onVacacionOpen();
                }}><FaPlus /></IconButton>
            </Flex>

            {vacaciones.length === 0 ? (
              <Text fontSize="xs" color="gray.400" textAlign="center" py={2}>
                Sin periodos marcados
              </Text>
            ) : (
              <VStack gap={1.5} align="stretch">
                {vacaciones.map(v => {
                  const ini = new Date(v.fechaInicio).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
                  const fin = new Date(v.fechaFin).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
                  return (
                    <Box
                      key={v.id}
                      p={2}
                      rounded="lg"
                      borderLeft="3px solid"
                      borderLeftColor={v.color || 'orange.300'}
                      bg="orange.50"
                      cursor="pointer"
                      _hover={{ bg: 'orange.100' }}
                      onClick={() => {
                        setEditingVacacion(v);
                        setVacacionForm({
                          fechaInicio: v.fechaInicio.slice(0, 10),
                          fechaFin: v.fechaFin.slice(0, 10),
                          motivo: v.motivo || '',
                          color: v.color || '#F6AD55',
                        });
                        onVacacionOpen();
                      }}
                    >
                      <Text fontSize="xs" fontWeight="600" color="orange.700" lineClamp={1}>
                        {v.motivo || 'Vacaciones'}
                      </Text>
                      <Text fontSize="2xs" color="orange.500">
                        {ini} – {fin}
                      </Text>
                    </Box>
                  );
                })}
              </VStack>
            )}
          </Box>

          {/* Contenido principal */}
          <Box flex={1} p={{ base: 4, md: 6 }}>
            {loading ? (
              <Flex justify="center" align="center" h="300px">
                <VStack gap={3}>
                  <Spinner size="xl" color="purple.500" borderWidth="3px" />
                  <Text color="gray.500">Cargando ensayos...</Text>
                </VStack>
              </Flex>
            ) : vistaActiva === 'lista' ? (
              /* ─── Vista Lista ─────────────────────────────── */
              ensayosFiltrados.length === 0 && actuacionesNorm.length === 0 ? (
                <Flex justify="center" align="center" h="300px" direction="column" gap={3}>
                  <Icon as={FaDrum} boxSize={14} color="gray.200" />
                  <Text color="gray.500" fontSize="lg" fontWeight="600">
                    {ensayos.length === 0 ? 'Aún no hay ensayos' : 'No hay ensayos con estos filtros'}
                  </Text>
                  {ensayos.length === 0 && (
                    <Button colorPalette="purple" onClick={handleNewEnsayo}><Icon as={FaPlus} />Crear primer ensayo
                                          </Button>
                  )}
                </Flex>
              ) : (
                <VStack gap={6} align="stretch">
                  <HStack
                    flexWrap="wrap"
                    gap={2}
                    bg="gray.100"
                    _dark={{ bg: 'whiteAlpha.100' }}
                    p={1}
                    rounded="xl"
                  >
                    <Button
                      size="sm"
                      variant={listaPestana === 'proximos' ? 'solid' : 'ghost'}
                      colorPalette="purple"
                      onClick={() => setListaPestana('proximos')}
                      flex={{ base: '1 1 100%', sm: '0 0 auto' }}><Icon as={FaCalendarAlt} />
                        Próximos
                        {nEventosProximos > 0 && (
                      <Badge ml={2} colorPalette="purple" variant={listaPestana === 'proximos' ? 'solid' : 'subtle'}>
                        {nEventosProximos}
                      </Badge>
                    )}</Button>
                    <Button
                      size="sm"
                      variant={listaPestana === 'pasados' ? 'solid' : 'ghost'}
                      colorPalette="gray"
                      onClick={() => setListaPestana('pasados')}
                      flex={{ base: '1 1 100%', sm: '0 0 auto' }}><Icon as={FaHistory} />
                        Histórico
                        {nEventosPasados > 0 && (
                      <Badge ml={2} colorPalette="gray" variant={listaPestana === 'pasados' ? 'solid' : 'subtle'}>
                        {nEventosPasados}
                      </Badge>
                    )}</Button>
                  </HStack>

                  {itemsPorMesListado.length === 0 ? (
                    <Flex justify="center" align="center" minH="200px" direction="column" gap={2} px={4}>
                      <Icon as={FaDrum} boxSize={10} color="gray.200" />
                      <Text color="gray.500" textAlign="center" fontSize="sm">
                        {listaPestana === 'proximos'
                          ? (nEventosPasados > 0
                            ? 'No hay ensayos ni actuaciones futuras. Revisa el histórico para fechas anteriores.'
                            : 'No hay eventos próximos con los filtros actuales.')
                          : (nEventosProximos > 0
                            ? 'No hay eventos en el histórico con los filtros actuales. Los próximos siguen en la otra pestaña.'
                            : 'No hay eventos pasados (ensayos ni actuaciones) con los filtros actuales.')}
                      </Text>
                    </Flex>
                  ) : (
                <VStack gap={6} align="stretch">
                  {itemsPorMesListado.map(({ year, month, items }) => {
                    const ensayosCount = items.filter(i => i._tipo === 'ensayo').length;
                    const actCount = items.filter(i => i._tipo === 'actuacion').length;
                    // Vacation periods that overlap this month
                    const mesIni = new Date(year, month, 1);
                    const mesFin = new Date(year, month + 1, 0);
                    const vacsMes = vacaciones.filter(v => {
                      const vi = new Date(v.fechaInicio);
                      const vf = new Date(v.fechaFin);
                      return vi <= mesFin && vf >= mesIni;
                    });
                    return (
                      <Box key={`${year}-${month}`}>
                        <HStack gap={2} mb={3} flexWrap="wrap">
                          <Text fontSize="sm" fontWeight="700" color="purple.600">
                            {MESES[month]} {year}
                          </Text>
                          {ensayosCount > 0 && <Badge colorPalette="purple" variant="subtle">{ensayosCount} ensayo{ensayosCount !== 1 ? 's' : ''}</Badge>}
                          {actCount > 0 && <Badge colorPalette="orange" variant="subtle">{actCount} actuación{actCount !== 1 ? 'es' : ''}</Badge>}
                          {vacsMes.length > 0 && <Badge colorPalette="yellow" variant="subtle">☀️ {vacsMes.length} periodo{vacsMes.length !== 1 ? 's' : ''} vacaciones</Badge>}
                        </HStack>
                        {/* Vacation banners for this month */}
                        {vacsMes.map(v => {
                          const ini = new Date(v.fechaInicio).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
                          const fin = new Date(v.fechaFin).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
                          return (
                            <Box
                              key={`vac-list-${v.id}`}
                              mb={2}
                              p={3}
                              rounded="xl"
                              bg="orange.50"
                              borderLeft="4px solid"
                              borderLeftColor={v.color || 'orange.300'}
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <HStack gap={2}>
                                <Text fontSize="lg">☀️</Text>
                                <Box>
                                  <Text fontSize="sm" fontWeight="700" color="orange.700">
                                    {v.motivo || 'Vacaciones / descanso'}
                                  </Text>
                                  <Text fontSize="xs" color="orange.500">{ini} – {fin}</Text>
                                </Box>
                              </HStack>
                              <IconButton
                                size="xs"
                                variant="ghost"
                                colorPalette="orange"
                                aria-label="Editar vacaciones"
                                onClick={() => {
                                  setEditingVacacion(v);
                                  setVacacionForm({ fechaInicio: v.fechaInicio.slice(0, 10), fechaFin: v.fechaFin.slice(0, 10), motivo: v.motivo || '', color: v.color || '#F6AD55' });
                                  onVacacionOpen();
                                }}><Icon as={FaEdit} /></IconButton>
                            </Box>
                          );
                        })}
                        <VStack gap={2} align="stretch">
                          {items.map(item =>
                            item._tipo === 'actuacion' ? (
                              <ActuacionSombreada key={`act-${item.id}`} actuacion={item} />
                            ) : (
                              <EnsayoCard
                                key={item.id}
                                ensayo={item}
                                grupos={grupos}
                                onEdit={handleEditEnsayo}
                                onDelete={e => handleDeleteRequest('ensayo', e)}
                                onViewDetail={handleViewDetail}
                                onShare={openEnsayoShare}
                              />
                            )
                          )}
                        </VStack>
                      </Box>
                    );
                  })}
                </VStack>
                  )}
                </VStack>
              )
            ) : (
              /* ─── Vista Calendario ────────────────────────── */
              <Box>
                {/* Leyenda */}
                <HStack gap={4} mb={3} flexWrap="wrap">
                  <HStack gap={1}>
                    <Box w={3} h={3} rounded="sm" bg="purple.500" />
                    <Text fontSize="xs" color="gray.500">Ensayos</Text>
                  </HStack>
                  <HStack gap={1}>
                    <Box w={3} h={3} rounded="sm" bg="orange.300" border="1px dashed" borderColor="orange.400" />
                    <Text fontSize="xs" color="gray.500">Actuaciones programadas</Text>
                  </HStack>
                  <HStack gap={1}>
                    <Box w={3} h={3} rounded="sm" bg="yellow.300" opacity={0.7} />
                    <Text fontSize="xs" color="gray.500">Vacaciones / descanso</Text>
                  </HStack>
                </HStack>
                {/* Navegación de mes */}
                <Flex align="center" justify="space-between" mb={4}>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    onClick={() => setMesVista(mv => {
                      const d = new Date(mv.year, mv.month - 1);
                      return { year: d.getFullYear(), month: d.getMonth() };
                    })}
                    aria-label="Mes anterior"><FaChevronLeft /></IconButton>
                  <Text fontWeight="700" fontSize="lg">
                    {MESES[mesVista.month]} {mesVista.year}
                  </Text>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    onClick={() => setMesVista(mv => {
                      const d = new Date(mv.year, mv.month + 1);
                      return { year: d.getFullYear(), month: d.getMonth() };
                    })}
                    aria-label="Mes siguiente"><FaChevronRight /></IconButton>
                </Flex>

                {/* Cabeceras días */}
                <SimpleGrid columns={7} mb={1}>
                  {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(d => (
                    <Box key={d} textAlign="center" py={1}>
                      <Text fontSize="xs" fontWeight="700" color="gray.400">{d}</Text>
                    </Box>
                  ))}
                </SimpleGrid>

                {/* Grid del mes */}
                <SimpleGrid columns={7} gap={1}>
                  {/* Celdas vacías hasta el primer día */}
                  {Array.from({ length: primerDia }).map((_, i) => (
                    <Box key={`empty-${i}`} minH="80px" />
                  ))}
                  {/* Días del mes */}
                  {Array.from({ length: diasEnMes }).map((_, i) => {
                    const dia = i + 1;
                    const fecha = `${mesVista.year}-${String(mesVista.month + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
                    const ensayosDelDia = ensayosDelMes.filter(e => e.fecha.slice(0, 10) === fecha);
                    const actDelDia = actuacionesDelMes.filter(a => a.fecha.slice(0, 10) === fecha);
                    const isToday = new Date().toISOString().slice(0, 10) === fecha;
                    const tieneAct = actDelDia.length > 0;
                    const vacacion = getVacacionDesFecha(fecha);
                    const bgCell = isToday ? 'purple.50' : vacacion ? `${vacacion.color}22` : tieneAct ? 'orange.50' : bg;
                    const borderCell = isToday ? 'purple.200' : vacacion ? vacacion.color : tieneAct ? 'orange.200' : borderColor;
                    return (
                      <Box
                        key={dia}
                        minH="80px"
                        bg={bgCell}
                        border="1px"
                        borderColor={borderCell}
                        rounded="lg"
                        p={1}
                        overflow="hidden"
                        position="relative"
                      >
                        <Flex justify="space-between" align="center" mb={0.5}>
                          <Text
                            fontSize="xs"
                            fontWeight={isToday ? '800' : '500'}
                            color={isToday ? 'purple.600' : vacacion ? 'orange.700' : 'gray.500'}
                          >
                            {dia}
                          </Text>
                        </Flex>
                        {vacacion && (
                          <Box
                            fontSize="2xs"
                            fontWeight="600"
                            color="orange.700"
                            bg={`${vacacion.color}44`}
                            borderLeft="2px solid"
                            borderLeftColor={vacacion.color || '#F6AD55'}
                            px={1}
                            py={0.5}
                            rounded="sm"
                            lineClamp={1}
                            mb={0.5}
                            title={vacacion.motivo || 'Vacaciones'}
                          >
                            ☀️ {vacacion.motivo || 'Vacaciones'}
                          </Box>
                        )}
                        <VStack gap={0.5} align="stretch">
                          {/* Actuaciones primero (sombreadas) */}
                          {actDelDia.map(a => (
                            <Box
                              key={`cal-act-${a.id}`}
                              bg="orange.100"
                              color="orange.800"
                              fontSize="2xs"
                              px={1}
                              py={0.5}
                              rounded="sm"
                              lineClamp={1}
                              borderLeft="3px solid"
                              borderLeftColor="orange.400"
                              opacity={0.85}
                              title={a.nombre}
                            >
                              🎺 {a.horaInicio ? `${a.horaInicio} ` : ''}{a.nombre}
                            </Box>
                          ))}
                          {/* Ensayos */}
                          {ensayosDelDia.map(e => {
                            const grupo = grupos.find(g => g.id === e.grupoId);
                            return (
                              <Flex
                                key={e.id}
                                align="center"
                                gap={0.5}
                                bg={grupo?.color ?? '#6B46C1'}
                                color="white"
                                fontSize="2xs"
                                px={0.5}
                                py={0.5}
                                rounded="sm"
                                _hover={{ opacity: 0.92 }}
                              >
                                <Box
                                  flex={1}
                                  minW={0}
                                  lineClamp={1}
                                  cursor="pointer"
                                  onClick={() => handleViewDetail(e)}
                                  pl={0.5}
                                >
                                  {e.horaInicio} {grupo?.nombre ?? 'Ensayo'}
                                </Box>
                                <IconButton
                                  size="xs"
                                  variant="ghost"
                                  color="white"
                                  aria-label="Compartir ensayo"
                                  minW="22px"
                                  h="22px"
                                  flexShrink={0}
                                  onClick={(ev) => { ev.stopPropagation(); openEnsayoShare(e); }}><FaShareAlt /></IconButton>
                              </Flex>
                            );
                          })}
                        </VStack>
                      </Box>
                    );
                  })}
                </SimpleGrid>

                {/* Lista del mes */}
                {(ensayosDelMes.length > 0 || actuacionesDelMes.length > 0) && (
                  <Box mt={6}>
                    <HStack gap={2} mb={3}>
                      <Text fontWeight="700" color="gray.600">
                        {MESES[mesVista.month]}
                      </Text>
                      {ensayosDelMes.length > 0 && <Badge colorPalette="purple" variant="subtle">{ensayosDelMes.length} ensayo{ensayosDelMes.length !== 1 ? 's' : ''}</Badge>}
                      {actuacionesDelMes.length > 0 && <Badge colorPalette="orange" variant="subtle">{actuacionesDelMes.length} actuación{actuacionesDelMes.length !== 1 ? 'es' : ''}</Badge>}
                    </HStack>
                    <VStack gap={2} align="stretch">
                      {[
                        ...ensayosDelMes.map(e => ({ ...e, _tipo: 'ensayo' })),
                        ...actuacionesDelMes,
                      ].sort((a, b) => new Date(a.fecha) - new Date(b.fecha)).map(item =>
                        item._tipo === 'actuacion' ? (
                          <ActuacionSombreada key={`list-act-${item.id}`} actuacion={item} />
                        ) : (
                          <EnsayoCard
                            key={item.id}
                            ensayo={item}
                            grupos={grupos}
                            onEdit={handleEditEnsayo}
                            onDelete={e => handleDeleteRequest('ensayo', e)}
                            onViewDetail={handleViewDetail}
                            onShare={openEnsayoShare}
                          />
                        )
                      )}
                    </VStack>
                  </Box>
                )}
              </Box>
            )}

            {/* ─── Vista Estadísticas ──────────────────────── */}
            {vistaActiva === 'estadisticas' && (() => {
              const getColor = (pct) => {
                if (pct === null) return 'gray';
                if (pct >= 80) return 'green';
                if (pct >= 50) return 'yellow';
                return 'red';
              };

              const statsFiltradas = (statsData?.stats ?? []).filter(m => {
                if (!statsBusqueda) return true;
                return `${m.nombre} ${m.apellidos ?? ''}`.toLowerCase().includes(statsBusqueda.toLowerCase());
              }).sort((a, b) => {
                if (statsOrden === 'nombre') return `${a.nombre} ${a.apellidos ?? ''}`.localeCompare(`${b.nombre} ${b.apellidos ?? ''}`);
                if (statsOrden === 'ausencias') return (b.ausente ?? 0) - (a.ausente ?? 0);
                // porcentaje: worst first, nulls last
                if (a.porcentaje === null && b.porcentaje === null) return 0;
                if (a.porcentaje === null) return 1;
                if (b.porcentaje === null) return -1;
                return a.porcentaje - b.porcentaje;
              });

              return (
                <VStack gap={4} align="stretch">
                  {/* Filtros de estadísticas */}
                  <Box p={4} bg={bg} rounded="xl" shadow="sm" borderWidth="1px" borderColor={borderColor}>
                    <SimpleGrid columns={{ base: 1, md: 4 }} gap={3}>
                      <Box>
                        <Text fontSize="xs" color="gray.500" mb={1}>Grupo de ensayos</Text>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            size="sm"
                            value={statsFiltroGrupo}
                            onChange={e => setStatsFiltroGrupo(e.target.value)}
                            placeholder="Todos los grupos de ensayos">
                            {grupos.map(g => <option key={g.id} value={g.id}>{g.nombre}</option>)}
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500" mb={1}>Desde</Text>
                        <Input size="sm" type="date" value={statsDesde} onChange={e => setStatsDesde(e.target.value)} />
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500" mb={1}>Hasta</Text>
                        <Input size="sm" type="date" value={statsHasta} onChange={e => setStatsHasta(e.target.value)} />
                      </Box>
                      <Flex align="flex-end">
                        <Button
                          size="sm"
                          colorPalette="purple"
                          w="full"
                          onClick={() => loadEstadisticas({ grupoId: statsFiltroGrupo, desde: statsDesde, hasta: statsHasta })}
                          loading={statsLoading}
                        >
                          Actualizar
                        </Button>
                      </Flex>
                    </SimpleGrid>
                  </Box>

                  {statsLoading ? (
                    <Flex justify="center" align="center" h="200px"><Spinner size="xl" color="purple.500" /></Flex>
                  ) : !statsData ? (
                    <Box py={10} textAlign="center" color="gray.400">
                      <Icon as={FaChartBar} boxSize={12} mb={3} />
                      <Text>Selecciona los filtros y pulsa "Actualizar"</Text>
                    </Box>
                  ) : (
                    <>
                      {/* Summary cards */}
                      <SimpleGrid columns={{ base: 2, md: 4 }} gap={3}>
                        {[
                          { label: 'Ensayos realizados', value: statsData.totalEnsayos, color: 'purple', icon: FaDrum },
                          { label: 'Músicos con datos', value: statsData.totalMusicos, color: 'blue', icon: FaUsers },
                          {
                            label: 'Media asistencia',
                            value: statsData.mediaAsistencia != null ? `${statsData.mediaAsistencia}%` : '—',
                            color: statsData.mediaAsistencia != null
                              ? (statsData.mediaAsistencia >= 80 ? 'green' : statsData.mediaAsistencia >= 50 ? 'yellow' : 'red')
                              : 'gray',
                            icon: FaChartBar,
                          },
                          {
                            label: 'Baja asistencia (<50%)',
                            value: statsData.stats.filter(s => s.porcentaje !== null && s.porcentaje < 50).length,
                            color: 'red',
                            icon: FaExclamationTriangle,
                          },
                        ].map(({ label, value, color, icon }) => (
                          <Box key={label} p={4} bg={`${color}.50`} rounded="xl" borderWidth="1px" borderColor={`${color}.100`}>
                            <HStack gap={2} mb={1}>
                              <Icon as={icon} color={`${color}.500`} boxSize={4} />
                              <Text fontSize="xs" color={`${color}.600`} fontWeight="600">{label}</Text>
                            </HStack>
                            <Text fontSize="2xl" fontWeight="800" color={`${color}.700`}>{value}</Text>
                          </Box>
                        ))}
                      </SimpleGrid>

                      {/* Buscar + ordenar */}
                      <HStack gap={3}>
                        <InputGroup size="sm" flex={1}>
                          <InputLeftElement><Icon as={FaSearch} color="gray.400" /></InputLeftElement>
                          <Input
                            pl={8}
                            placeholder="Buscar músico..."
                            value={statsBusqueda}
                            onChange={e => setStatsBusqueda(e.target.value)}
                            rounded="xl"
                          />
                        </InputGroup>
                        <NativeSelect.Root>
                          <NativeSelect.Field
                            size="sm"
                            value={statsOrden}
                            onChange={e => setStatsOrden(e.target.value)}
                            w="200px"
                            rounded="xl">
                            <option value="porcentaje">Ordenar: peor asistencia</option>
                            <option value="nombre">Ordenar: A-Z</option>
                            <option value="ausencias">Ordenar: más ausencias</option>
                          </NativeSelect.Field>
                          <NativeSelect.Indicator />
                        </NativeSelect.Root>
                      </HStack>

                      {/* Tabla de músicos */}
                      {statsFiltradas.length === 0 ? (
                        <Box py={8} textAlign="center" color="gray.400">
                          <Text>No hay datos de músicos con esos filtros</Text>
                        </Box>
                      ) : (
                        <VStack gap={2} align="stretch">
                          {statsFiltradas.map((m, idx) => {
                            const pct = m.porcentaje;
                            const color = getColor(pct);
                            const instrPrincipal = m.instrumentosAsignados?.[0];
                            const instrNombre = instrPrincipal
                              ? `${instrPrincipal.instrumento?.nombre ?? ''}${instrPrincipal.vozPartituraPersonalizada?.nombre ? ` ${instrPrincipal.vozPartituraPersonalizada.nombre}` : ''}`
                              : null;

                            return (
                              <Box
                                key={m.id}
                                p={4}
                                bg={bg}
                                rounded="xl"
                                borderWidth="1px"
                                borderColor={pct !== null && pct < 50 ? 'red.200' : borderColor}
                                shadow="sm"
                              >
                                <Flex gap={4} align="center" wrap="wrap">
                                  {/* Posición + músico */}
                                  <HStack gap={3} flex={1} minW="180px">
                                    <Text fontSize="sm" color="gray.400" w="24px" textAlign="right" flexShrink={0}>
                                      {idx + 1}.
                                    </Text>
                                    <Avatar.Root size="sm"><Avatar.Fallback name={`${m.nombre} ${m.apellidos ?? ''}`} /><Avatar.Image src={m.foto} /></Avatar.Root>
                                    <Box minW={0}>
                                      <Text fontSize="sm" fontWeight="700" lineClamp={1}>
                                        {m.nombre} {m.apellidos ?? ''}
                                      </Text>
                                      {instrNombre && (
                                        <Text fontSize="xs" color="gray.500" lineClamp={1}>{instrNombre}</Text>
                                      )}
                                    </Box>
                                  </HStack>

                                  {/* Contadores */}
                                  <SimpleGrid columns={4} gap={2} minW="240px">
                                    {[
                                      { label: 'Presente', val: m.presente, color: 'green' },
                                      { label: 'Ausente', val: m.ausente, color: 'red' },
                                      { label: 'Justif.', val: m.justificado, color: 'orange' },
                                      { label: 'Pendiente', val: m.pendiente, color: 'gray' },
                                    ].map(({ label, val, color: c }) => (
                                      <Box key={label} textAlign="center">
                                        <Text fontSize="lg" fontWeight="800" color={`${c}.500`}>{val ?? 0}</Text>
                                        <Text fontSize="2xs" color="gray.500">{label}</Text>
                                      </Box>
                                    ))}
                                  </SimpleGrid>

                                  {/* Barra de porcentaje */}
                                  <Box minW="140px" flex={1}>
                                    <Flex justify="space-between" mb={1}>
                                      <Text fontSize="xs" color="gray.500">Asistencia</Text>
                                      <Text fontSize="sm" fontWeight="800" color={`${color}.600`}>
                                        {pct !== null ? `${pct}%` : '—'}
                                      </Text>
                                    </Flex>
                                    <Box bg="gray.100" rounded="full" h="8px" overflow="hidden">
                                      <Box
                                        bg={pct !== null ? `${color}.400` : 'gray.200'}
                                        h="full"
                                        w={pct !== null ? `${pct}%` : '0%'}
                                        rounded="full"
                                        transition="width 0.4s"
                                      />
                                    </Box>
                                    <Text fontSize="2xs" color="gray.400" mt={0.5}>
                                      {m.total} ensayo{m.total !== 1 ? 's' : ''} convocado
                                      {m.obligatoriosFaltados > 0 && (
                                        <Text as="span" color="red.500" fontWeight="600">
                                          {' '}· {m.obligatoriosFaltados} obligatorio{m.obligatoriosFaltados !== 1 ? 's' : ''} faltado
                                        </Text>
                                      )}
                                    </Text>
                                  </Box>

                                  {/* Badge de alerta */}
                                  {pct !== null && pct < 50 && (
                                    <Tooltip content="Asistencia inferior al 50%" showArrow>
                                      <Badge colorPalette="red" variant="solid" fontSize="xs" px={2} py={1} rounded="lg">
                                        <Icon as={FaExclamationTriangle} mr={1} />
                                        Baja
                                      </Badge>
                                    </Tooltip>
                                  )}
                                  {pct !== null && pct === 100 && (
                                    <Tooltip content="Asistencia perfecta" showArrow>
                                      <Badge colorPalette="green" variant="solid" fontSize="xs" px={2} py={1} rounded="lg">
                                        <Icon as={FaTrophy} mr={1} />
                                        100%
                                      </Badge>
                                    </Tooltip>
                                  )}
                                </Flex>
                              </Box>
                            );
                          })}
                        </VStack>
                      )}
                    </>
                  )}
                </VStack>
              );
            })()}
          </Box>
        </Flex>
      </Box>

      {/* Modals */}
      <SedesModal
        isOpen={isSedesOpen}
        onClose={onSedesClose}
        sedes={sedes}
        onSaved={loadSedes}
      />

      <GrupoModal
        isOpen={isGrupoOpen}
        onClose={onGrupoClose}
        grupo={editingGrupo}
        sedes={sedes}
        onSaved={loadAll}
        onVincularEnsayosRequest={(g) => {
          setVincularGrupoTarget(g);
          onVincularGrupoOpen();
        }}
      />

      <VincularEnsayosGrupoModal
        isOpen={isVincularGrupoOpen}
        onClose={() => {
          onVincularGrupoClose();
          setVincularGrupoTarget(null);
        }}
        grupo={vincularGrupoTarget}
        grupos={grupos}
        onSuccess={loadAll}
      />

      <EnsayoModal
        isOpen={isEnsayoOpen}
        onClose={onEnsayoClose}
        ensayo={editingEnsayo}
        grupos={grupos}
        musicos={musicos}
        sedes={sedes}
        actuaciones={actuaciones}
        onSaved={loadAll}
      />

      <EnsayoDetalleModal
        isOpen={isDetalleOpen}
        onClose={onDetalleClose}
        ensayoId={detalleId}
        grupos={grupos}
        musicos={musicos}
        actuaciones={actuaciones}
        onEdit={handleEditEnsayo}
        onDelete={e => handleDeleteRequest('ensayo', e)}
        onUpdated={loadAll}
      />

      <Dialog.Root open={isEnsayoShareOpen} size='md' scrollBehavior="inside" onOpenChange={e => {
        if (!e.open) {
          closeEnsayoShare();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <HStack gap={2}>
                  <Icon as={FaShareAlt} color="green.500" />
                  <Text>Compartir ensayo</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body pb={6}>
                {ensayoShareLoading ? (
                  <Flex justify="center" align="center" py={10}>
                    <Spinner size="lg" color="purple.500" />
                  </Flex>
                ) : ensayoShareDet ? (
                  <VStack align="stretch" gap={4}>
                    <Text fontSize="sm" color="gray.600">
                      {formatFecha(ensayoShareDet.fecha)}
                      {ensayoShareDet.grupo ? ` · ${ensayoShareDet.grupo.nombre}` : ''}
                    </Text>
                    <Checkbox.Root
                      onCheckedChange={(details) => setEnsayoShareIncluirMusicos(details.checked === true)}
                      colorPalette="purple"
                      checked={ensayoShareIncluirMusicos}
                    ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                      <Text fontSize="sm">Incluir lista de músicos (convocados y estado de asistencia)</Text>
                    </Checkbox.Label></Checkbox.Root>
                    <SimpleGrid columns={2} gap={3}>
                      <Button colorPalette="green" onClick={handleEnsayoShareWhatsapp}><Icon as={FaWhatsapp} />WhatsApp
                                          </Button>
                      <Button
                        colorPalette="purple"
                        onClick={handleEnsayoSharePdf}
                        loading={ensayoSharePdfGenerando}><Icon as={FaFilePdf} />PDF
                                          </Button>
                    </SimpleGrid>
                    <Text fontSize="xs" color="gray.500">
                      Se incluyen fecha, horario, grupo de ensayos, sede, notas, repertorio y demás datos del ensayo. La lista de músicos es opcional. El enlace del repertorio compartido, si está activo, va al final del mensaje.
                    </Text>
                  </VStack>
                ) : null}
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* ── Modal de compartir ─────────────────────────────────────────────── */}
      <Dialog.Root open={isShareOpen} size='lg' scrollBehavior="inside" onOpenChange={e => {
        if (!e.open) {
          onShareClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <HStack gap={2}>
                  <Icon as={FaShareAlt} color="green.500" />
                  <Text>Compartir programación</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                {/* ── Paso 1: formato ── */}
                {sharePaso === 1 && (
                  <VStack gap={4}>
                    <Text fontSize="sm" color="gray.600">¿Cómo quieres compartir el informe?</Text>
                    <SimpleGrid columns={2} gap={3} w="full">
                      <Box
                        p={4}
                        border="2px solid"
                        borderColor={shareFormato === 'pdf' ? 'purple.500' : 'gray.200'}
                        rounded="xl"
                        cursor="pointer"
                        bg={shareFormato === 'pdf' ? 'purple.50' : 'white'}
                        onClick={() => setShareFormato('pdf')}
                        textAlign="center"
                      >
                        <Icon as={FaFilePdf} boxSize={8} color={shareFormato === 'pdf' ? 'purple.500' : 'gray.400'} mb={2} />
                        <Text fontWeight="700" fontSize="sm">PDF</Text>
                        <Text fontSize="xs" color="gray.500">Abre una nueva pestaña para imprimir o guardar</Text>
                      </Box>
                      <Box
                        p={4}
                        border="2px solid"
                        borderColor={shareFormato === 'wa' ? 'green.500' : 'gray.200'}
                        rounded="xl"
                        cursor="pointer"
                        bg={shareFormato === 'wa' ? 'green.50' : 'white'}
                        onClick={() => setShareFormato('wa')}
                        textAlign="center"
                      >
                        <Icon as={FaWhatsapp} boxSize={8} color={shareFormato === 'wa' ? 'green.500' : 'gray.400'} mb={2} />
                        <Text fontWeight="700" fontSize="sm">WhatsApp</Text>
                        <Text fontSize="xs" color="gray.500">Texto formateado listo para enviar</Text>
                      </Box>
                    </SimpleGrid>
                  </VStack>
                )}

                {/* ── Paso 2: opciones ── */}
                {sharePaso === 2 && (
                  <VStack gap={5} align="stretch">
                    {/* Meses */}
                    <Box>
                      <Text fontWeight="700" fontSize="sm" mb={2}>Meses a incluir</Text>
                      {getMesesDisponibles().length === 0 ? (
                        <Text fontSize="sm" color="gray.400" fontStyle="italic">No hay eventos en el calendario</Text>
                      ) : (
                        <Wrap gap={2}>
                          {getMesesDisponibles().map(mes => {
                            const [y, m] = mes.split('-').map(Number);
                            const sel = shareMeses.includes(mes);
                            return (
                              <WrapItem key={mes}>
                                <Button
                                  size="xs"
                                  rounded="full"
                                  colorPalette={sel ? 'purple' : 'gray'}
                                  variant={sel ? 'solid' : 'outline'}
                                  onClick={() => setShareMeses(prev =>
                                    prev.includes(mes) ? prev.filter(x => x !== mes) : [...prev, mes]
                                  )}
                                >
                                  {MESES[m - 1]} {y}
                                </Button>
                              </WrapItem>
                            );
                          })}
                        </Wrap>
                      )}
                    </Box>

                    {/* Vista (solo PDF) */}
                    {shareFormato === 'pdf' && (
                      <Box>
                        <Text fontWeight="700" fontSize="sm" mb={2}>Vista del informe</Text>
                        <HStack gap={3}>
                          {[
                            { v: 'lista', icon: FaThList, label: 'Listado' },
                            { v: 'calendario', icon: FaCalendar, label: 'Calendario' },
                          ].map(({ v, icon, label }) => (
                            <Button
                              key={v}
                              size="sm"
                              colorPalette={shareVistaInforme === v ? 'purple' : 'gray'}
                              variant={shareVistaInforme === v ? 'solid' : 'outline'}
                              onClick={() => setShareVistaInforme(v)}><Icon as={icon} />{label}</Button>
                          ))}
                        </HStack>
                      </Box>
                    )}

                    {/* Secciones */}
                    <Box>
                      <Text fontWeight="700" fontSize="sm" mb={2}>Incluir en el informe</Text>
                      <VStack align="start" gap={2}>
                        {[
                          { key: 'ensayos', label: '🥁 Ensayos' },
                          { key: 'actuaciones', label: '🎺 Actuaciones programadas' },
                          { key: 'grupos', label: '🎼 Nombre del grupo de ensayos en cada ensayo' },
                          { key: 'estadisticas', label: '📊 Resumen de estadísticas (solo PDF)' },
                        ].map(({ key, label }) => (
                          <Checkbox.Root
                            key={key}
                            onCheckedChange={details => setShareSecciones(s => ({ ...s, [key]: details.checked === true }))}
                            colorPalette="purple"
                            disabled={key === 'estadisticas' && shareFormato === 'wa'}
                            checked={shareSecciones[key]}
                          ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                            <Text fontSize="sm">{label}</Text>
                          </Checkbox.Label></Checkbox.Root>
                        ))}
                      </VStack>
                    </Box>

                    <Box>
                      <Text fontWeight="700" fontSize="sm" mb={2}>Detalle por ensayo (opcional)</Text>
                      <Text fontSize="xs" color="gray.500" mb={2}>
                        Añade repertorio, sede y notas en el resumen de cada ensayo (WhatsApp y PDF en vista listado).
                      </Text>
                      <VStack align="start" gap={2}>
                        {[
                          { key: 'sede', label: '📍 Sede (nombre y dirección)' },
                          { key: 'notas', label: '📝 Notas u observaciones' },
                          { key: 'repertorio', label: '🎼 Repertorio' },
                        ].map(({ key, label }) => (
                          <Checkbox.Root
                            key={key}
                            onCheckedChange={(details) =>
                              setShareEnsayoDetalle((s) => ({ ...s, [key]: details.checked === true }))
                            }
                            colorPalette="purple"
                            disabled={!shareSecciones.ensayos}
                            checked={shareEnsayoDetalle[key]}
                          ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                            <Text fontSize="sm">{label}</Text>
                          </Checkbox.Label></Checkbox.Root>
                        ))}
                      </VStack>
                    </Box>
                  </VStack>
                )}

                {/* ── Paso 3: mensaje WA ── */}
                {sharePaso === 3 && (
                  <VStack gap={3} align="stretch">
                    <HStack justify="space-between">
                      <Text fontWeight="700" fontSize="sm">Mensaje listo para enviar</Text>
                      <Button
                        size="xs"
                        colorPalette={shareCopied ? 'green' : 'gray'}
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(shareWaMensaje);
                          setShareCopied(true);
                          setTimeout(() => setShareCopied(false), 2000);
                        }}><Icon as={FaCopy} />{shareCopied ? '✓ Copiado' : 'Copiar'}</Button>
                    </HStack>
                    <Box
                      p={3}
                      bg="green.50"
                      border="1px solid"
                      borderColor="green.200"
                      rounded="xl"
                      maxH="300px"
                      overflowY="auto"
                      fontFamily="monospace"
                      fontSize="xs"
                      whiteSpace="pre-wrap"
                      color="gray.700"
                    >
                      {shareWaMensaje}
                    </Box>
                    <Text fontSize="xs" color="gray.500">
                      Se abrirá WhatsApp Web con este mensaje. Elige el contacto o grupo al que enviarlo.
                    </Text>
                  </VStack>
                )}
              </Dialog.Body>

              <Dialog.Footer gap={2}>
                {sharePaso > 1 && (
                  <Button variant="ghost" onClick={() => setSharePaso(p => p - 1)}>
                    ← Atrás
                  </Button>
                )}
                <Button variant="ghost" onClick={onShareClose}>Cancelar</Button>
                {sharePaso === 1 && (
                  <Button colorPalette="purple" onClick={() => setSharePaso(2)}>
                    Siguiente →
                  </Button>
                )}
                {sharePaso === 2 && (
                  <Button
                    colorPalette={shareFormato === 'wa' ? 'green' : 'purple'}
                    onClick={handleShareSiguiente}
                    disabled={shareMeses.length === 0}><Icon as={shareFormato === 'wa' ? FaWhatsapp : FaFilePdf} />{shareFormato === 'wa' ? 'Generar mensaje' : 'Generar PDF'}</Button>
                )}
                {sharePaso === 3 && (
                  <Button
                    colorPalette="green"
                    onClick={() => openWhatsappChat(null, shareWaMensaje)}><Icon as={FaWhatsapp} />Abrir WhatsApp
                                  </Button>
                )}
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Confirmación de borrado */}
      <Dialog.Root
        open={isDeleteOpen}
        initialFocusEl={() => cancelRef.current}
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            onDeleteClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop>
            <Dialog.Positioner>
              <Dialog.Content>
                <Dialog.Header>
                  {deletingItem?.tipo === 'grupo' ? 'Eliminar grupo de ensayos' : 'Eliminar ensayo'}
                </Dialog.Header>
                <Dialog.Body>
                  {deletingItem?.tipo === 'grupo'
                    ? `¿Eliminar el grupo de ensayos "${deletingItem?.item?.nombre}"? Los ensayos de este grupo no se eliminarán, pero quedarán sin grupo de ensayos.`
                    : `¿Eliminar el ensayo del ${deletingItem?.item?.fecha ? formatFechaCorta(deletingItem.item.fecha) : ''}? Esta acción no se puede deshacer.`
                  }
                </Dialog.Body>
                <Dialog.Footer gap={2}>
                  <Button ref={cancelRef} variant="ghost" onClick={onDeleteClose}>Cancelar</Button>
                  <Button colorPalette="red" onClick={handleDeleteConfirm}>Eliminar</Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Positioner>
          </Dialog.Backdrop>

        </Portal>
</Dialog.Root>

      {/* ── Modal de vacaciones ─────────────────────────────────────────────── */}
      <Dialog.Root open={isVacacionOpen} size='md' onOpenChange={e => {
        if (!e.open) {
          onVacacionClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <HStack gap={2}>
                  <Text fontSize="xl">☀️</Text>
                  <Text>{editingVacacion ? 'Editar periodo de vacaciones' : 'Marcar vacaciones / descanso'}</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body pb={2}>
                <VStack gap={4} align="stretch">
                  <Field.Root>
                    <Field.Label fontSize="sm">Motivo / Nombre del periodo</Field.Label>
                    <Input
                      placeholder="Ej: Semana Santa, Vacaciones verano…"
                      value={vacacionForm.motivo}
                      onChange={e => setVacacionForm(f => ({ ...f, motivo: e.target.value }))}
                    />
                  </Field.Root>
                  <SimpleGrid columns={2} gap={3}>
                    <Field.Root required>
                      <Field.Label fontSize="sm">Fecha inicio</Field.Label>
                      <Input
                        type="date"
                        value={vacacionForm.fechaInicio}
                        onChange={e => setVacacionForm(f => ({ ...f, fechaInicio: e.target.value }))}
                      />
                    </Field.Root>
                    <Field.Root required>
                      <Field.Label fontSize="sm">Fecha fin</Field.Label>
                      <Input
                        type="date"
                        value={vacacionForm.fechaFin}
                        min={vacacionForm.fechaInicio}
                        onChange={e => setVacacionForm(f => ({ ...f, fechaFin: e.target.value }))}
                      />
                    </Field.Root>
                  </SimpleGrid>
                  <Field.Root>
                    <Field.Label fontSize="sm">Color de marcado</Field.Label>
                    <HStack gap={3} flexWrap="wrap">
                      {['#F6AD55', '#FC8181', '#68D391', '#63B3ED', '#B794F4', '#F687B3', '#FBD38D'].map(c => (
                        <Box
                          key={c}
                          w={7}
                          h={7}
                          rounded="full"
                          bg={c}
                          cursor="pointer"
                          border="3px solid"
                          borderColor={vacacionForm.color === c ? 'gray.700' : 'transparent'}
                          _hover={{ transform: 'scale(1.2)' }}
                          transition="all 0.15s"
                          onClick={() => setVacacionForm(f => ({ ...f, color: c }))}
                        />
                      ))}
                      <Input
                        type="color"
                        value={vacacionForm.color}
                        onChange={e => setVacacionForm(f => ({ ...f, color: e.target.value }))}
                        w={9}
                        h={9}
                        p={1}
                        rounded="full"
                        cursor="pointer"
                        border="none"
                        title="Color personalizado"
                      />
                    </HStack>
                  </Field.Root>
                  {/* Preview */}
                  {vacacionForm.fechaInicio && vacacionForm.fechaFin && (
                    <Box
                      p={3}
                      rounded="xl"
                      bg="orange.50"
                      borderLeft="4px solid"
                      borderLeftColor={vacacionForm.color || 'orange.300'}
                    >
                      <HStack gap={2}>
                        <Text fontSize="lg">☀️</Text>
                        <Box>
                          <Text fontSize="sm" fontWeight="700" color="orange.700">
                            {vacacionForm.motivo || 'Vacaciones / descanso'}
                          </Text>
                          <Text fontSize="xs" color="orange.500">
                            {new Date(vacacionForm.fechaInicio).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                            {' '}–{' '}
                            {new Date(vacacionForm.fechaFin).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </Text>
                        </Box>
                      </HStack>
                    </Box>
                  )}
                </VStack>
              </Dialog.Body>
              <Dialog.Footer gap={2}>
                {editingVacacion && (
                  <Button
                    colorPalette="red"
                    variant="ghost"
                    mr="auto"
                    loading={vacacionSaving}
                    onClick={handleDeleteVacacion}><Icon as={FaTrash} />Eliminar
                                  </Button>
                )}
                <Button variant="ghost" onClick={onVacacionClose} disabled={vacacionSaving}>Cancelar</Button>
                <Button colorPalette="orange" loading={vacacionSaving} onClick={handleSaveVacacion}>
                  {editingVacacion ? 'Guardar cambios' : 'Añadir periodo'}
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
    </AppLayout>
  );
}
